/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../ReduxToolkit/Store';
import apiClient from '../../Api/ApiClient';
import { Settings, Plus, Trash2, Edit3, Save, RotateCcw, BarChart3, Clock, HelpCircle, FileText, Check, Users, LayoutDashboard, Database, Activity, ShieldCheck, Mail, ArrowRight, Bold, Italic, Heading, List, Link, UserPlus, Calendar, Lock, Unlock, Shield, Eye, EyeOff, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, ChevronLeft, X, Phone, Building2, User, UserCheck, Briefcase, Filter, CheckCircle2, AlertCircle, Loader2, Search, Sparkles, AtSign, KeyRound, CheckSquare, Square, Cloud, ExternalLink, RefreshCw, Send, CheckCheck, Upload, FileSpreadsheet, Power, MapPin, Ban, History, DownloadCloud, Download } from 'lucide-react';
import { LegalDocument, DocumentNature, LegalOrigin, LegalStatus, LegalDomain } from '../../types';
import { formatFrenchDate, formatHumanDateTime } from '../../utils/legalUtils';
import { CreateStaffModal } from '../ui/CreateStaffModal';
import { CreateUserModal } from '../ui/CreateUserModal';
import { Document, Page } from 'react-pdf';
import { formulaService } from '../../Services/FormulaService';
import { AdminOcrTab } from '../admin/AdminOcrTab';


const MarkdownEditor = ({ value, onChange, id }: { value: string, onChange: (v: string) => void, id: string }) => {
  const insertText = (prefix: string, suffix: string = '') => {
    const textarea = document.getElementById(id) as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = value.substring(0, start);
    const selected = value.substring(start, end) || 'texte';
    const after = value.substring(end);
    onChange(before + prefix + selected + suffix + after);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
    }, 0);
  };

  const ToolbarButton = ({ icon: Icon, onClick, title }: { icon: any, onClick: () => void, title: string }) => (
    <button type="button" onClick={onClick} title={title} className="p-1.5 text-zinc-500 hover:text-primary-700 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-zinc-200 hover:shadow-sm">
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div className="w-full border border-zinc-200 rounded-xl overflow-hidden focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all shadow-sm">
      {/* Toolbar */}
      <div className="bg-zinc-50 border-b border-zinc-200 px-2 py-1 flex items-center gap-0.5">
        <ToolbarButton icon={Bold} onClick={() => insertText('**', '**')} title="Gras" />
        <ToolbarButton icon={Italic} onClick={() => insertText('*', '*')} title="Italique" />
        <div className="w-px h-3.5 bg-zinc-300 mx-1"></div>
        <ToolbarButton icon={Heading} onClick={() => insertText('\n### ', '')} title="Titre" />
        <ToolbarButton icon={List} onClick={() => insertText('\n- ', '')} title="Liste à puces" />
        <div className="w-px h-3.5 bg-zinc-300 mx-1"></div>
        <ToolbarButton icon={Link} onClick={() => insertText('[', '](https://)')} title="Lien" />
      </div>
      {/* Textarea */}
      <textarea
        id={id}
        rows={4}
        placeholder="# LOI N°... portant règlement de..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2.5 focus:outline-none text-xs resize-y"
        style={{ backgroundColor: '#fbf5ff', fontFamily: 'Arial, sans-serif' }}
      />
    </div>
  );
};

interface AdminViewProps {
  documents: LegalDocument[];
  addDocument: (doc: LegalDocument) => void;
  updateDocument: (docId: number, updated: Partial<LegalDocument>) => void;
  deleteDocument: (docId: number) => void;
  suggestions: any[];
  approveSuggestion: (id: string, documentsList: LegalDocument[]) => void;
  users: any[];
  setUsers: React.Dispatch<React.SetStateAction<any[]>>;
}

export const AdminView: React.FC<AdminViewProps> = ({
  documents: _documentsProp,
  addDocument,
  updateDocument,
  deleteDocument,
  suggestions: _suggestionsProp,
  approveSuggestion,
  users: _usersProp,
  setUsers: _setUsersProp
}) => {
  const token = localStorage.getItem('accessToken');
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const isAdmin = currentUser?.role === 'ADMIN' || (Array.isArray(currentUser?.role) && currentUser.role.includes('ADMIN'));
  const isStaff = currentUser?.role === 'STAFF' || (Array.isArray(currentUser?.role) && currentUser.role.includes('STAFF'));

  // Permissions par module
  const canViewDocs = isAdmin || !!(currentUser?.permissions && (currentUser.permissions.includes('view_doc') || currentUser.permissions.includes('edit_doc')));
  const canAddDocs = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('add_doc'));
  const canViewStats = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_stats'));
  const canManageGroups = isAdmin || !!(currentUser?.permissions && (currentUser.permissions.includes('manage_group_perms') || currentUser.permissions.includes('create_group')));

  // Permissions granulaires de gestion des utilisateurs
  const canViewStaff = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_staff_users'));
  const canViewIndividuals = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_individual_users'));
  const canViewBusinesses = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_business_users'));
  const canCreateStaff = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('create_staff_account'));
  const canActivateAccount = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('activate_account'));
  const canResetPassword = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('reset_user_password'));
  const canAssignGroup = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('assign_user_group'));

  const canViewUserManagement = isAdmin || canViewStaff || canViewIndividuals || canViewBusinesses || canCreateStaff || canAssignGroup;

  // Permissions Traçabilité & Activité
  const canViewLogins = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_logins'));
  const canViewConsultations = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_consultations'));
  const canViewDownloads = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_downloads'));
  const canViewSearches = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('view_searches'));
  const canViewActivity = isAdmin || canViewLogins || canViewConsultations || canViewDownloads || canViewSearches;

  // Permission OCR & Numérisation haute précision
  const canManageOcr = isAdmin || !!(currentUser?.permissions && currentUser.permissions.includes('manage_ocr'));

  const [documents, setDocuments] = React.useState<any[]>([]);
  const [users, setUsers] = React.useState<any[]>([]);
  const [isCreateStaffModalOpen, setIsCreateStaffModalOpen] = React.useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<any[]>(_suggestionsProp || []);

  const fetchAdminSuggestions = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await apiClient.get<any>('/api/suggestions');
      if (res && Array.isArray(res.items)) {
        setSuggestions(res.items);
      } else if (Array.isArray(res)) {
        setSuggestions(res);
      }
    } catch (err) {
      console.warn("Impossible de charger /api/suggestions", err);
    }
  }, [token]);

  React.useEffect(() => {
    fetchAdminSuggestions();
  }, [fetchAdminSuggestions]);


  React.useEffect(() => {
    if (_suggestionsProp && _suggestionsProp.length > 0) {
      setSuggestions(prev => prev.length === 0 ? _suggestionsProp : prev);
    }
  }, [_suggestionsProp]);

  const [adminStats, setAdminStats] = React.useState<any>(null);
  const [statsLoading, setStatsLoading] = React.useState<boolean>(false);

  const fetchAdminStats = React.useCallback(async () => {
    if (!token || !canViewStats) return;
    setStatsLoading(true);
    try {
      const data = await apiClient.get<any>('/api/admin/stats');
      if (data) {
        setAdminStats(data);
      }
    } catch (err) {
      console.warn("Impossible de charger les statistiques réelles:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [token, canViewStats]);



  const [docsPage, setDocsPage] = React.useState(1);
  const [docsPageSize, setDocsPageSize] = React.useState(20);
  const [docsTotalCount, setDocsTotalCount] = React.useState(0);
  const [docsSearchInput, setDocsSearchInput] = React.useState('');
  const [docsSearchTerm, setDocsSearchTerm] = React.useState('');
  const [docsMinioFilter, setDocsMinioFilter] = React.useState<'ALL' | 'MINIO_ONLY' | 'NOT_MINIO'>('ALL');
  const [docsLegalStatusFilter, setDocsLegalStatusFilter] = React.useState('');
  const [docsCategoryFilter, setDocsCategoryFilter] = React.useState('');

  const [usersPage, setUsersPage] = React.useState(1);
  const [usersPageSize, setUsersPageSize] = React.useState(20);
  const [usersTotalCount, setUsersTotalCount] = React.useState(0);

  const defaultAccountFilter = React.useMemo(() => {
    if (canViewStaff && canViewIndividuals && canViewBusinesses) return '';
    if (canViewStaff) return 'STAFF';
    if (canViewIndividuals) return 'INDIVIDUAL';
    if (canViewBusinesses) return 'BUSINESS';
    return '';
  }, [canViewStaff, canViewIndividuals, canViewBusinesses]);

  const [usersAccountTypeFilter, setUsersAccountTypeFilter] = React.useState<string>(defaultAccountFilter);

  React.useEffect(() => {
    if (defaultAccountFilter && !usersAccountTypeFilter) {
      setUsersAccountTypeFilter(defaultAccountFilter);
    }
  }, [defaultAccountFilter]);

  const [usersPendingFilter, setUsersPendingFilter] = React.useState<'PENDING' | 'ALL' | 'ASSIGNED'>('ALL');
  const [usersSearchInput, setUsersSearchInput] = React.useState('');
  const [usersSearchTerm, setUsersSearchTerm] = React.useState('');
  const [togglingStatusId, setTogglingStatusId] = React.useState<string | null>(null);

  const [availableGroups, setAvailableGroups] = React.useState<any[]>([]);
  const [selectedGroupForUser, setSelectedGroupForUser] = React.useState<Record<string, string>>({});
  const [selectedExpireAtForUser, setSelectedExpireAtForUser] = React.useState<Record<string, string>>({});
  const [assigningLoading, setAssigningLoading] = React.useState<Record<string, boolean>>({});
  const [actionAlert, setActionAlert] = React.useState<{ type: 'success' | 'error', message: string } | null>(null);

  const [resetPasswordUser, setResetPasswordUser] = React.useState<any | null>(null);
  const [resetPasswordLoading, setResetPasswordLoading] = React.useState(false);

  const fetchUsers = () => {
    if (!token) return;
    const params = new URLSearchParams();
    params.set('page', usersPage.toString());
    params.set('pageSize', usersPageSize.toString());
    if (usersAccountTypeFilter) params.set('accountType', usersAccountTypeFilter);
    if (usersPendingFilter === 'PENDING') params.set('pendingGroupOnly', 'true');
    else if (usersPendingFilter === 'ASSIGNED') params.set('pendingGroupOnly', 'false');
    if (usersSearchTerm.trim()) params.set('search', usersSearchTerm.trim());

    apiClient.get<any>(`/api/admin/users?${params.toString()}`)
      .then(res => {
        const items = res.items || [];
        setUsers(items);
        setUsersTotalCount(res.totalCount || 0);

        // Pre-fill expiration dates
        const expMap: Record<string, string> = {};
        items.forEach((u: any) => {
          if (u.expireAt) {
            expMap[u.id] = u.expireAt.substring(0, 10);
          }
        });
        setSelectedExpireAtForUser(prev => ({ ...expMap, ...prev }));
      })
      .catch(err => {
        console.error("Failed to load admin users", err);
      });
  };

  const [dbCategories, setDbCategories] = React.useState<any[]>([]);
  const [dbAreasOfLaw, setDbAreasOfLaw] = React.useState<any[]>([]);
  const [dbLegalStatuses, setDbLegalStatuses] = React.useState<string[]>([]);

  React.useEffect(() => {
    apiClient.get<any[]>('/api/Categories/dropdown')
      .then(res => { if (Array.isArray(res)) setDbCategories(res); })
      .catch(console.error);

    apiClient.get<any[]>('/api/AreaOfLaw/dropdown')
      .then(res => { if (Array.isArray(res)) setDbAreasOfLaw(res); })
      .catch(console.error);

    apiClient.get<string[]>('/api/documents/legal-statuses')
      .then(res => { if (Array.isArray(res)) setDbLegalStatuses(res); })
      .catch(console.error);
  }, []);

  // Natures exclusivement issues de la base de données
  const categoryOptions = React.useMemo(() => {
    return dbCategories.map((c: any) => ({
      value: c.value || c.label || c,
      label: c.label || c.value || c
    }));
  }, [dbCategories]);

  // Statuts juridiques stricts (En vigueur, Modifié, Abrogé) pour les formulaires
  const primaryLegalStatuses = React.useMemo(() => [
    { value: 'EnVigeur', label: '🟢 En vigueur' },
    { value: 'Modifie', label: '🟠 Modifié' },
    { value: 'Abroge', label: '🔴 Abrogé' }
  ], []);

  // États juridiques pour filtres (avec En vigueur, Modifié, Abrogé en tête)
  const legalStatusOptions = React.useMemo(() => {
    const list = [
      { value: 'EnVigeur', label: '🟢 En vigueur' },
      { value: 'Modifie', label: '🟠 Modifié' },
      { value: 'Abroge', label: '🔴 Abrogé' }
    ];
    dbLegalStatuses.forEach((s: string) => {
      const raw = s.trim();
      const lower = raw.toLowerCase();
      if (raw && !lower.includes('vigueur') && !lower.includes('vigeur') && !lower.includes('modif') && !lower.includes('abrog')) {
        list.push({
          value: raw,
          label: raw.replace(/_/g, ' ')
        });
      }
    });
    return list;
  }, [dbLegalStatuses]);

  const areaOfLawOptions = React.useMemo(() => {
    const list = dbAreasOfLaw.map((a: any) => ({
      value: a.value || a.label || a,
      label: a.label || a.value || a
    }));
    return list;
  }, [dbAreasOfLaw]);

  const fetchDocuments = React.useCallback(() => {
    if (!token) return;
    const params = new URLSearchParams();
    params.set('page', docsPage.toString());
    params.set('pageSize', docsPageSize.toString());
    if (docsLegalStatusFilter) params.set('legalStatus', docsLegalStatusFilter);
    if (docsCategoryFilter) params.set('categoryName', docsCategoryFilter);
    if (docsMinioFilter === 'MINIO_ONLY') params.set('isDeployedToMinio', 'true');
    else if (docsMinioFilter === 'NOT_MINIO') params.set('isDeployedToMinio', 'false');
    if (docsSearchTerm.trim()) params.set('search', docsSearchTerm.trim());

    apiClient.get<any>(`/api/admin/documents?${params.toString()}`)
      .then(res => { setDocuments(res.items || []); setDocsTotalCount(res.totalCount || 0); })
      .catch(console.error);
  }, [token, docsPage, docsPageSize, docsLegalStatusFilter, docsCategoryFilter, docsMinioFilter, docsSearchTerm]);

  React.useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const [docActionLoading, setDocActionLoading] = React.useState<Record<string, boolean>>({});

  const handleRevertToDraft = async (docId: string | number, docTitle: string) => {
    if (!confirm(`Souhaitez-vous renvoyer l'acte "${docTitle}" en brouillon ?\n\nIl sera retiré du catalogue public et du moteur de recherche, et redeviendra disponible pour modification et prévisualisation dans l'onglet Brouillons.`)) {
      return;
    }
    const key = docId.toString();
    setDocActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await apiClient.post<any>(`/api/admin/documents/${docId}/revert-to-draft`, {});
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({ type: 'success', message: res.message || "Acte renvoyé en brouillon avec succès !" });
        setTimeout(() => setActionAlert(null), 5000);
        fetchDocuments();
        fetchDrafts();
      } else {
        alert(res?.message || "Erreur lors du renvoi en brouillon.");
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors du renvoi en brouillon.");
    } finally {
      setDocActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDeleteDocument = async (docId: string | number) => {
    if (!confirm("Souhaitez-vous définitivement supprimer cet acte de la bibliothèque et du moteur de recherche ?")) {
      return;
    }
    const key = docId.toString();
    setDocActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await apiClient.delete<any>(`/api/admin/documents/${docId}`);
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({ type: 'success', message: res.message || "Acte supprimé avec succès." });
        setTimeout(() => setActionAlert(null), 4000);
        fetchDocuments();
      } else {
        alert(res?.message || "Erreur lors de la suppression de l'acte.");
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors de la suppression de l'acte.");
    } finally {
      setDocActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  React.useEffect(() => {
    fetchUsers();
  }, [token, usersPage, usersPageSize, usersAccountTypeFilter, usersPendingFilter, usersSearchTerm]);

  const handleToggleUserStatus = async (userId: string, currentIsActive: boolean) => {
    if (!canActivateAccount) return;
    setTogglingStatusId(userId);
    try {
      const res: any = await apiClient.post(`/api/admin/users/${userId}/toggle-status`, {});
      if (res && res.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: res.isActive } : u));
        setActionAlert({
          type: 'success',
          message: res.message || (res.isActive ? "Compte activé avec succès !" : "Compte suspendu avec succès !")
        });
        setTimeout(() => setActionAlert(null), 4000);
      } else {
        setActionAlert({ type: 'error', message: res?.message || "Erreur lors du changement de statut." });
        setTimeout(() => setActionAlert(null), 4000);
      }
    } catch (err: any) {
      setActionAlert({ type: 'error', message: err?.message || "Erreur lors du changement de statut." });
      setTimeout(() => setActionAlert(null), 4000);
    } finally {
      setTogglingStatusId(null);
    }
  };

  const handleAssignGroup = async (userId: string, userAccountType: string) => {
    const isStaff = (userAccountType || '').toUpperCase() === 'STAFF';
    const targetCategory = isStaff ? 'STAFF' : 'CUSTOMER';
    const validGroups = availableGroups.filter((g: any) => {
      const cat = (g.groupCategory || g.GroupCategory || '').toUpperCase();
      return cat === targetCategory && cat !== 'ADMIN';
    });

    const defaultGId = validGroups.length > 0 ? (validGroups[0].id || validGroups[0].Id) : '';
    const groupId = selectedGroupForUser[userId] || (validGroups.some((g: any) => (g.id || g.Id) === selectedGroupForUser[userId]) ? selectedGroupForUser[userId] : defaultGId);

    if (!groupId) {
      alert(`Aucun groupe de catégorie ${targetCategory} n'est disponible.`);
      return;
    }

    const expireDateStr = selectedExpireAtForUser[userId];
    if (expireDateStr) {
      const todayStr = new Date().toISOString().split('T')[0];
      if (expireDateStr < todayStr) {
        alert("La date d'expiration ne peut pas être antérieure à la date d'aujourd'hui.");
        return;
      }
    }

    setAssigningLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await apiClient.post<any>(`/api/admin/users/${userId}/assign-group`, { 
        groupId,
        expireAt: expireDateStr ? new Date(expireDateStr).toISOString() : null
      });
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({
          type: 'success',
          message: res.message || 'Groupe et date d\'expiration mis à jour avec succès.'
        });
        setTimeout(() => setActionAlert(null), 5000);
        fetchUsers();
      } else {
        alert(res?.message || "Erreur lors de la mise à jour du compte.");
      }
    } catch (err: any) {
      console.error("Error updating user account", err);
      alert(err.message || err.data?.message || "Erreur lors de la mise à jour du compte.");
    } finally {
      setAssigningLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPasswordUser) return;
    setResetPasswordLoading(true);
    try {
      const res = await apiClient.post<any>(`/api/admin/users/${resetPasswordUser.id}/reset-password`, {});
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({
          type: 'success',
          message: res.message || `Mot de passe réinitialisé et envoyé par email à ${resetPasswordUser.email}.`
        });
        setTimeout(() => setActionAlert(null), 6000);
        setResetPasswordUser(null);
      } else {
        alert(res?.message || "Erreur lors de la réinitialisation du mot de passe.");
      }
    } catch (err: any) {
      console.error("Error resetting password", err);
      alert(err.message || err.data?.message || "Erreur lors de la réinitialisation du mot de passe.");
    } finally {
      setResetPasswordLoading(false);
    }
  };
  // ── Drafts (Brouillons) state ──────────────────────────────────────────
  const [drafts, setDrafts] = React.useState<any[]>([]);
  const [draftsPage, setDraftsPage] = React.useState(1);
  const [draftsPageSize, setDraftsPageSize] = React.useState(20);
  const [draftsTotalCount, setDraftsTotalCount] = React.useState(0);
  const [draftsSearchInput, setDraftsSearchInput] = React.useState('');
  const [draftsSearchTerm, setDraftsSearchTerm] = React.useState('');
  const [draftsMinioFilter, setDraftsMinioFilter] = React.useState<'ALL' | 'MINIO_ONLY' | 'NOT_MINIO'>('ALL');
  const [draftsStatusFilter, setDraftsStatusFilter] = React.useState('');
  const [draftsLegalStatusFilter, setDraftsLegalStatusFilter] = React.useState('');
  const [draftsCategoryFilter, setDraftsCategoryFilter] = React.useState('');
  const [selectedDraftIds, setSelectedDraftIds] = React.useState<string[]>([]);
  const [draftActionLoading, setDraftActionLoading] = React.useState<Record<string, boolean>>({});
  const [batchPublishing, setBatchPublishing] = React.useState(false);
  // ── Unified Document / Draft Preview (Streaming page par page) ──────────
  const [previewDoc, setPreviewDoc] = React.useState<{
    id: string;
    title: string;
    referenceNumber?: string;
    isDraft: boolean;
    isDeployedToMinio?: boolean;
    nature?: string;
    categoryName?: string;
    draftObj?: any;
    docObj?: any;
  } | null>(null);
  const [previewPage, setPreviewPage] = React.useState<number>(1);
  const [previewTotalPages, setPreviewTotalPages] = React.useState<number | null>(null);
  const [previewPageInput, setPreviewPageInput] = React.useState<string>('1');
  const [previewPdfData, setPreviewPdfData] = React.useState<Uint8Array | null>(null);
  const [previewPdfLoading, setPreviewPdfLoading] = React.useState<boolean>(false);
  const [previewPdfError, setPreviewPdfError] = React.useState<string | null>(null);
  const [editingDraft, setEditingDraft] = React.useState<any | null>(null);
  const [editDraftForm, setEditDraftForm] = React.useState<any>({});
  const [savingDraft, setSavingDraft] = React.useState(false);

  const openDocumentPreview = (doc: any, isDraft: boolean) => {
    setPreviewDoc({
      id: (doc.id || doc.Id)?.toString(),
      title: doc.officialTitle || 'Document sans titre',
      referenceNumber: doc.referenceNumber,
      isDraft,
      isDeployedToMinio: doc.isDeployedToMinio,
      nature: doc.nature,
      categoryName: doc.categoryName,
      draftObj: isDraft ? doc : undefined,
      docObj: !isDraft ? doc : undefined
    });
    setPreviewPage(1);
    setPreviewPageInput('1');
    setPreviewTotalPages(null);
  };

  const closeDocumentPreview = () => {
    setPreviewDoc(null);
    setPreviewPdfData(null);
    setPreviewPdfLoading(false);
    setPreviewPdfError(null);
  };

  const handlePageInputSubmit = () => {
    const val = parseInt(previewPageInput, 10);
    if (!isNaN(val) && val >= 1) {
      const target = previewTotalPages ? Math.min(previewTotalPages, val) : val;
      setPreviewPage(target);
      setPreviewPageInput(target.toString());
    } else {
      setPreviewPageInput(previewPage.toString());
    }
  };

  // Chargement du nombre de pages du document
  React.useEffect(() => {
    if (!previewDoc?.id) return;
    setPreviewTotalPages(null);

    apiClient.get<{ totalPages: number }>(`/api/documents/${previewDoc.id}/pages/info`)
      .then(res => {
        const total = (res as any)?.totalPages ?? (res as any)?.TotalPages;
        if (total && total > 0) {
          setPreviewTotalPages(total);
        }
      })
      .catch(err => {
        console.warn("Info pages:", err);
      });
  }, [previewDoc?.id]);

  // Chargement ultra-rapide de la page courante en mémoire vive (Zéro URL Blob, 100% sécurisé)
  React.useEffect(() => {
    if (!previewDoc?.id) {
      setPreviewPdfData(null);
      setPreviewPdfLoading(false);
      setPreviewPdfError(null);
      return;
    }

    let isCancelled = false;
    setPreviewPdfLoading(true);
    setPreviewPdfError(null);
    setPreviewPageInput(previewPage.toString());

    apiClient.getArrayBuffer(`/api/documents/${previewDoc.id}/pages/${previewPage}`)
      .then((bytes) => {
        if (isCancelled) return;
        if (bytes.length < 50) {
          setPreviewPdfError("La page demandée est vide ou indisponible.");
          setPreviewPdfLoading(false);
          return;
        }
        setPreviewPdfData(bytes);
        setPreviewPdfLoading(false);
      })
      .catch((err) => {
        if (isCancelled) return;
        console.error("Erreur chargement page PDF:", err);
        setPreviewPdfError("Impossible de charger cette page du document. Le fichier est introuvable ou inaccessible.");
        setPreviewPdfLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [previewDoc?.id, previewPage]);

  const fetchDrafts = () => {
    if (!token) return;
    const params = new URLSearchParams();
    params.set('page', draftsPage.toString());
    params.set('pageSize', draftsPageSize.toString());
    if (draftsStatusFilter) params.set('status', draftsStatusFilter);
    if (draftsLegalStatusFilter) params.set('legalStatus', draftsLegalStatusFilter);
    if (draftsCategoryFilter) params.set('categoryName', draftsCategoryFilter);
    if (draftsMinioFilter === 'MINIO_ONLY') params.set('isDeployedToMinio', 'true');
    else if (draftsMinioFilter === 'NOT_MINIO') params.set('isDeployedToMinio', 'false');
    if (draftsSearchTerm.trim()) params.set('search', draftsSearchTerm.trim());

    apiClient.get<any>(`/api/admin/drafts?${params.toString()}`)
      .then(res => {
        setDrafts(res.items || []);
        setDraftsTotalCount(res.totalCount || 0);
      })
      .catch(err => console.error("Failed to load drafts", err));
  };

  React.useEffect(() => {
    fetchDrafts();
  }, [token, draftsPage, draftsPageSize, draftsStatusFilter, draftsLegalStatusFilter, draftsCategoryFilter, draftsMinioFilter, draftsSearchTerm]);

  const handlePublishDraft = async (draftId: string) => {
    setDraftActionLoading(prev => ({ ...prev, [draftId]: true }));
    try {
      const res = await apiClient.post<any>(`/api/admin/drafts/${draftId}/publish`, {});
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({ type: 'success', message: res.message || "Brouillon validé et publié avec succès !" });
        setTimeout(() => setActionAlert(null), 5000);
        fetchDrafts();
        fetchDocuments();
        if (previewDoc?.id === draftId) closeDocumentPreview();
        if (editingDraft?.id === draftId) setEditingDraft(null);
      } else {
        alert(res?.message || "Erreur lors de la publication.");
      }
    } catch (err: any) {
      alert(err.message || err.data?.message || "Erreur lors de la publication.");
    } finally {
      setDraftActionLoading(prev => ({ ...prev, [draftId]: false }));
    }
  };

  const handlePublishBatch = async (publishAllReady: boolean = false) => {
    if (!publishAllReady && selectedDraftIds.length === 0) {
      alert("Veuillez sélectionner au moins un brouillon à publier.");
      return;
    }
    if (!confirm(publishAllReady ? "Voulez-vous valider et publier tous les brouillons prêts ?" : `Voulez-vous valider et publier les ${selectedDraftIds.length} brouillons sélectionnés ?`)) {
      return;
    }
    setBatchPublishing(true);
    try {
      const res = await apiClient.post<any>('/api/admin/drafts/publish-batch', {
        draftIds: publishAllReady ? null : selectedDraftIds,
        publishAllReady
      });
      if (res) {
        setActionAlert({
          type: res.success ? 'success' : 'error',
          message: res.message || `${res.publishedCount || 0} brouillon(s) publié(s).`
        });
        setTimeout(() => setActionAlert(null), 6000);
        setSelectedDraftIds([]);
        fetchDrafts();
        fetchDocuments();
      }
    } catch (err: any) {
      alert(err.message || err.data?.message || "Erreur lors de la publication groupée.");
    } finally {
      setBatchPublishing(false);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    if (!confirm("Voulez-vous supprimer définitivement ce brouillon ?")) return;
    setDraftActionLoading(prev => ({ ...prev, [draftId]: true }));
    try {
      const res = await apiClient.delete<any>(`/api/admin/drafts/${draftId}`);
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({ type: 'success', message: "Brouillon supprimé avec succès." });
        setTimeout(() => setActionAlert(null), 4000);
        fetchDrafts();
        if (previewDoc?.id === draftId) closeDocumentPreview();
        if (editingDraft?.id === draftId) setEditingDraft(null);
      } else {
        alert(res?.message || "Erreur lors de la suppression.");
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors de la suppression.");
    } finally {
      setDraftActionLoading(prev => ({ ...prev, [draftId]: false }));
    }
  };

  const [draftFile, setDraftFile] = React.useState<File | null>(null);
  const [editDraftWithFile, setEditDraftWithFile] = React.useState(false);

  const openEditDraftModal = (draft: any) => {
    setEditingDraft(draft);
    setDraftFile(null);
    setEditDraftWithFile(false);
    setEditDraftForm({
      officialTitle: draft.officialTitle || '',
      referenceNumber: draft.referenceNumber || '',
      categoryName: draft.categoryName || draft.nature || '',
      effectiveYear: draft.effectiveYear || new Date().getFullYear(),
      dateOfAdoption: draft.dateOfAdoption ? draft.dateOfAdoption.substring(0, 10) : '',
      effectiveDate: draft.effectiveDate ? draft.effectiveDate.substring(0, 10) : '',
      areaOfLaw: draft.areaOfLaw || '',
      scopeOfApplication: draft.scopeOfApplication || 'National',
      legalOrigin: draft.legalOrigin || 'NATIONAL',
      legalStatus: draft.legalStatus || 'EnVigeur',
      groupStatus: draft.groupStatus || '',
      relatedTexts: draft.relatedTexts || '',
      legalKeywords: draft.legalKeywords || '',
      issuingAuthority: draft.issuingAuthority || ''
    });
  };

  const handleSaveEditDraft = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingDraft) return;

    if (editDraftWithFile && !draftFile) {
      alert("Veuillez sélectionner un fichier PDF à téléverser avant de sauvegarder le brouillon.");
      return;
    }

    setSavingDraft(true);
    try {
      const formData = new FormData();
      formData.append('officialTitle', editDraftForm.officialTitle || '');
      formData.append('referenceNumber', editDraftForm.referenceNumber || '');
      formData.append('categoryName', editDraftForm.categoryName || '');
      if (editDraftForm.effectiveYear) formData.append('effectiveYear', editDraftForm.effectiveYear.toString());
      if (editDraftForm.dateOfAdoption) formData.append('dateOfAdoption', new Date(editDraftForm.dateOfAdoption).toISOString());
      if (editDraftForm.effectiveDate) formData.append('effectiveDate', new Date(editDraftForm.effectiveDate).toISOString());
      formData.append('areaOfLaw', editDraftForm.areaOfLaw || '');
      formData.append('scopeOfApplication', editDraftForm.scopeOfApplication || 'National');
      formData.append('legalOrigin', editDraftForm.legalOrigin || 'NATIONAL');
      formData.append('legalStatus', editDraftForm.legalStatus || 'EnVigeur');
      formData.append('groupStatus', editDraftForm.groupStatus || '');
      formData.append('relatedTexts', editDraftForm.relatedTexts || '');
      formData.append('legalKeywords', editDraftForm.legalKeywords || '');
      formData.append('issuingAuthority', editDraftForm.issuingAuthority || '');

      if (editDraftWithFile && draftFile) {
        formData.append('file', draftFile);
      }

      const res = await apiClient.putForm<any>(`/api/admin/drafts/${editingDraft.id}`, formData);
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({ type: 'success', message: res.message || "Brouillon mis à jour avec succès !" });
        setTimeout(() => setActionAlert(null), 4000);
        setEditingDraft(null);
        setDraftFile(null);
        fetchDrafts();
      } else {
        alert(res?.message || "Erreur lors de la mise à jour du brouillon.");
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors de la mise à jour du brouillon.");
    } finally {
      setSavingDraft(false);
    }
  };

  // ── Excel Import & Synchronization state ───────────────────────────────
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [importFile, setImportFile] = React.useState<File | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [importJobId, setImportJobId] = React.useState<string | null>(null);
  const [importJobStatus, setImportJobStatus] = React.useState<any | null>(null);
  const [importError, setImportError] = React.useState<string | null>(null);
  const importPollingRef = React.useRef<any>(null);

  const startImportExcel = async () => {
    if (!importFile) return;
    setIsImporting(true);
    setImportError(null);
    setImportJobStatus(null);
    setImportJobId(null);

    const formData = new FormData();
    formData.append('ExcelFile', importFile);

    try {
      const res = await apiClient.postForm<any>('/api/documents/import-xlsx', formData);
      if (res && res.importId) {
        setImportJobId(res.importId);
        pollImportStatus(res.importId);
      } else {
        setIsImporting(false);
        setImportError(res?.message || res?.Error || "Erreur lors du lancement de l'importation.");
      }
    } catch (err: any) {
      setIsImporting(false);
      setImportError(err.message || "Erreur lors de l'envoi du fichier.");
    }
  };

  const startUpdateExcel = async () => {
    if (!importFile) return;
    setIsImporting(true);
    setImportError(null);
    setImportJobStatus(null);
    setImportJobId(null);

    const formData = new FormData();
    formData.append('ExcelFile', importFile);

    try {
      const res = await apiClient.postForm<any>('/api/documents/import-update-xlsx', formData);
      if (res && res.importId) {
        setImportJobId(res.importId);
        pollImportStatus(res.importId);
      } else {
        setIsImporting(false);
        setImportError(res?.message || res?.Error || "Erreur lors du lancement de la mise à jour.");
      }
    } catch (err: any) {
      setIsImporting(false);
      setImportError(err.message || "Erreur lors de l'envoi du fichier.");
    }
  };

  const pollImportStatus = (jobId: string) => {
    if (importPollingRef.current) clearInterval(importPollingRef.current);

    const check = async () => {
      try {
        const status = await apiClient.get<any>(`/api/documents/imports/${jobId}/status`);
        if (status) {
          setImportJobStatus(status);
          if (status.status === 'Completed' || status.status === 'Failed') {
            setIsImporting(false);
            if (importPollingRef.current) clearInterval(importPollingRef.current);
            fetchDrafts();
          }
        }
      } catch (err) {
        console.error("Error polling import status", err);
      }
    };

    check();
    importPollingRef.current = setInterval(check, 1500);
  };

  React.useEffect(() => {
    return () => {
      if (importPollingRef.current) clearInterval(importPollingRef.current);
    };
  }, []);

  // ── Types pour le suivi d'activité (Connexions & Consultations) ────────
  interface LoginHistoryItem {
    id: string;
    accountId: string;
    userName: string;
    email: string;
    fullName: string;
    phoneNumber?: string;
    accountType: string;
    groupName?: string;
    loginTime: string;
    ipAddress: string;
    userAgent: string;
    isActive: boolean;
  }

  interface ConsultationHistoryItem {
    id: string;
    accountId: string;
    userName: string;
    email: string;
    fullName: string;
    phoneNumber?: string;
    accountType: string;
    groupName?: string;
    distinctDocumentsCount?: number;
    maxConsultations?: number;
    documentId: string;
    documentTitle: string;
    referenceNumber?: string;
    categoryName?: string;
    areaOfLaw?: string;
    effectiveYear?: number;
    consultedAt: string;
  }

  interface UserConsultationSummary {
    accountId: string;
    userName: string;
    email: string;
    fullName: string;
    groupName?: string;
    maxConsultations: number;
    totalConsulted: number;
    page?: number;
    pageSize?: number;
    consultations: ConsultationHistoryItem[];
  }

  interface DownloadHistoryItem {
    id: string;
    accountId: string;
    userName: string;
    email: string;
    fullName: string;
    phoneNumber?: string;
    accountType: string;
    groupName?: string;
    distinctDocumentsCount?: number;
    maxDownloads?: number;
    documentId: string;
    documentTitle: string;
    referenceNumber?: string;
    categoryName?: string;
    areaOfLaw?: string;
    effectiveYear?: number;
    downloadedAt: string;
  }

  interface UserDownloadSummary {
    accountId: string;
    userName: string;
    email: string;
    fullName: string;
    groupName?: string;
    maxDownloads: number;
    totalDownloaded: number;
    page?: number;
    pageSize?: number;
    downloads: DownloadHistoryItem[];
  }

  interface SearchLogItem {
    id: string;
    query: string;
    normalizedQuery: string;
    totalResults: number;
    hasResults: boolean;
    executionTimeMs: number;
    isAuthenticated: boolean;
    accountId?: string;
    userFullName?: string;
    userEmail?: string;
    accountType?: string;
    groupName?: string;
    visitorToken?: string;
    ipAddress?: string;
    userAgent?: string;
    deviceType?: string;
    filtersApplied?: string;
    page: number;
    searchOrigin: string;
    executedAt: string;
  }

  interface TopQueryItem {
    query: string;
    count: number;
    lastSearchedAt: string;
  }

  interface SearchAnalyticsSummary {
    totalSearches: number;
    successSearches: number;
    unfoundSearches: number;
    successRate: number;
    anonymousSearchesCount: number;
    authenticatedSearchesCount: number;
    topUnfoundQueries: TopQueryItem[];
    topSearchedQueries: TopQueryItem[];
  }

  // ── Activité (Connexions, Consultations, Téléchargements & Recherches) state ──
  const [activitySubTab, setActivitySubTab] = React.useState<'LOGINS' | 'CONSULTATIONS' | 'DOWNLOADS' | 'SEARCHES'>('LOGINS');

  // Connexions state
  const [loginHistory, setLoginHistory] = React.useState<LoginHistoryItem[]>([]);
  const [loginsPage, setLoginsPage] = React.useState(1);
  const [loginsPageSize, setLoginsPageSize] = React.useState(20);
  const [loginsTotalCount, setLoginsTotalCount] = React.useState(0);
  const [loginsSearchInput, setLoginsSearchInput] = React.useState('');
  const [loginsSearchTerm, setLoginsSearchTerm] = React.useState('');
  const [loginsLoading, setLoginsLoading] = React.useState(false);

  // Consultations state
  const [consultationHistory, setConsultationHistory] = React.useState<ConsultationHistoryItem[]>([]);
  const [consultationsPage, setConsultationsPage] = React.useState(1);
  const [consultationsPageSize, setConsultationsPageSize] = React.useState(20);
  const [consultationsTotalCount, setConsultationsTotalCount] = React.useState(0);
  const [consultationsSearchInput, setConsultationsSearchInput] = React.useState('');
  const [consultationsSearchTerm, setConsultationsSearchTerm] = React.useState('');
  const [consultationsLoading, setConsultationsLoading] = React.useState(false);

  // Téléchargements state
  const [downloadHistory, setDownloadHistory] = React.useState<DownloadHistoryItem[]>([]);
  const [downloadsPage, setDownloadsPage] = React.useState(1);
  const [downloadsPageSize, setDownloadsPageSize] = React.useState(20);
  const [downloadsTotalCount, setDownloadsTotalCount] = React.useState(0);
  const [downloadsSearchInput, setDownloadsSearchInput] = React.useState('');
  const [downloadsSearchTerm, setDownloadsSearchTerm] = React.useState('');
  const [downloadsLoading, setDownloadsLoading] = React.useState(false);

  // Recherches state
  const [searchHistory, setSearchHistory] = React.useState<SearchLogItem[]>([]);
  const [searchAnalytics, setSearchAnalytics] = React.useState<SearchAnalyticsSummary | null>(null);
  const [searchesPage, setSearchesPage] = React.useState(1);
  const [searchesPageSize, setSearchesPageSize] = React.useState(20);
  const [searchesTotalCount, setSearchesTotalCount] = React.useState(0);
  const [searchesSearchInput, setSearchesSearchInput] = React.useState('');
  const [searchesSearchTerm, setSearchesSearchTerm] = React.useState('');
  const [searchesStatusFilter, setSearchesStatusFilter] = React.useState<'ALL' | 'FOUND_ONLY' | 'NOT_FOUND_ONLY'>('ALL');
  const [searchesUserFilter, setSearchesUserFilter] = React.useState<'ALL' | 'AUTHENTICATED_ONLY' | 'ANONYMOUS_ONLY'>('ALL');
  const [searchesOriginFilter, setSearchesOriginFilter] = React.useState<'ALL' | 'GLOBAL_SEARCH' | 'AUTOCOMPLETE_CLICK'>('ALL');
  const [searchesLoading, setSearchesLoading] = React.useState(false);

  // Modal consultation d'un utilisateur spécifique
  const [selectedUserConsultation, setSelectedUserConsultation] = React.useState<UserConsultationSummary | null>(null);
  const [userConsultationLoading, setUserConsultationLoading] = React.useState(false);
  const [modalConsultationsPage, setModalConsultationsPage] = React.useState(1);
  const [modalConsultationsPageSize, setModalConsultationsPageSize] = React.useState(5);

  // Modal téléchargements d'un utilisateur spécifique
  const [selectedUserDownload, setSelectedUserDownload] = React.useState<UserDownloadSummary | null>(null);
  const [userDownloadLoading, setUserDownloadLoading] = React.useState(false);
  const [modalDownloadsPage, setModalDownloadsPage] = React.useState(1);
  const [modalDownloadsPageSize, setModalDownloadsPageSize] = React.useState(5);

  const fetchLoginHistory = React.useCallback(() => {
    if (!token || !canViewLogins) return;
    setLoginsLoading(true);
    const params = new URLSearchParams();
    params.set('page', loginsPage.toString());
    params.set('pageSize', loginsPageSize.toString());
    if (loginsSearchTerm.trim()) params.set('search', loginsSearchTerm.trim());

    apiClient.get<any>(`/api/admin/login-history?${params.toString()}`)
      .then(res => {
        setLoginHistory(res.items || []);
        setLoginsTotalCount(res.totalCount || 0);
      })
      .catch(console.error)
      .finally(() => setLoginsLoading(false));
  }, [token, canViewLogins, loginsPage, loginsPageSize, loginsSearchTerm]);

  const fetchConsultationHistory = React.useCallback(() => {
    if (!token || !canViewConsultations) return;
    setConsultationsLoading(true);
    const params = new URLSearchParams();
    params.set('page', consultationsPage.toString());
    params.set('pageSize', consultationsPageSize.toString());
    if (consultationsSearchTerm.trim()) params.set('search', consultationsSearchTerm.trim());

    apiClient.get<any>(`/api/admin/consultation-history?${params.toString()}`)
      .then(res => {
        setConsultationHistory(res.items || []);
        setConsultationsTotalCount(res.totalCount || 0);
      })
      .catch(console.error)
      .finally(() => setConsultationsLoading(false));
  }, [token, canViewConsultations, consultationsPage, consultationsPageSize, consultationsSearchTerm]);

  const fetchUserConsultationsPage = React.useCallback(async (userId: string, page: number, pageSize: number) => {
    if (!token || !userId) return;
    setUserConsultationLoading(true);
    try {
      const res = await apiClient.get<UserConsultationSummary>(`/api/admin/users/${userId}/consultations?page=${page}&pageSize=${pageSize}`);
      if (res) {
        setSelectedUserConsultation(res);
        setModalConsultationsPage(res.page ?? page);
        setModalConsultationsPageSize(res.pageSize ?? pageSize);
      }
    } catch (err) {
      console.error("Erreur chargement consultations utilisateur", err);
    } finally {
      setUserConsultationLoading(false);
    }
  }, [token]);

  const openUserConsultationsModal = (user: any) => {
    if (!token || !user?.id) return;
    const initialPage = 1;
    const initialPageSize = modalConsultationsPageSize || 5;
    setModalConsultationsPage(initialPage);
    setSelectedUserConsultation({
      accountId: user.id,
      userName: user.userName || user.email,
      email: user.email,
      fullName: user.fullName || user.userName,
      groupName: user.groupLabel,
      maxConsultations: user.maxConsultations !== undefined ? user.maxConsultations : -1,
      totalConsulted: user.distinctDocumentsCount ?? 0,
      page: initialPage,
      pageSize: initialPageSize,
      consultations: []
    });
    fetchUserConsultationsPage(user.id, initialPage, initialPageSize);
  };

  const fetchDownloadHistory = React.useCallback(() => {
    if (!token || !canViewDownloads) return;
    setDownloadsLoading(true);
    const params = new URLSearchParams();
    params.set('page', downloadsPage.toString());
    params.set('pageSize', downloadsPageSize.toString());
    if (downloadsSearchTerm.trim()) params.set('search', downloadsSearchTerm.trim());

    apiClient.get<any>(`/api/admin/download-history?${params.toString()}`)
      .then(res => {
        setDownloadHistory(res.items || []);
        setDownloadsTotalCount(res.totalCount || 0);
      })
      .catch(console.error)
      .finally(() => setDownloadsLoading(false));
  }, [token, canViewDownloads, downloadsPage, downloadsPageSize, downloadsSearchTerm]);

  const fetchUserDownloadsPage = React.useCallback(async (userId: string, page: number, pageSize: number) => {
    if (!token || !userId) return;
    setUserDownloadLoading(true);
    try {
      const res = await apiClient.get<UserDownloadSummary>(`/api/admin/users/${userId}/downloads?page=${page}&pageSize=${pageSize}`);
      if (res) {
        setSelectedUserDownload(res);
        setModalDownloadsPage(res.page ?? page);
        setModalDownloadsPageSize(res.pageSize ?? pageSize);
      }
    } catch (err) {
      console.error("Erreur chargement téléchargements utilisateur", err);
    } finally {
      setUserDownloadLoading(false);
    }
  }, [token]);

  const openUserDownloadsModal = (user: any) => {
    if (!token || !user?.id) return;
    const initialPage = 1;
    const initialPageSize = modalDownloadsPageSize || 5;
    setModalDownloadsPage(initialPage);
    setSelectedUserDownload({
      accountId: user.id,
      userName: user.userName || user.email,
      email: user.email,
      fullName: user.fullName || user.userName,
      groupName: user.groupLabel,
      maxDownloads: user.maxDownloads !== undefined ? user.maxDownloads : -1,
      totalDownloaded: user.distinctDocumentsCount ?? 0,
      page: initialPage,
      pageSize: initialPageSize,
      downloads: []
    });
    fetchUserDownloadsPage(user.id, initialPage, initialPageSize);
  };

  const fetchSearchHistory = React.useCallback(() => {
    if (!token || !canViewSearches) return;
    setSearchesLoading(true);
    const params = new URLSearchParams();
    params.set('page', searchesPage.toString());
    params.set('pageSize', searchesPageSize.toString());
    if (searchesSearchTerm.trim()) params.set('search', searchesSearchTerm.trim());
    if (searchesStatusFilter !== 'ALL') params.set('statusFilter', searchesStatusFilter);
    if (searchesUserFilter !== 'ALL') params.set('userFilter', searchesUserFilter);
    if (searchesOriginFilter !== 'ALL') params.set('originFilter', searchesOriginFilter);

    apiClient.get<any>(`/api/admin/search-history?${params.toString()}`)
      .then(res => {
        if (res) {
          setSearchHistory(res.logs?.items || []);
          setSearchesTotalCount(res.logs?.totalCount || 0);
          setSearchAnalytics({
            totalSearches: res.totalSearches || 0,
            successSearches: res.successSearches || 0,
            unfoundSearches: res.unfoundSearches || 0,
            successRate: res.successRate || 100,
            anonymousSearchesCount: res.anonymousSearchesCount || 0,
            authenticatedSearchesCount: res.authenticatedSearchesCount || 0,
            topUnfoundQueries: res.topUnfoundQueries || [],
            topSearchedQueries: res.topSearchedQueries || []
          });
        }
      })
      .catch(console.error)
      .finally(() => setSearchesLoading(false));
  }, [token, canViewSearches, searchesPage, searchesPageSize, searchesSearchTerm, searchesStatusFilter, searchesUserFilter, searchesOriginFilter]);

  const fetchActivityCounts = React.useCallback(async () => {
    if (!token || !canViewActivity) return;
    try {
      const counts = await apiClient.get<{ loginsCount: number; consultationsCount: number; downloadsCount: number; searchesCount: number }>('/api/admin/activity-counts');
      if (counts) {
        if (typeof counts.loginsCount === 'number') setLoginsTotalCount(counts.loginsCount);
        if (typeof counts.consultationsCount === 'number') setConsultationsTotalCount(counts.consultationsCount);
        if (typeof counts.downloadsCount === 'number') setDownloadsTotalCount(counts.downloadsCount);
        if (typeof counts.searchesCount === 'number') setSearchesTotalCount(counts.searchesCount);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des compteurs d'activité:", err);
    }
  }, [token, canViewActivity]);

  const availableTabs = React.useMemo(() => {
    const list: Array<{ id: 'ACTES' | 'BROUILLONS' | 'AJOUT' | 'OCR' | 'STATS' | 'USERS' | 'GROUPES' | 'ACTIVITY', icon: any, label: string, count?: number }> = [];
    if (canViewDocs) list.push({ id: 'ACTES', icon: Database, label: 'Liste des Actes', count: docsTotalCount });
    if (canViewDocs || canAddDocs) list.push({ id: 'BROUILLONS', icon: FileText, label: 'Brouillons', count: draftsTotalCount });
    if (canAddDocs) list.push({ id: 'AJOUT', icon: Plus, label: 'Enregistrer un Acte' });
    if (canManageOcr) list.push({ id: 'OCR', icon: Sparkles, label: 'Traitement OCR' });
    if (canViewStats) list.push({ id: 'STATS', icon: Activity, label: 'Suggestions', count: suggestions.filter((s: any) => s.status === 'PENDING').length });
    if (canViewUserManagement) list.push({ id: 'USERS', icon: Users, label: 'Utilisateurs', count: usersTotalCount });
    if (canManageGroups) list.push({ id: 'GROUPES', icon: Shield, label: 'Groupes & Permissions' });
    if (canViewActivity) list.push({ id: 'ACTIVITY', icon: History, label: 'Connexions & Activités' });
    return list;
  }, [canViewDocs, canAddDocs, canManageOcr, canViewStats, canViewUserManagement, canManageGroups, canViewActivity, docsTotalCount, draftsTotalCount, suggestions, usersTotalCount]);

  const [activeSubTab, setActiveSubTab] = React.useState<'ACTES' | 'BROUILLONS' | 'AJOUT' | 'OCR' | 'STATS' | 'USERS' | 'GROUPES' | 'ACTIVITY'>('ACTES');

  React.useEffect(() => {
    if (availableTabs.length > 0) {
      if (!availableTabs.some(t => t.id === activeSubTab)) {
        setActiveSubTab(availableTabs[0].id);
      }
    }
  }, [availableTabs, activeSubTab]);

  // Si le sous-onglet d'activité actuel n'est pas autorisé pour le staff, basculer sur le premier permis
  React.useEffect(() => {
    if (activeSubTab === 'ACTIVITY') {
      if (activitySubTab === 'LOGINS' && !canViewLogins) {
        if (canViewConsultations) setActivitySubTab('CONSULTATIONS');
        else if (canViewDownloads) setActivitySubTab('DOWNLOADS');
        else if (canViewSearches) setActivitySubTab('SEARCHES');
      } else if (activitySubTab === 'CONSULTATIONS' && !canViewConsultations) {
        if (canViewLogins) setActivitySubTab('LOGINS');
        else if (canViewDownloads) setActivitySubTab('DOWNLOADS');
        else if (canViewSearches) setActivitySubTab('SEARCHES');
      } else if (activitySubTab === 'DOWNLOADS' && !canViewDownloads) {
        if (canViewLogins) setActivitySubTab('LOGINS');
        else if (canViewConsultations) setActivitySubTab('CONSULTATIONS');
        else if (canViewSearches) setActivitySubTab('SEARCHES');
      } else if (activitySubTab === 'SEARCHES' && !canViewSearches) {
        if (canViewLogins) setActivitySubTab('LOGINS');
        else if (canViewConsultations) setActivitySubTab('CONSULTATIONS');
        else if (canViewDownloads) setActivitySubTab('DOWNLOADS');
      }
    }
  }, [activeSubTab, activitySubTab, canViewLogins, canViewConsultations, canViewDownloads, canViewSearches]);

  React.useEffect(() => {
    if (activeSubTab === 'ACTIVITY' && canViewActivity) {
      fetchActivityCounts();
    }
  }, [activeSubTab, canViewActivity, fetchActivityCounts]);

  React.useEffect(() => {
    if (activeSubTab === 'ACTIVITY') {
      if (activitySubTab === 'LOGINS' && canViewLogins) {
        fetchLoginHistory();
      } else if (activitySubTab === 'CONSULTATIONS' && canViewConsultations) {
        fetchConsultationHistory();
      } else if (activitySubTab === 'DOWNLOADS' && canViewDownloads) {
        fetchDownloadHistory();
      } else if (activitySubTab === 'SEARCHES' && canViewSearches) {
        fetchSearchHistory();
      }
    }
  }, [activeSubTab, activitySubTab, canViewLogins, canViewConsultations, canViewDownloads, canViewSearches, fetchLoginHistory, fetchConsultationHistory, fetchDownloadHistory, fetchSearchHistory]);

  React.useEffect(() => {
    fetchAdminStats();
  }, [fetchAdminStats, activeSubTab]);

  // ── Groups & Permissions state ────────────────────────────────────────
  type GroupKey = string; // Now GroupKey will be the Guid
  const [groupLabels, setGroupLabels] = React.useState<Record<string, string>>({});
  const [groupDescriptions, setGroupDescriptions] = React.useState<Record<string, string>>({});
  
  // A generic type for permissions fetched from API
  type PermKey = string; 
  
  const [groupPerms, setGroupPerms] = React.useState<Record<string, Record<string, boolean>>>({});
  const [selectedGroup, setSelectedGroup] = React.useState<string>('');
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({ Abonnements: true, Configuration: true, Documents: true, Groupes: true, Interactions: true, Statistiques: true, Thématiques: true, Utilisateurs: true, 'Intelligence Artificielle': true, 'Activité & Traçabilité': true, 'OCR & Numérisation': true });
  
  const [editingGroupKey, setEditingGroupKey] = React.useState<string | null>(null);
  const [tempGroupLabel, setTempGroupLabel] = React.useState('');

  const [apiCategories, setApiCategories] = React.useState<any[]>([]);
  const [featuresMap, setFeaturesMap] = React.useState<Record<string, string>>({});

  const [showCreateGroupModal, setShowCreateGroupModal] = React.useState(false);
  const [newGroupForm, setNewGroupForm] = React.useState({ label: '', description: '', category: 'CUSTOMER' });
  const [isCreatingGroup, setIsCreatingGroup] = React.useState(false);

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupForm.label) return;
    setIsCreatingGroup(true);
    try {
      const created = await apiClient.post<any>('/api/groups', {
        label: newGroupForm.label,
        description: newGroupForm.description,
        groupCategory: newGroupForm.category
      });
      if (created) {
        setGroupLabels(prev => ({ ...prev, [created.id]: created.label }));
        setGroupDescriptions(prev => ({ ...prev, [created.id]: created.description }));
        setSelectedGroup(created.id);
        setShowCreateGroupModal(false);
        setNewGroupForm({ label: '', description: '', category: 'CUSTOMER' });
        setMsg('Groupe créé avec succès. Veuillez configurer ses permissions.');
        setTimeout(() => setMsg(''), 3000);
      } else {
        alert("Erreur lors de la création du groupe.");
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la création du groupe.");
    } finally {
      setIsCreatingGroup(false);
    }
  };

  interface GroupQuotaConfig {
    maxDailyDownloads: number;
    maxWeeklyDownloads: number;
    maxMonthlyDownloads: number;
    maxConsultations: number;
    isFormulaVisible: boolean;
  }
  const [groupQuotas, setGroupQuotas] = React.useState<Record<string, GroupQuotaConfig>>({});
  const [isSavingQuotas, setIsSavingQuotas] = React.useState(false);

  const handleSaveGroupQuotas = async () => {
    if (!selectedGroup) return;
    const current = groupQuotas[selectedGroup];
    if (!current) return;
    setIsSavingQuotas(true);
    try {
      await apiClient.put(`/api/groups/${selectedGroup}/quotas`, {
        maxDailyDownloads: Number(current.maxDailyDownloads),
        maxWeeklyDownloads: Number(current.maxWeeklyDownloads),
        maxMonthlyDownloads: Number(current.maxMonthlyDownloads),
        maxConsultations: Number(current.maxConsultations),
        isFormulaVisible: Boolean(current.isFormulaVisible)
      });
      formulaService.clearCache();
      setMsg('Quotas et visibilité de la formule enregistrés avec succès.');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      console.error("Failed to save group quotas", err);
      alert("Erreur lors de l'enregistrement des quotas.");
    } finally {
      setIsSavingQuotas(false);
    }
  };

  React.useEffect(() => {
    // Fetch Groups
    if (!token) return;
    apiClient.get<any>('/api/groups')
      .then(res => {
        const data: any[] = res.items || res || [];
        setAvailableGroups(data);
        const labels: Record<string, string> = {};
        const descs: Record<string, string> = {};
        const quotas: Record<string, GroupQuotaConfig> = {};
        data.forEach((g: any) => {
          const gid = g.id || g.Id;
          labels[gid] = g.label || g.Label;
          descs[gid] = g.description || g.Description;
          quotas[gid] = {
            maxDailyDownloads: g.maxDailyDownloads ?? g.MaxDailyDownloads ?? -1,
            maxWeeklyDownloads: g.maxWeeklyDownloads ?? g.MaxWeeklyDownloads ?? -1,
            maxMonthlyDownloads: g.maxMonthlyDownloads ?? g.MaxMonthlyDownloads ?? -1,
            maxConsultations: g.maxConsultations ?? g.MaxConsultations ?? -1,
            isFormulaVisible: g.isFormulaVisible ?? g.IsFormulaVisible ?? false
          };
        });
        setGroupLabels(labels);
        setGroupDescriptions(descs);
        setGroupQuotas(quotas);
        if (data.length > 0) setSelectedGroup(data[0].id || data[0].Id);
      })
      .catch(err => console.error("Failed to load groups", err));
  }, [token]);

  React.useEffect(() => {
    if (selectedGroup && token) {
      apiClient.get<any[]>(`/api/groups/${selectedGroup}/permissions`)
        .then(data => {
          const perms: Record<string, boolean> = {};
          const categoriesMap: Record<string, any[]> = {};
          const fMap: Record<string, string> = {};

          data.forEach((p: any) => {
            perms[p.feature.code] = p.canDo;
            fMap[p.feature.code] = p.featureId;

            if (!categoriesMap[p.feature.category]) categoriesMap[p.feature.category] = [];
            categoriesMap[p.feature.category].push({
              key: p.feature.code,
              label: p.feature.labelFr,
              desc: p.feature.description
            });
          });

          const cats = Object.keys(categoriesMap).map(cat => ({
            key: cat,
            label: cat,
            perms: categoriesMap[cat]
          }));

          setGroupPerms(prev => ({ ...prev, [selectedGroup]: perms }));
          setApiCategories(cats);
          setFeaturesMap(fMap);
        })
        .catch(err => console.error("Failed to load permissions", err));
    }
  }, [selectedGroup, token]);

  const togglePerm = async (group: string, perm: string) => {
    const featureId = featuresMap[perm];
    if (!featureId) return;

    // Optimistic UI update
    setGroupPerms(prev => ({
      ...prev,
      [group]: { ...prev[group], [perm]: !prev[group]?.[perm] }
    }));

    try {
      await apiClient.post(`/api/groups/${group}/permissions/${featureId}/toggle`, {});
    } catch (err) {
      console.error("Failed to toggle permission", err);
      // Revert on error
      setGroupPerms(prev => ({
        ...prev,
        [group]: { ...prev[group], [perm]: !prev[group]?.[perm] }
      }));
    }
  };

  // ── Create User Modal state ────────────────────────────────────────────
  const [showCreateUserModal, setShowCreateUserModal] = React.useState(false);
  const [newUserForm, setNewUserForm] = React.useState({
    fullName: '', email: '', phone: '', address: '',
    cabinetName: '', role: 'USER', group: 'PARTICULIER',
    password: '', expiryDate: (() => {
      const d = new Date(); d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0];
    })()
  });

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    const id = `usr-${Date.now()}`;
    const newUser = { ...newUserForm, id, status: 'APPROVED', createdAt: new Date().toISOString(), activationExpiry: newUserForm.expiryDate };
    setUsers(prev => [...prev, newUser]);
    setShowCreateUserModal(false);
    setNewUserForm({ fullName: '', email: '', phone: '', address: '', cabinetName: '', role: 'USER', group: 'PARTICULIER', password: '', expiryDate: (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; })() });
    setMsg('Compte utilisateur créé avec succès !');
    setTimeout(() => setMsg(''), 3000);
  };

  // ── Activate User with Expiry Date state ──────────────────────────────
  const [activatingUserId, setActivatingUserId] = React.useState<string | null>(null);
  const [activationExpiry, setActivationExpiry] = React.useState<string>(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split('T')[0];
  });

  const handleActivateUser = () => {
    if (!activatingUserId) return;
    setUsers(prev => prev.map(u => u.id === activatingUserId ? { ...u, status: 'APPROVED', activationExpiry } : u));
    setActivatingUserId(null);
    setMsg('Compte activé jusqu\'au ' + new Date(activationExpiry).toLocaleDateString('fr-FR'));
    setTimeout(() => setMsg(''), 3000);
  };

  // Form states (vides par défaut, aucun préchargement forcé)
  const [title, setTitle] = React.useState('');
  const [refNum, setRefNum] = React.useState('');
  const [nature, setNature] = React.useState<string>(''); // Vide par défaut
  const [origin, setOrigin] = React.useState<LegalOrigin>('NATIONAL');
  const [status, setStatus] = React.useState<string>('EnVigeur');
  const [domain, setDomain] = React.useState<string>(''); // Vide par défaut
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [adoptDate, setAdoptDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [authority, setAuthority] = React.useState('');
  const [keywordsText, setKeywordsText] = React.useState('');
  const [markdownContent, setMarkdownContent] = React.useState('');
  const [pdfFile, setPdfFile] = React.useState<File | null>(null);
  const [msg, setMsg] = React.useState('');
  const [isCreatingAct, setIsCreatingAct] = React.useState(false);

  // Combobox Domaine
  const [domainSuggestionsOpen, setDomainSuggestionsOpen] = React.useState(false);
  const domainComboboxRef = React.useRef<HTMLDivElement>(null);

  // Combobox Nature de l'Acte
  const [natureSuggestionsOpen, setNatureSuggestionsOpen] = React.useState(false);
  const natureComboboxRef = React.useRef<HTMLDivElement>(null);

  // Fermer les listes de suggestions au clic extérieur
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (domainComboboxRef.current && !domainComboboxRef.current.contains(e.target as Node)) {
        setDomainSuggestionsOpen(false);
      }
      if (natureComboboxRef.current && !natureComboboxRef.current.contains(e.target as Node)) {
        setNatureSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAreas = React.useMemo(() => {
    if (!domain.trim()) return areaOfLawOptions;
    const q = domain.toLowerCase().trim();
    return areaOfLawOptions.filter(a => a.label.toLowerCase().includes(q) || a.value.toLowerCase().includes(q));
  }, [domain, areaOfLawOptions]);

  const filteredCategories = React.useMemo(() => {
    if (!nature.trim()) return categoryOptions;
    const q = nature.toLowerCase().trim();
    return categoryOptions.filter(c => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q));
  }, [nature, categoryOptions]);

  // Editing state (Modal)
  const [editingFullDocId, setEditingFullDocId] = React.useState<any>(null);
  const [editingDoc, setEditingDoc] = React.useState<any | null>(null);
  const [effectiveDate, setEffectiveDate] = React.useState('');
  const [relatedTexts, setRelatedTexts] = React.useState('');
  const [savingDoc, setSavingDoc] = React.useState(false);
  const [editDocWithFile, setEditDocWithFile] = React.useState(false);

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !refNum.trim()) {
      alert("Veuillez renseigner l'intitulé officiel et le numéro de référence.");
      return;
    }
    if (!nature) {
      alert("Veuillez sélectionner la nature de l'acte.");
      return;
    }
    if (!domain.trim()) {
      alert("Veuillez renseigner le domaine de droit.");
      return;
    }
    if (!adoptDate) {
      alert("Veuillez renseigner la date d'adoption.");
      return;
    }
    if (!pdfFile) {
      alert("Le document PDF officiel est strictement obligatoire.");
      return;
    }
    if (!pdfFile.name.toLowerCase().endsWith('.pdf')) {
      alert("Le document joint doit obligatoirement être un fichier au format PDF.");
      return;
    }

    setIsCreatingAct(true);
    try {
      const formData = new FormData();
      formData.append('officialTitle', title.trim());
      formData.append('referenceNumber', refNum.trim());
      formData.append('categoryName', nature);
      formData.append('effectiveYear', (year || new Date(adoptDate).getFullYear()).toString());
      formData.append('dateOfAdoption', new Date(adoptDate).toISOString());
      formData.append('effectiveDate', effectiveDate ? new Date(effectiveDate).toISOString() : new Date(adoptDate).toISOString());
      formData.append('areaOfLaw', domain.trim());
      formData.append('scopeOfApplication', origin === 'NATIONAL' ? 'National' : 'Régional');
      formData.append('legalOrigin', origin);
      formData.append('legalStatus', status || 'EnVigeur');
      formData.append('issuingAuthority', authority.trim());
      formData.append('legalKeywords', keywordsText.trim());
      formData.append('relatedTexts', relatedTexts.trim());
      formData.append('file', pdfFile);

      const res = await apiClient.postForm<any>('/api/admin/drafts', formData);
      if (res && (res.success || res.Success !== false)) {
        setActionAlert({
          type: 'success',
          message: res.message || "L'acte a été enregistré avec succès dans les brouillons et son document PDF a été hébergé sur MinIO."
        });
        setTimeout(() => setActionAlert(null), 6000);

        // Réinitialisation des champs du formulaire (tous vides par défaut)
        setTitle('');
        setRefNum('');
        setNature('');
        setDomain('');
        setAuthority('');
        setKeywordsText('');
        setRelatedTexts('');
        setPdfFile(null);

        // Recharger les brouillons, catégories et domaines
        fetchDrafts();
        apiClient.get<any[]>('/api/Categories/dropdown')
          .then(resCats => { if (Array.isArray(resCats)) setDbCategories(resCats); })
          .catch(console.error);
        apiClient.get<any[]>('/api/AreaOfLaw/dropdown')
          .then(resAreas => { if (Array.isArray(resAreas)) setDbAreasOfLaw(resAreas); })
          .catch(console.error);

        // Basculer automatiquement vers l'onglet Brouillons pour visualiser et publier
        setActiveSubTab('BROUILLONS');
      } else {
        alert(res?.message || "Erreur lors de l'enregistrement de l'acte.");
      }
    } catch (err: any) {
      console.error("Erreur enregistrement acte:", err);
      alert(err.message || err.data?.message || "Erreur lors de l'enregistrement de l'acte.");
    } finally {
      setIsCreatingAct(false);
    }
  };

  const openEditModal = (doc: any) => {
    setEditingFullDocId(doc.id);
    setEditingDoc(doc);
    setEditDocWithFile(false);
    setTitle(doc.officialTitle || '');
    setRefNum(doc.referenceNumber || '');
    setNature(doc.nature || doc.categoryName || '');
    setOrigin(doc.legalOrigin || 'NATIONAL');
    setStatus(doc.legalStatus || 'EnVigeur');
    setDomain(doc.areaOfLaw || '');
    setYear(doc.effectiveYear || new Date().getFullYear());
    setAdoptDate(doc.dateOfAdoption ? doc.dateOfAdoption.substring(0, 10) : '');
    setEffectiveDate(doc.effectiveDate ? doc.effectiveDate.substring(0, 10) : '');
    setAuthority(doc.issuingAuthority || '');
    setKeywordsText(Array.isArray(doc.legalKeywords) ? doc.legalKeywords.join(', ') : (doc.legalKeywords || ''));
    setRelatedTexts(doc.relatedTexts || '');
    setMarkdownContent(doc.fullTextMarkdown || '');
    setPdfFile(null);
  };

  const closeEditModal = () => {
    setEditingFullDocId(null);
    setEditingDoc(null);
    setEditDocWithFile(false);
    setTitle('');
    setRefNum('');
    setNature('');
    setDomain('');
    setKeywordsText('');
    setMarkdownContent('');
    setAuthority('');
    setRelatedTexts('');
    setEffectiveDate('');
    setPdfFile(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFullDocId || !title || !refNum) return;

    if (editDocWithFile && !pdfFile) {
      alert("Veuillez sélectionner un fichier PDF à téléverser avant de valider l'enregistrement.");
      return;
    }

    setSavingDoc(true);
    try {
      const formData = new FormData();
      formData.append('officialTitle', title.trim());
      formData.append('referenceNumber', refNum.trim());
      formData.append('categoryName', nature);
      formData.append('areaOfLaw', domain);
      formData.append('legalOrigin', origin);
      formData.append('legalStatus', status);
      formData.append('effectiveYear', year ? year.toString() : '');
      if (adoptDate) formData.append('dateOfAdoption', new Date(adoptDate).toISOString());
      if (effectiveDate) formData.append('effectiveDate', new Date(effectiveDate).toISOString());
      formData.append('issuingAuthority', authority || 'Autorité Administrative Camerounaise');
      formData.append('legalKeywords', keywordsText);
      formData.append('relatedTexts', relatedTexts);

      if (editDocWithFile && pdfFile) {
        formData.append('file', pdfFile);
      }

      const res = await apiClient.putForm<any>(`/api/admin/documents/${editingFullDocId}`, formData);
      if (res && (res.success || res.Success !== false)) {
        setMsg("Acte juridique mis à jour et synchronisé avec succès !");
        setActionAlert({ type: 'success', message: res.message || "Acte juridique mis à jour et synchronisé avec succès !" });
        setTimeout(() => setActionAlert(null), 4000);
        setTimeout(() => {
          setMsg('');
          closeEditModal();
        }, 1200);
        fetchDocuments();
      } else {
        alert(res?.message || "Erreur lors de la mise à jour de l'acte.");
      }
    } catch (err: any) {
      alert(err.message || "Erreur lors de la mise à jour de l'acte.");
    } finally {
      setSavingDoc(false);
    }
  };

  const handleApproveWithNotification = (id: string) => {
     approveSuggestion(id, documents);
     alert("Requête approuvée ! L'acte a été généré et inséré dans la base.");
  };

  const naturesList: DocumentNature[] = ['CONSTITUTION', 'LOI', 'ORDONNANCE', 'DECRET', 'ARRETE', 'CIRCULAIRE', 'DECISION', 'OHADA', 'CEMAC', 'COBAC', 'TRAITE', 'DIRECTIVE'];
  const originsList: LegalOrigin[] = ['NATIONAL', 'COMMUNITY', 'INTERNATIONAL'];
  const statusValues: LegalStatus[] = ['EnVigeur', 'Modifie', 'Abroge', 'Suspendu', 'Remplace'];
  const domainsList: LegalDomain[] = ['DROIT_CIVIL', 'DROIT_PENAL', 'DROIT_COMMERCIAL', 'DROIT_FISCAL', 'DROIT_SOCIAL', 'DROIT_ADMINISTRATIF', 'DROIT_CONSTITUTIONNEL', 'DROIT_FONCIER', 'DROIT_MINIER', 'DROIT_ENVIRONNEMENT', 'DROIT_DOUANIER', 'DROIT_BANQUE', 'DROIT_OHADA', 'DROIT_INTERNATIONAL', 'DROIT_TRAVAIL'];

  const PaginationBar = ({ page, pageSize, totalCount, onPageChange, onPageSizeChange }: {
    page: number; pageSize: number; totalCount: number;
    onPageChange: (p: number) => void; onPageSizeChange: (s: number) => void;
  }) => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-3 border-t border-zinc-100 bg-zinc-50/50 text-xs">
        <span className="text-zinc-500 font-medium">{totalCount} résultat(s)</span>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500">Par page :</span>
          <select
            value={pageSize}
            onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
            className="border border-zinc-200 rounded-lg px-2 py-1 text-xs font-bold bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
          >
            {[10, 20, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="px-2.5 py-1 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed font-bold transition-colors"
          >‹</button>
          <span className="font-bold text-zinc-700 min-w-[80px] text-center">Page {page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="px-2.5 py-1 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed font-bold transition-colors"
          >›</button>
        </div>
      </div>
    );
  };

  const TabButton = ({ id, icon: Icon, label, count }: { id: any, icon: any, label: string, count?: number }) => {
    const isActive = activeSubTab === id;
    return (
      <button
        onClick={() => setActiveSubTab(id)}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-300 ${
          isActive 
            ? 'bg-white text-primary-750 shadow-md shadow-primary-750/10 border border-primary-750/10 scale-105' 
            : 'text-zinc-500 hover:text-zinc-800 hover:bg-zinc-200/50'
        }`}
      >
        <Icon className={`w-4 h-4 ${isActive ? 'text-primary-600' : 'text-zinc-400'}`} />
        {label}
        {count !== undefined && (
          <span className={`ml-1.5 px-2 py-0.5 rounded-full text-[10px] ${isActive ? 'bg-primary-100 text-primary-800' : 'bg-zinc-200 text-zinc-600'}`}>
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
      
      {/* Premium Header Section */}
      <div className="bg-gradient-to-r from-primary-950 via-primary-800 to-primary-900 rounded-2xl p-4 sm:p-5 text-white shadow-xl shadow-primary-900/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-500/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-bold uppercase tracking-widest backdrop-blur-sm">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Accès Sécurisé
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
              <LayoutDashboard className="w-8 h-8 text-white/80" /> Portail d'Administration
            </h2>
            <p className="text-white/70 text-sm max-w-xl">
              Espace d'indexation et de gestion global réservé aux administrateurs de la base de données DOCARN JURIDIQUE.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-5 py-4 rounded-2xl flex items-center gap-4">
             <div>
               <div className="text-xs text-white/60 font-bold uppercase tracking-wider">Connecté en tant que</div>
               <div className="font-display font-bold text-lg">
                 {isAdmin ? 'Admin Général' : (currentUser?.fullName || (isStaff ? 'Membre Staff' : 'Utilisateur'))}
               </div>
               <div className="text-[10px] text-white/70 font-semibold mt-0.5">
                 {isAdmin ? 'Super Administrateur Système' : `Groupe : ${currentUser?.cabinetName || 'Personnel'}`}
               </div>
             </div>
          </div>
        </div>
      </div>

      {/* Modern Tabs Navigation - Only allowed modules */}
      <div className="flex bg-zinc-100/80 p-1.5 rounded-xl gap-1.5 overflow-x-auto border border-primary-750/5 shadow-inner">
        {availableTabs.map(tab => (
          <TabButton key={tab.id} id={tab.id} icon={tab.icon} label={tab.label} count={tab.count} />
        ))}
      </div>

      {availableTabs.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-3xl p-12 text-center max-w-md mx-auto my-8 shadow-sm">
          <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-amber-200">
            <Lock className="w-8 h-8" />
          </div>
          <h3 className="font-display font-bold text-lg text-zinc-900 mb-2">Accès restreint aux modules</h3>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Votre profil Staff ne dispose actuellement d'aucune permission active pour les modules de gestion.
            Veuillez demander à un Administrateur d'activer les permissions requises dans <strong>Groupes &amp; Permissions</strong>.
          </p>
        </div>
      )}

      <div className="animate-fade-in">
        {/* 1. DOCUMENTS DATABASE GRID TABLE (CRUD) */}
        {activeSubTab === 'ACTES' && canViewDocs && (
          <div className="bg-white border border-primary-750/10 rounded-3xl overflow-hidden shadow-xl shadow-primary-750/5 space-y-0">
            {/* Header épuré et sobre avec compteur et bouton rafraîchir */}
            <div className="p-5 sm:p-6 bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display text-lg font-bold text-zinc-900">Catalogue des Textes (Édition)</h3>
                  <span className="bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-emerald-200/60">
                    {docsTotalCount} acte{docsTotalCount > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 font-medium">
                  Gérez, modifiez ou supprimez les actes juridiques publiés dans la bibliothèque officielle.
                </p>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => fetchDocuments()}
                  className="p-2 text-zinc-600 hover:text-zinc-900 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-xl transition-all shadow-sm cursor-pointer"
                  title="Rafraîchir la liste"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Section Filtres Avancés & Recherche (Identique à Brouillons) */}
            <div className="p-5 border-b border-zinc-100 bg-zinc-50/70 space-y-4">
              {/* Ligne 1 : Disponibilité du document (MinIO) */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1 mr-1">
                    <FileText className="w-3.5 h-3.5 text-emerald-600" /> Disponibilité du document :
                  </span>
                  {[
                    { value: 'ALL', label: 'Tous' },
                    { value: 'MINIO_ONLY', label: '✅ Document disponible' },
                    { value: 'NOT_MINIO', label: '⚠️ Fichier manquant' }
                  ].map(opt => {
                    const isSelected = docsMinioFilter === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setDocsMinioFilter(opt.value as any); setDocsPage(1); }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-emerald-700 text-white shadow-md shadow-emerald-700/20'
                            : 'bg-white text-zinc-600 border border-zinc-200 hover:border-emerald-300 hover:text-emerald-700 shadow-sm'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Ligne 2 : Filtres Déroulants & Recherche Rapide */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-zinc-200/60">
                {/* État Juridique */}
                <div>
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    État Juridique
                  </label>
                  <select
                    value={docsLegalStatusFilter}
                    onChange={(e) => { setDocsLegalStatusFilter(e.target.value); setDocsPage(1); }}
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer shadow-sm"
                  >
                    <option value="">Tous les états juridiques</option>
                    {legalStatusOptions.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Nature de l'Acte */}
                <div>
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    Nature de l'Acte
                  </label>
                  <select
                    value={docsCategoryFilter}
                    onChange={(e) => { setDocsCategoryFilter(e.target.value); setDocsPage(1); }}
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 cursor-pointer shadow-sm"
                  >
                    <option value="">Toutes les natures</option>
                    {categoryOptions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Recherche Rapide */}
                <div className="lg:col-span-2">
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    Recherche Rapide
                  </label>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setDocsSearchTerm(docsSearchInput);
                      setDocsPage(1);
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Titre, référence, autorité, mots-clés..."
                        value={docsSearchInput}
                        onChange={(e) => setDocsSearchInput(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-sm"
                      />
                      {docsSearchInput && (
                        <button
                          type="button"
                          onClick={() => {
                            setDocsSearchInput('');
                            setDocsSearchTerm('');
                            setDocsPage(1);
                          }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-0.5"
                          title="Effacer la recherche"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition-all cursor-pointer"
                    >
                      Rechercher
                    </button>
                  </form>
                </div>
              </div>

              {/* Bandeau de réinitialisation si filtres actifs */}
              {(docsMinioFilter !== 'ALL' || docsLegalStatusFilter || docsCategoryFilter || docsSearchTerm) && (
                <div className="flex items-center justify-between bg-emerald-50/70 border border-emerald-200/60 p-2.5 px-4 rounded-xl text-xs text-emerald-900 animate-fade-in">
                  <div className="flex items-center gap-2">
                    <span className="font-bold">Filtres actifs :</span>
                    <span>Affichage de <strong>{documents.length}</strong> acte(s) sur un total de <strong>{docsTotalCount}</strong>.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDocsMinioFilter('ALL');
                      setDocsLegalStatusFilter('');
                      setDocsCategoryFilter('');
                      setDocsSearchInput('');
                      setDocsSearchTerm('');
                      setDocsPage(1);
                    }}
                    className="font-bold text-emerald-800 hover:text-emerald-950 underline text-xs cursor-pointer"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-zinc-50/50 border-b border-zinc-100 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                  <tr>
                    <th className="p-5 font-semibold">Nature</th>
                    <th className="p-5 font-semibold">Référence</th>
                    <th className="p-5 font-semibold w-5/12">Intitulé Officiel</th>
                    <th className="p-5 font-semibold text-center">Document</th>
                    <th className="p-5 font-semibold text-center">Statut / Vigueur</th>
                    <th className="p-5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {documents.map((doc) => {
                    return (
                      <tr key={doc.id} className="hover:bg-primary-50/30 transition-colors group">
                        {/* Nature */}
                        <td className="p-5 shrink-0">
                          <span className="font-mono font-bold bg-primary-100 text-primary-800 border border-primary-200/50 px-2.5 py-1 rounded-md text-[10px] shadow-sm">
                            {doc.categoryName || doc.nature}
                          </span>
                        </td>

                        {/* Reference Number */}
                        <td className="p-5 font-mono text-xs font-semibold text-zinc-700">
                          {doc.referenceNumber}
                        </td>

                        {/* Title block */}
                        <td className="p-5">
                          <span className="line-clamp-2 leading-relaxed text-sm font-semibold text-zinc-800 group-hover:text-primary-750 transition-colors">
                            {doc.officialTitle}
                          </span>
                          {doc.issuingAuthority && (
                            <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-1.5">
                              <span>{doc.issuingAuthority}</span>
                              {doc.effectiveYear && <span>• {doc.effectiveYear}</span>}
                            </div>
                          )}
                        </td>

                        {/* Document PDF Availability */}
                        <td className="p-5 text-center">
                          {doc.isDeployedToMinio ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200" title="Document PDF officiel disponible">
                              <FileText className="w-3 h-3 text-emerald-600" />
                              PDF disponible
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200" title="Aucun fichier PDF associé à cet acte">
                              <AlertCircle className="w-3 h-3 text-amber-600" />
                              Fichier manquant
                            </span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="p-5 text-center">
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                              doc.legalStatus === 'EnVigeur' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              doc.legalStatus === 'Modifie' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              doc.legalStatus === 'Abroge' ? 'bg-red-50 text-red-700 border-red-200' :
                              'bg-zinc-100 text-zinc-600 border-zinc-200'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                doc.legalStatus === 'EnVigeur' ? 'bg-emerald-500' :
                                doc.legalStatus === 'Modifie' ? 'bg-amber-500' :
                                doc.legalStatus === 'Abroge' ? 'bg-red-500' : 'bg-zinc-400'
                              }`}></span>
                              {doc.legalStatus ? doc.legalStatus.replace('_', ' ') : 'Statut inconnu'}
                            </div>
                        </td>

                        {/* CRUD Actions */}
                        <td className="p-5 text-right">
                          <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => openDocumentPreview(doc, false)}
                              className="bg-white border border-zinc-200 hover:border-emerald-600 hover:bg-emerald-50 text-zinc-500 hover:text-emerald-700 p-2.5 rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                              title="Prévisualiser l'acte (page par page)"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => openEditModal(doc)}
                              disabled={!!docActionLoading[doc.id?.toString()]}
                              className="bg-white border border-zinc-200 hover:border-primary-750 hover:bg-primary-50 text-zinc-500 hover:text-primary-750 disabled:opacity-50 disabled:cursor-not-allowed p-2.5 rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                              title="Modifier cet acte"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => handleRevertToDraft(doc.id, doc.officialTitle)}
                              disabled={!!docActionLoading[doc.id?.toString()]}
                              className="bg-white border border-zinc-200 hover:border-amber-500 hover:bg-amber-50 text-zinc-500 hover:text-amber-600 disabled:opacity-50 disabled:cursor-not-allowed p-2.5 rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                              title="Renvoyer en brouillon (dépublier du site public et retirer du moteur de recherche)"
                            >
                              {docActionLoading[doc.id?.toString()] ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" /> : <RotateCcw className="w-4 h-4" />}
                            </button>

                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              disabled={!!docActionLoading[doc.id?.toString()]}
                              className="bg-white border border-zinc-200 hover:border-red-500 hover:bg-red-50 text-zinc-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed p-2.5 rounded-xl hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                              title="Supprimer définitivement l'acte"
                            >
                              {docActionLoading[doc.id?.toString()] ? <Loader2 className="w-4 h-4 animate-spin text-red-600" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-12 text-center">
                        <div className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center mx-auto text-zinc-400 mb-3">
                          <Search className="w-6 h-6" />
                        </div>
                        <h4 className="font-bold text-zinc-800 text-sm mb-1">Aucun acte juridique trouvé</h4>
                        <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-3">
                          Aucun acte ne correspond à vos filtres ou à votre recherche actuelle.
                        </p>
                        {(docsMinioFilter !== 'ALL' || docsLegalStatusFilter || docsCategoryFilter || docsSearchTerm) && (
                          <button
                            type="button"
                            onClick={() => {
                              setDocsMinioFilter('ALL');
                              setDocsLegalStatusFilter('');
                              setDocsCategoryFilter('');
                              setDocsSearchInput('');
                              setDocsSearchTerm('');
                              setDocsPage(1);
                            }}
                            className="text-xs font-bold text-emerald-700 hover:text-emerald-800 underline cursor-pointer"
                          >
                            Réinitialiser tous les filtres
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={docsPage} pageSize={docsPageSize} totalCount={docsTotalCount}
              onPageChange={setDocsPage} onPageSizeChange={setDocsPageSize}
            />
          </div>
        )}

        {/* 1.B. BROUILLONS & WORKFLOW DE VALIDATION */}
        {activeSubTab === 'BROUILLONS' && (canViewDocs || canAddDocs) && (
          <div className="bg-white border border-primary-750/10 rounded-3xl overflow-hidden shadow-xl shadow-primary-750/5 space-y-0">
            {/* Header épuré et sobre */}
            <div className="p-5 sm:p-6 bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display text-lg font-bold text-zinc-900">Brouillons d'Actes</h3>
                  <span className="bg-indigo-100 text-indigo-800 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-indigo-200/60">
                    {draftsTotalCount} acte{draftsTotalCount > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 font-medium">
                  Vérifiez les métadonnées et prévisualisez les documents avant validation et publication officielle.
                </p>
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setShowImportModal(true); setImportFile(null); setImportJobStatus(null); setImportError(null); }}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-sm flex items-center gap-2 transition-all cursor-pointer"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Importer Excel (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={() => fetchDrafts()}
                  className="p-2 text-zinc-600 hover:text-zinc-900 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-xl transition-all shadow-sm cursor-pointer"
                  title="Rafraîchir la liste"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                {selectedDraftIds.length > 0 && (
                  <button
                    type="button"
                    disabled={batchPublishing}
                    onClick={() => handlePublishBatch(false)}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-sm flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    {batchPublishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                    Publier la sélection ({selectedDraftIds.length})
                  </button>
                )}
                <button
                  type="button"
                  disabled={batchPublishing || draftsTotalCount === 0}
                  onClick={() => handlePublishBatch(true)}
                  className="bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-sm flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  {batchPublishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Tout publier ({draftsTotalCount})
                </button>
              </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="p-5 border-b border-zinc-100 bg-zinc-50/70 space-y-4">
              {/* Row 1: Quick Filter Pills (Document & Processing Status) */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Document Availability filter */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1 mr-1">
                    <FileText className="w-3.5 h-3.5 text-indigo-600" /> Disponibilité du document :
                  </span>
                  {[
                    { value: 'ALL', label: 'Tous' },
                    { value: 'MINIO_ONLY', label: '✅ Document disponible' },
                    { value: 'NOT_MINIO', label: '⚠️ Fichier manquant' }
                  ].map(opt => {
                    const isSelected = draftsMinioFilter === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setDraftsMinioFilter(opt.value as any); setDraftsPage(1); }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-700 text-white shadow-md shadow-indigo-700/20'
                            : 'bg-white text-zinc-600 border border-zinc-200 hover:border-indigo-300 hover:text-indigo-700 shadow-sm'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Processing / Ingestion Status Pills */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1 mr-1">
                    <Activity className="w-3.5 h-3.5 text-indigo-600" /> Statut Traitement :
                  </span>
                  {[
                    { value: '', label: 'Tous les statuts' },
                    { value: 'Prêt', label: '✨ Prêts' },
                    { value: 'En attente', label: '🕒 En attente' },
                    { value: 'Erreur', label: '⚠️ Échec / Erreur' }
                  ].map(opt => {
                    const isSelected = draftsStatusFilter === opt.value;
                    return (
                      <button
                        key={opt.value || 'all'}
                        type="button"
                        onClick={() => { setDraftsStatusFilter(opt.value); setDraftsPage(1); }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-indigo-700 text-white shadow-md shadow-indigo-700/20'
                            : 'bg-white text-zinc-600 border border-zinc-200 hover:border-indigo-300 hover:text-indigo-700 shadow-sm'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Row 2: Dropdowns & Search */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-zinc-200/60">
                {/* Legal Status Dropdown */}
                <div>
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    État Juridique
                  </label>
                  <select
                    value={draftsLegalStatusFilter}
                    onChange={(e) => { setDraftsLegalStatusFilter(e.target.value); setDraftsPage(1); }}
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer shadow-sm"
                  >
                    <option value="">Tous les états juridiques</option>
                    {legalStatusOptions.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                {/* Nature / Category Dropdown */}
                <div>
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    Nature de l'Acte
                  </label>
                  <select
                    value={draftsCategoryFilter}
                    onChange={(e) => { setDraftsCategoryFilter(e.target.value); setDraftsPage(1); }}
                    className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer shadow-sm"
                  >
                    <option value="">Toutes les natures</option>
                    {categoryOptions.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {/* Full-text Search Bar */}
                <div className="lg:col-span-2">
                  <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1">
                    Recherche Rapide
                  </label>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setDraftsSearchTerm(draftsSearchInput);
                      setDraftsPage(1);
                    }}
                    className="flex items-center gap-2"
                  >
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Titre, référence, autorité, mots-clés..."
                        value={draftsSearchInput}
                        onChange={(e) => setDraftsSearchInput(e.target.value)}
                        className="w-full pl-9 pr-8 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
                      />
                      {draftsSearchInput && (
                        <button
                          type="button"
                          onClick={() => {
                            setDraftsSearchInput('');
                            setDraftsSearchTerm('');
                            setDraftsPage(1);
                          }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-0.5"
                          title="Effacer la recherche"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-sm transition-all cursor-pointer"
                    >
                      Rechercher
                    </button>
                  </form>
                </div>
              </div>

              {/* Reset active filters banner if filtered */}
              {(draftsMinioFilter !== 'ALL' || draftsStatusFilter || draftsLegalStatusFilter || draftsCategoryFilter || draftsSearchTerm) && (
                <div className="flex items-center justify-between bg-indigo-50/70 border border-indigo-100 px-4 py-2 rounded-xl text-xs">
                  <div className="flex items-center gap-2 text-indigo-900 font-medium">
                    <Filter className="w-3.5 h-3.5 text-indigo-600" />
                    <span>
                      Filtres actifs : 
                      {draftsMinioFilter !== 'ALL' && <strong className="ml-1 text-indigo-700">Disponibilité ({draftsMinioFilter === 'MINIO_ONLY' ? 'Document disponible' : 'Fichier manquant'})</strong>}
                      {draftsLegalStatusFilter && <strong className="ml-1 text-indigo-700">État ({draftsLegalStatusFilter})</strong>}
                      {draftsStatusFilter && <strong className="ml-1 text-indigo-700">Statut ({draftsStatusFilter})</strong>}
                      {draftsCategoryFilter && <strong className="ml-1 text-indigo-700">Nature ({draftsCategoryFilter})</strong>}
                      {draftsSearchTerm && <strong className="ml-1 text-indigo-700">Recherche ("{draftsSearchTerm}")</strong>}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDraftsMinioFilter('ALL');
                      setDraftsStatusFilter('');
                      setDraftsLegalStatusFilter('');
                      setDraftsCategoryFilter('');
                      setDraftsSearchInput('');
                      setDraftsSearchTerm('');
                      setDraftsPage(1);
                    }}
                    className="text-indigo-700 hover:text-indigo-900 font-bold text-xs flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" /> Réinitialiser tous les filtres
                  </button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-zinc-50/70 border-b border-zinc-100 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                  <tr>
                    <th className="p-4 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={drafts.length > 0 && selectedDraftIds.length === drafts.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDraftIds(drafts.map(d => d.id));
                          } else {
                            setSelectedDraftIds([]);
                          }
                        }}
                        className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                      />
                    </th>
                    <th className="p-4 font-semibold">Nature &amp; Catégorie</th>
                    <th className="p-4 font-semibold">Référence</th>
                    <th className="p-4 font-semibold w-4/12">Intitulé Officiel</th>
                    <th className="p-4 font-semibold text-center">Document PDF</th>
                    <th className="p-4 font-semibold text-center">État Juridique</th>
                    <th className="p-4 font-semibold text-center">Statut</th>
                    <th className="p-4 font-semibold text-right">Actions de Validation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {drafts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-12 text-center text-zinc-500">
                        <div className="max-w-sm mx-auto flex flex-col items-center">
                          <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mb-3">
                            <FileText className="w-6 h-6" />
                          </div>
                          <div className="font-bold text-sm text-zinc-800">Aucun acte correspondant aux filtres</div>
                          <p className="text-xs text-zinc-400 mt-1">Modifiez vos critères de recherche ou réinitialisez les filtres pour afficher les autres brouillons.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    drafts.map((draft) => {
                      const isSelected = selectedDraftIds.includes(draft.id);
                      const isActionLoading = draftActionLoading[draft.id];
                      return (
                        <tr key={draft.id} className={`hover:bg-indigo-50/30 transition-colors ${isSelected ? 'bg-indigo-50/50' : ''}`}>
                          {/* Selection Checkbox */}
                          <td className="p-4 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDraftIds(prev => [...prev, draft.id]);
                                } else {
                                  setSelectedDraftIds(prev => prev.filter(id => id !== draft.id));
                                }
                              }}
                              className="rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          </td>

                          {/* Nature / Category */}
                          <td className="p-4 shrink-0">
                            <span className="font-mono font-bold bg-indigo-100 text-indigo-800 border border-indigo-200/50 px-2.5 py-1 rounded-md text-[10px] shadow-sm">
                              {draft.categoryName || 'Acte'}
                            </span>
                          </td>

                          {/* Reference Number */}
                          <td className="p-4 font-mono text-xs font-bold text-zinc-800">
                            {draft.referenceNumber}
                          </td>

                          {/* Title block */}
                          <td className="p-4">
                            <div className="font-semibold text-xs text-zinc-900 line-clamp-2 leading-relaxed">
                              {draft.officialTitle}
                            </div>
                            {draft.issuingAuthority && (
                              <div className="text-[10px] text-zinc-400 font-medium mt-0.5">
                                {draft.issuingAuthority}
                              </div>
                            )}
                          </td>

                          {/* Document Status */}
                          <td className="p-4 text-center">
                            {draft.isDeployedToMinio ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <Check className="w-3 h-3 text-emerald-600" /> Document disponible
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                <AlertCircle className="w-3 h-3 text-amber-600" /> Fichier manquant
                              </span>
                            )}
                          </td>

                          {/* Legal Status */}
                          <td className="p-4 text-center">
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${
                              draft.legalStatus === 'EnVigeur' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              draft.legalStatus === 'Modifie' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              draft.legalStatus === 'Abroge' ? 'bg-red-50 text-red-700 border-red-200' :
                              'bg-zinc-100 text-zinc-600 border-zinc-200'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                draft.legalStatus === 'EnVigeur' ? 'bg-emerald-500' :
                                draft.legalStatus === 'Modifie' ? 'bg-amber-500' :
                                draft.legalStatus === 'Abroge' ? 'bg-red-500' : 'bg-zinc-400'
                              }`}></span>
                              {draft.legalStatus ? draft.legalStatus.replace('_', ' ') : 'Non défini'}
                            </div>
                          </td>

                          {/* Draft Status */}
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200">
                              {draft.status || 'Prêt'}
                            </span>
                          </td>

                          {/* Actions */}
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* Preview PDF */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openDocumentPreview(draft, true);
                                }}
                                className="bg-white border border-indigo-200 hover:border-indigo-500 hover:bg-indigo-50 text-indigo-700 p-2 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                title="Visualiser le document PDF (page par page)"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span className="hidden xl:inline">Aperçu PDF</span>
                              </button>

                              {/* Edit Metadata */}
                              <button
                                onClick={() => openEditDraftModal(draft)}
                                className="bg-white border border-zinc-200 hover:border-amber-500 hover:bg-amber-50 text-zinc-600 hover:text-amber-700 p-2 rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                                title="Modifier les métadonnées"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>

                              {/* Publish Single */}
                              <button
                                disabled={isActionLoading}
                                onClick={() => handlePublishDraft(draft.id)}
                                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 px-3 rounded-xl text-xs font-bold transition-all shadow-sm hover:shadow-md flex items-center gap-1 cursor-pointer"
                                title="Valider et publier l'acte au catalogue public"
                              >
                                {isActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                <span>{isActionLoading ? 'Validation...' : 'Valider'}</span>
                              </button>

                              {/* Delete Draft */}
                              <button
                                disabled={isActionLoading}
                                onClick={() => handleDeleteDraft(draft.id)}
                                className="bg-white border border-zinc-200 hover:border-red-500 hover:bg-red-50 text-zinc-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed p-2 rounded-xl transition-all shadow-sm cursor-pointer"
                                title="Supprimer le brouillon"
                              >
                                {isActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-600" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={draftsPage} pageSize={draftsPageSize} totalCount={draftsTotalCount}
              onPageChange={setDraftsPage} onPageSizeChange={setDraftsPageSize}
            />
          </div>
        )}

        {/* 2. CREATION FORM PANEL (ENREGISTRER UN ACTE DANS LES BROUILLONS & MINIO) */}
        {activeSubTab === 'AJOUT' && canAddDocs && (
          <div className="bg-white border border-primary-750/10 rounded-2xl p-6 shadow-xl shadow-primary-750/5 relative overflow-hidden">
            <div className="relative z-10 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-zinc-100 gap-2">
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-primary-750" /> Enregistrement d'un Nouvel Acte
                  </h3>
                  <p className="text-zinc-500 text-xs mt-0.5">
                    L'acte est téléversé dans le stockage MinIO et placé en <strong>Brouillon</strong> pour prévisualisation avant mise en ligne officielle.
                  </p>
                </div>
                <div className="text-[11px] font-semibold text-zinc-500 bg-zinc-50 px-3 py-1.5 rounded-xl border border-zinc-200 shrink-0">
                  <span className="text-red-500 font-bold mr-1">*</span> Tous les champs sont obligatoires sauf l'autorité émettrice
                </div>
              </div>

              {msg && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 text-emerald-800 border border-emerald-200/60 rounded-xl font-semibold text-xs shadow-sm animate-fade-in">
                  <div className="bg-emerald-100 p-1 rounded-full"><Check className="w-3.5 h-3.5 text-emerald-600" /></div>
                  {msg}
                </div>
              )}

              <form onSubmit={handleCreateDocument} className="space-y-4">
                {/* Row 1: Nature (BD), Origine, Statut (En vigueur / Modifié / Abrogé), Domaine (Double fonction) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                  {/* Nature de l'Acte : Double fonction (Saisie libre ou Sélection d'une existante) */}
                  <div className="space-y-1 relative" ref={natureComboboxRef}>
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                        Nature de l'Acte <span className="text-red-500">*</span>
                      </label>
                      <span className="text-[9px] text-zinc-400 font-medium">Recherche ou nouvelle</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        autoComplete="off"
                        placeholder="Tapez pour chercher ou créer..."
                        value={nature}
                        onChange={(e) => {
                          setNature(e.target.value);
                          setNatureSuggestionsOpen(true);
                        }}
                        onFocus={() => setNatureSuggestionsOpen(true)}
                        className="w-full p-2.5 pr-14 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                      />
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                        {nature && (
                          <button
                            type="button"
                            onClick={() => { setNature(''); setNatureSuggestionsOpen(true); }}
                            className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
                            title="Effacer la nature"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setNatureSuggestionsOpen(prev => !prev)}
                          className="p-1.5 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
                          title="Afficher la liste des natures existantes"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Menu déroulant autocomplétion / nouvelle nature */}
                    {natureSuggestionsOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto z-50 divide-y divide-zinc-100 animate-fade-in">
                        {filteredCategories.length > 0 && (
                          <div>
                            <div className="px-3 py-1.5 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                              Natures en base de données ({filteredCategories.length})
                            </div>
                            {filteredCategories.map(item => (
                              <button
                                key={item.value}
                                type="button"
                                onClick={() => {
                                  setNature(item.label || item.value);
                                  setNatureSuggestionsOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-primary-50 hover:text-primary-750 flex items-center justify-between transition-colors cursor-pointer"
                              >
                                <span>{item.label}</span>
                                {nature.toLowerCase() === (item.label || item.value).toLowerCase() && (
                                  <Check className="w-3.5 h-3.5 text-primary-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {nature.trim() && !categoryOptions.some(c => (c.label || c.value).toLowerCase() === nature.trim().toLowerCase()) && (
                          <div className="p-2.5 bg-amber-50 text-amber-900 text-xs font-semibold flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 truncate">
                              <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                              <span className="truncate">Nouvelle nature : <strong>{nature.trim()}</strong></span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setNatureSuggestionsOpen(false)}
                              className="px-2 py-1 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[10px] font-bold rounded-lg cursor-pointer shrink-0 transition-colors"
                            >
                              Valider ce nom
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Origine Légale */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Origine Légale <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={origin}
                      onChange={(e: any) => setOrigin(e.target.value)}
                      required
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm cursor-pointer"
                    >
                      {originsList.map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  {/* Statut Juridique (En vigueur, Modifié, Abrogé) */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Statut Juridique <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      required
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm cursor-pointer"
                    >
                      {primaryLegalStatuses.map(st => (
                        <option key={st.value} value={st.value}>{st.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Domaine de Droit : Double fonction (Saisie libre ou Sélection d'un existant) */}
                  <div className="space-y-1 relative" ref={domainComboboxRef}>
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                        Domaine de Droit <span className="text-red-500">*</span>
                      </label>
                      <span className="text-[9px] text-zinc-400 font-medium">Recherche ou nouveau</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        autoComplete="off"
                        placeholder="Tapez pour chercher ou créer..."
                        value={domain}
                        onChange={(e) => {
                          setDomain(e.target.value);
                          setDomainSuggestionsOpen(true);
                        }}
                        onFocus={() => setDomainSuggestionsOpen(true)}
                        className="w-full p-2.5 pr-14 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                      />
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
                        {domain && (
                          <button
                            type="button"
                            onClick={() => { setDomain(''); setDomainSuggestionsOpen(true); }}
                            className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
                            title="Effacer le domaine"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDomainSuggestionsOpen(prev => !prev)}
                          className="p-1.5 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-600 cursor-pointer"
                          title="Afficher la liste des domaines existants"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Menu déroulant autocomplétion / nouveau domaine */}
                    {domainSuggestionsOpen && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-zinc-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto z-50 divide-y divide-zinc-100 animate-fade-in">
                        {filteredAreas.length > 0 && (
                          <div>
                            <div className="px-3 py-1.5 bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                              Domaines en base de données ({filteredAreas.length})
                            </div>
                            {filteredAreas.map(item => (
                              <button
                                key={item.value}
                                type="button"
                                onClick={() => {
                                  setDomain(item.label || item.value);
                                  setDomainSuggestionsOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-primary-50 hover:text-primary-750 flex items-center justify-between transition-colors cursor-pointer"
                              >
                                <span>{item.label}</span>
                                {domain.toLowerCase() === (item.label || item.value).toLowerCase() && (
                                  <Check className="w-3.5 h-3.5 text-primary-600" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {domain.trim() && !areaOfLawOptions.some(a => (a.label || a.value).toLowerCase() === domain.trim().toLowerCase()) && (
                          <div className="p-2.5 bg-amber-50 text-amber-900 text-xs font-semibold flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 truncate">
                              <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                              <span className="truncate">Nouveau domaine : <strong>{domain.trim()}</strong></span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setDomainSuggestionsOpen(false)}
                              className="px-2 py-1 bg-amber-200 hover:bg-amber-300 text-amber-900 text-[10px] font-bold rounded-lg cursor-pointer shrink-0 transition-colors"
                            >
                              Valider ce nom
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Référence *, Année *, Date Adoption *, Autorité Émettrice (seul optionnel) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Numéro de Référence <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Loi N° 2026/040"
                      value={refNum}
                      onChange={(e) => setRefNum(e.target.value)}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Année d'Adoption <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min={1900}
                      max={2100}
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Date d'Adoption <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={adoptDate}
                      onChange={(e) => {
                        setAdoptDate(e.target.value);
                        if (e.target.value) {
                          const parsedYear = new Date(e.target.value).getFullYear();
                          if (parsedYear && !isNaN(parsedYear)) setYear(parsedYear);
                        }
                      }}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                        Autorité Émettrice
                      </label>
                      <span className="text-[9px] text-zinc-400 font-medium">Optionnel</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Ex: Présidence de la République (Optionnel)"
                      value={authority}
                      onChange={(e) => setAuthority(e.target.value)}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>
                </div>

                {/* Row 3: Intitulé Officiel (dans un textarea pour lecture intégrale) */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                    Intitulé Officiel (Titre complet de l'acte) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Saisissez l'intitulé officiel complet du texte (Ex: Décret N° 2026/040 portant organisation et fonctionnement des services juridiques...)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full p-3 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 text-xs font-medium text-zinc-800 shadow-sm leading-relaxed resize-y"
                  />
                </div>

                {/* Row 4: Mots-clés & Textes Liés */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Mots-clés Juridiques (séparés par des virgules)
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: fiscalité, déclaration, TVA, douane"
                      value={keywordsText}
                      onChange={(e) => setKeywordsText(e.target.value)}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                      Textes Liés / Références Complémentaires
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Modifie la Loi N° 96/06 du 18 janvier 1996"
                      value={relatedTexts}
                      onChange={(e) => setRelatedTexts(e.target.value)}
                      className="w-full p-2.5 bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs font-semibold text-zinc-800 shadow-sm"
                    />
                  </div>
                </div>

                {/* Row 5: Document PDF Officiel (Strictement Obligatoire) */}
                <div className={`p-4 rounded-2xl border-2 border-dashed transition-all ${
                  pdfFile 
                    ? 'border-emerald-300 bg-emerald-50/50' 
                    : 'border-zinc-300 hover:border-primary-400 bg-zinc-50/60'
                }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${
                        pdfFile ? 'bg-emerald-100 text-emerald-700' : 'bg-primary-100 text-primary-700'
                      }`}>
                        {pdfFile ? <Check className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-bold text-zinc-900">
                            Document PDF Officiel <span className="text-red-500">*</span>
                          </h4>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            pdfFile ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {pdfFile ? 'PDF Prêt pour MinIO' : 'Obligatoire (PDF uniquement)'}
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          {pdfFile 
                            ? `Fichier sélectionné : ${pdfFile.name} (${(pdfFile.size / 1024).toFixed(1)} Ko)`
                            : 'Sélectionnez le document PDF officiel numérisé qui sera transféré dans MinIO.'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-zinc-300 hover:border-primary-600 rounded-xl text-xs font-bold text-zinc-700 hover:text-primary-750 shadow-sm cursor-pointer transition-colors">
                        <Upload className="w-4 h-4 text-primary-600" />
                        <span>{pdfFile ? 'Remplacer le PDF' : 'Sélectionner le PDF *'}</span>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          required
                          onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                          className="hidden"
                        />
                      </label>
                      {pdfFile && (
                        <button
                          type="button"
                          onClick={() => setPdfFile(null)}
                          className="p-2 hover:bg-white rounded-xl text-zinc-400 hover:text-red-500 transition-colors border border-transparent hover:border-zinc-200 shadow-sm cursor-pointer"
                          title="Supprimer ce fichier"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Submit Button avec gestion du loader et désactivation */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isCreatingAct || !pdfFile || !title.trim() || !refNum.trim() || !domain.trim() || !adoptDate || !nature}
                    className="w-full bg-gradient-to-r from-primary-750 to-primary-600 hover:from-primary-800 hover:to-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-extrabold uppercase tracking-widest py-3.5 rounded-xl shadow-lg shadow-primary-750/30 hover:shadow-xl hover:shadow-primary-750/40 hover:-translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-2 text-xs"
                  >
                    {isCreatingAct ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Enregistrement du brouillon &amp; téléversement MinIO en cours...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Enregistrer dans les Brouillons (Hébergement MinIO)</span>
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-zinc-400 text-center mt-2">
                    🔒 L'acte sera immédiatement disponible dans l'onglet <strong>Brouillons</strong> pour prévisualisation et publication au catalogue public.
                  </p>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* 3. SIMULATED ANALYTICS STATS & REQ TICKETS */}
        {activeSubTab === 'STATS' && canViewStats && (
          <div className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-bold text-zinc-900">Statistiques & Métriques de la Plateforme</h3>
                <p className="text-xs text-zinc-500 font-medium mt-0.5">Données calculées en temps réel d'après les activités et indexations de la plateforme.</p>
              </div>
              <button
                type="button"
                onClick={() => fetchAdminStats()}
                disabled={statsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-white border border-zinc-200 rounded-xl hover:bg-zinc-50 shadow-sm transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${statsLoading ? 'animate-spin text-primary-600' : ''}`} />
                Actualiser
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { 
                  title: "Consultations", 
                  value: adminStats?.consultationsFormatted ?? (statsLoading ? "..." : "0"), 
                  desc: adminStats?.consultationsDesc ?? "📈 +14.2% (Douala, Yaoundé)", 
                  icon: BarChart3, 
                  color: "text-primary-600", 
                  bg: "bg-primary-50" 
                },
                { 
                  title: "PDF Téléchargés", 
                  value: adminStats?.downloadsFormatted ?? (statsLoading ? "..." : "0"), 
                  desc: adminStats?.downloadsDesc ?? "📥 Téléchargements réels", 
                  icon: FileText, 
                  color: "text-emerald-600", 
                  bg: "bg-emerald-50" 
                },
                { 
                  title: "Dossiers Actifs", 
                  value: adminStats?.activeFoldersFormatted ?? (statsLoading ? "..." : "0"), 
                  desc: adminStats?.activeFoldersDesc ?? "📁 Dossiers utilisateurs", 
                  icon: Database, 
                  color: "text-orange-600", 
                  bg: "bg-orange-50" 
                }
              ].map((stat, idx) => (
                <div key={idx} className="bg-white border border-zinc-100 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between h-36">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{stat.title}</span>
                    <div className={`p-2 rounded-xl ${stat.bg}`}><stat.icon className={`w-4 h-4 ${stat.color}`} /></div>
                  </div>
                  <div>
                    <h4 className="font-display font-extrabold text-3xl text-zinc-900">{stat.value}</h4>
                    <p className={`text-xs font-semibold ${stat.color} mt-1`}>{stat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-primary-750/10 rounded-3xl overflow-hidden shadow-xl shadow-primary-750/5">
              <div className="p-6 bg-gradient-to-r from-amber-50/50 to-white border-b border-primary-750/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">File d'attente des Suggestions</h3>
                  <p className="text-xs text-zinc-500 font-medium">Textes demandés par la communauté pour examen et indexation.</p>
                </div>
                <span className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase px-3 py-1.5 rounded-full font-extrabold shadow-sm">
                  {suggestions.filter(s => s.status === 'PENDING').length} en attente
                </span>
              </div>

              {suggestions.filter(s => s.status === 'PENDING').length === 0 ? (
                <div className="p-12 text-center flex flex-col items-center justify-center space-y-4">
                  <div className="bg-emerald-50 p-4 rounded-full"><Check className="w-8 h-8 text-emerald-500" /></div>
                  <div className="text-sm text-zinc-500 font-medium">Aucune requête en attente. Votre bibliothèque est parfaitement à jour !</div>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {suggestions.filter(s => s.status === 'PENDING').map((s) => (
                    <div key={s.id} className="p-6 hover:bg-zinc-50/50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-6 group">
                      <div className="space-y-2.5 flex-grow">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-md uppercase">
                            {s.type}
                          </span>
                          <span className="text-[10px] font-mono text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-md">Ticket: {s.id}</span>
                          <span className="text-[10px] font-semibold text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3"/> {formatFrenchDate(s.submittedAt)}</span>
                        </div>
                        <h5 className="font-display font-bold text-lg text-zinc-900 group-hover:text-primary-750 transition-colors">
                          <span className="text-primary-750">{s.nature}</span> - {s.reference}: {s.title}
                        </h5>
                        <p className="text-sm text-zinc-600 font-medium bg-zinc-50 p-3 rounded-xl border border-zinc-100 italic">
                          "{s.description || "Aucune description"}"
                        </p>
                      </div>
                      <button
                        onClick={() => handleApproveWithNotification(s.id)}
                        className="bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold px-5 py-3 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-2 shrink-0 cursor-pointer"
                      >
                        <Check className="w-4 h-4" /> Approuver & Indexer
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 4. USER ACCOUNTS & GROUP ASSIGNMENT MANAGEMENT */}
        {activeSubTab === 'USERS' && (
          <div className="bg-white border border-violet-100 rounded-3xl overflow-hidden shadow-xl shadow-violet-100/20 space-y-0">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-violet-900 via-primary-900 to-violet-950 text-white flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-bold uppercase tracking-wider mb-2">
                  <ShieldCheck className="w-3 h-3 text-emerald-400" /> Gestion des accès & groupes
                </div>
                <h3 className="font-display text-xl font-extrabold tracking-tight">Utilisateurs &amp; Attribution des Groupes</h3>
                <p className="text-xs text-white/70 mt-1">Consultez les nouveaux comptes créés, filtrez par profil (Staff, Particulier, Entreprise) et affectez les groupes de permissions.</p>
              </div>
              <div className="flex items-center gap-3">
                {(isAdmin || canCreateStaff || canActivateAccount) && (
                  <button 
                    onClick={() => setIsCreateUserModalOpen(true)}
                    className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-2xl text-xs font-bold flex items-center gap-2 transition-all shadow-sm shadow-primary-600/20 border border-primary-500 cursor-pointer"
                  >
                    <UserPlus className="w-4 h-4" />
                    Nouvel Utilisateur
                  </button>
                )}
                {canCreateStaff && (
                  <button 
                    onClick={() => setIsCreateStaffModalOpen(true)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2.5 rounded-2xl text-xs font-bold flex items-center gap-2 transition-all shadow-sm shadow-emerald-500/20 border border-emerald-400 cursor-pointer"
                  >
                    <UserPlus className="w-4 h-4" />
                    Nouveau Staff
                  </button>
                )}
                <div className="bg-white/10 border border-white/15 rounded-2xl px-4 py-2.5 flex items-center gap-3 text-right">
                  <div>
                    <div className="text-[10px] uppercase font-bold text-white/60">Total répertorié</div>
                    <div className="text-xl font-extrabold text-white">{usersTotalCount}</div>
                  </div>
                  <Users className="w-8 h-8 text-white/40" />
                </div>
              </div>
            </div>

            {/* Toast Alert */}
            {actionAlert && (
              <div className={`mx-6 mt-4 p-4 rounded-2xl flex items-center gap-3 text-xs font-bold ${
                actionAlert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {actionAlert.type === 'success' ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                <div className="flex-1">{actionAlert.message}</div>
                <button onClick={() => setActionAlert(null)} className="text-zinc-400 hover:text-zinc-700 cursor-pointer">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Filter Section */}
            <div className="p-6 border-b border-zinc-100 bg-zinc-50/50 space-y-4">
              {/* Type Filter Tabs */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1.5 mr-1">
                    <Filter className="w-3.5 h-3.5" /> Type de compte :
                  </span>
                  {[
                    { value: '', label: 'Tous les types', icon: Users, show: canViewStaff && canViewIndividuals && canViewBusinesses },
                    { value: 'STAFF', label: 'Staff Docarn', icon: Briefcase, show: canViewStaff },
                    { value: 'INDIVIDUAL', label: 'Particulier', icon: User, show: canViewIndividuals },
                    { value: 'BUSINESS', label: 'Entreprise', icon: Building2, show: canViewBusinesses }
                  ].filter(opt => opt.show).map(opt => {
                    const isSelected = usersAccountTypeFilter === opt.value;
                    const IconComp = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => { setUsersAccountTypeFilter(opt.value); setUsersPage(1); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-violet-700 text-white shadow-md shadow-violet-700/20 scale-105'
                            : 'bg-white text-zinc-600 border border-zinc-200 hover:border-violet-300 hover:text-violet-700 shadow-sm'
                        }`}
                      >
                        <IconComp className="w-3.5 h-3.5" />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Search Bar */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setUsersSearchTerm(usersSearchInput);
                    setUsersPage(1);
                  }}
                  className="flex items-center gap-2 min-w-[260px]"
                >
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Nom, nom d'utilisateur, email, tél..."
                      value={usersSearchInput}
                      onChange={(e) => setUsersSearchInput(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-violet-700 hover:bg-violet-800 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-sm transition-all cursor-pointer"
                  >
                    Filtrer
                  </button>
                </form>
              </div>

              {/* Status Filter (Pending vs All vs Assigned) */}
              <div className="flex items-center gap-2 pt-2 border-t border-zinc-200/60 flex-wrap">
                <span className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mr-1">Statut Groupe :</span>
                {[
                  { value: 'PENDING', label: "⏳ En attente d'attribution de groupe (Sans groupe)", highlight: true },
                  { value: 'ALL', label: '👥 Tous les comptes' },
                  { value: 'ASSIGNED', label: '🛡️ Comptes avec groupe actif' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setUsersPendingFilter(opt.value as any); setUsersPage(1); }}
                    className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all border cursor-pointer flex items-center gap-1.5 ${
                      usersPendingFilter === opt.value
                        ? opt.highlight
                          ? 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-500/20'
                          : 'bg-zinc-800 text-white border-zinc-800 shadow-sm'
                        : opt.highlight
                          ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
                          : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-zinc-50 border-b border-zinc-100 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                  <tr>
                    <th className="p-4 font-bold">Utilisateur / Identité</th>
                    <th className="p-4 font-bold">Contact &amp; Localisation</th>
                    <th className="p-4 font-bold">Inscrit le</th>
                    <th className="p-4 font-bold text-center">Profil &amp; Statuts</th>
                    <th className="p-4 font-bold">Groupe Attribué</th>
                    <th className="p-4 font-bold">Expiration Compte</th>
                    <th className="p-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-12 text-center text-zinc-500">
                        <div className="max-w-sm mx-auto flex flex-col items-center">
                          <div className="w-12 h-12 bg-violet-50 text-violet-600 rounded-full flex items-center justify-center mb-3">
                            <Users className="w-6 h-6" />
                          </div>
                          <div className="font-bold text-sm text-zinc-800">Aucun compte trouvé</div>
                          <p className="text-xs text-zinc-400 mt-1">Aucun utilisateur ne correspond aux filtres sélectionnés.</p>
                          {(usersAccountTypeFilter || usersPendingFilter !== 'ALL' || usersSearchTerm) && (
                            <button
                              onClick={() => {
                                setUsersAccountTypeFilter('');
                                setUsersPendingFilter('ALL');
                                setUsersSearchInput('');
                                setUsersSearchTerm('');
                                setUsersPage(1);
                              }}
                              className="mt-3 text-xs font-bold text-violet-700 hover:underline cursor-pointer"
                            >
                              Réinitialiser les filtres
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((u) => {
                      const isStaffUser = (u.accountType || '').toUpperCase() === 'STAFF';
                      const isUserAdmin = (u.accountType || '').toUpperCase() === 'ADMIN' || (u.groupCategory || '').toUpperCase() === 'ADMIN';
                      const expectedCat = isStaffUser ? 'STAFF' : 'CUSTOMER';
                      const userGroups = availableGroups.filter((g: any) => {
                        const cat = (g.groupCategory || g.GroupCategory || '').toUpperCase();
                        return cat === expectedCat && cat !== 'ADMIN';
                      });
                      const defaultGId = userGroups.length > 0 ? (userGroups[0].id || userGroups[0].Id) : '';
                      const currentSelectedGId = selectedGroupForUser[u.id] || (u.groupId && userGroups.some((g: any) => (g.id || g.Id) === u.groupId) ? u.groupId : defaultGId);
                      const isAssigning = !!assigningLoading[u.id];

                      const today = new Date().toISOString().split('T')[0];
                      const expVal = selectedExpireAtForUser[u.id] !== undefined 
                        ? selectedExpireAtForUser[u.id] 
                        : (u.expireAt ? u.expireAt.substring(0, 10) : '');

                      const accountTypeBadge = u.accountType === 'STAFF' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200">
                          <Briefcase className="w-3 h-3" /> STAFF
                        </span>
                      ) : u.accountType === 'BUSINESS' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-violet-50 text-violet-800 border border-violet-200">
                          <Building2 className="w-3 h-3" /> ENTREPRISE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-sky-50 text-sky-800 border border-sky-200">
                          <User className="w-3 h-3" /> PARTICULIER
                        </span>
                      );

                      return (
                        <tr key={u.id} className="group hover:bg-violet-50/20 transition-colors">
                          {/* Identité Détaillée */}
                          <td className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-extrabold shadow-sm shrink-0 mt-0.5 ${
                                u.accountType === 'STAFF' ? 'bg-emerald-100 text-emerald-800' :
                                u.accountType === 'BUSINESS' ? 'bg-violet-100 text-violet-800' :
                                'bg-sky-100 text-sky-800'
                              }`}>
                                {(u.fullName || u.email || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="space-y-0.5">
                                <div className="font-bold text-xs text-zinc-900 flex items-center gap-1.5 flex-wrap">
                                  <span>{u.fullName}</span>
                                  {u.userName && (
                                    <span className="inline-flex items-center gap-0.5 font-mono text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200/80 px-1.5 py-0.5 rounded-md">
                                      <AtSign className="w-2.5 h-2.5 text-violet-500" />
                                      {u.userName}
                                    </span>
                                  )}
                                </div>
                                {u.businessName && (
                                  <div className="text-[11px] text-zinc-700 font-semibold flex items-center gap-1">
                                    <Building2 className="w-3 h-3 text-zinc-400 shrink-0" />
                                    <span>{u.businessName}</span>
                                  </div>
                                )}
                                {u.registrationNumber && (
                                  <div className="text-[10px] text-zinc-500 font-mono">
                                    NIF / RCCM : <span className="font-bold text-zinc-700">{u.registrationNumber}</span>
                                  </div>
                                )}
                                {u.representativeName && (
                                  <div className="text-[10px] text-zinc-400">
                                    Rép. Légal : {u.representativeName}
                                  </div>
                                )}
                                {u.nationalIdNumber && (
                                  <div className="text-[10px] text-zinc-500 font-mono">
                                    CNI / ID : <span className="font-semibold text-zinc-700">{u.nationalIdNumber}</span>
                                  </div>
                                )}
                                {isStaffUser && u.hireDate && (
                                  <div className="text-[10px] text-emerald-700 font-medium">
                                    Embauché le : {new Date(u.hireDate).toLocaleDateString('fr-FR')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Contact & Localisation */}
                          <td className="p-4">
                            <div className="space-y-1">
                              <div className="text-xs font-bold text-zinc-800 flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                                <span className="truncate max-w-[180px]">{u.email}</span>
                              </div>
                              <div className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                <span className="font-mono">{u.phoneNumber || 'Non renseigné'}</span>
                              </div>
                              {(u.city || u.address) && (
                                <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-violet-400 shrink-0" />
                                  <span className="truncate max-w-[180px]">{[u.city, u.address].filter(Boolean).join(', ')}</span>
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Date et Heure d'Inscription */}
                          <td className="p-4">
                            <div className="space-y-1">
                              {u.createdAt ? (
                                <>
                                  <div className="text-xs font-bold text-zinc-800 flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                                    <span>{new Date(u.createdAt).toLocaleDateString('fr-FR')}</span>
                                  </div>
                                  <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1 pl-5">
                                    <Clock className="w-2.5 h-2.5 text-zinc-400" />
                                    <span>{new Date(u.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                                  </div>
                                </>
                              ) : (
                                <span className="text-[10px] text-zinc-400">—</span>
                              )}
                            </div>
                          </td>

                          {/* Profil, Statut d'Activité & Email */}
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center gap-1">
                              {accountTypeBadge}
                              {/* Statut d'activité du compte */}
                              {u.isActive !== false ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /> Compte Actif
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-red-50 text-red-700 border border-red-200">
                                  <Ban className="w-2.5 h-2.5 text-red-600" /> Compte Suspendu
                                </span>
                              )}
                              {/* Validation Email */}
                              {u.emailConfirmed ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200">
                                  <CheckCircle2 className="w-2.5 h-2.5 text-sky-600" /> Email Validé
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  <Clock className="w-2.5 h-2.5 text-amber-600" /> Email En attente
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Groupe Actuel / Modification */}
                          <td className="p-4">
                            {canAssignGroup ? (
                              <div className="space-y-1.5">
                                <select
                                  value={currentSelectedGId}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedGroupForUser(prev => ({ ...prev, [u.id]: val }));
                                  }}
                                  disabled={userGroups.length === 0}
                                  className="bg-white border border-zinc-200 text-xs font-bold p-1.5 rounded-xl focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:outline-none w-full max-w-[160px]"
                                >
                                  {userGroups.length === 0 ? (
                                    <option value="">Aucun groupe ({isStaffUser ? 'Staff' : 'Client'})</option>
                                  ) : (
                                    userGroups.map((g: any) => {
                                      const gid = g.id || g.Id;
                                      const glab = g.label || g.Label;
                                      return (
                                        <option key={gid} value={gid}>
                                          {glab}
                                        </option>
                                      );
                                    })
                                  )}
                                </select>

                                {!u.groupId && (
                                  <div className="text-[10px] text-amber-700 font-bold flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                    En attente d'attribution
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-zinc-100 text-zinc-800 border border-zinc-200">
                                  <Shield className="w-3 h-3 text-violet-600" />
                                  {u.groupLabel || "Aucun groupe"}
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Expiration Compte */}
                          <td className="p-4">
                            {isUserAdmin ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-extrabold bg-purple-50 text-purple-700 border border-purple-200">
                                👑 Illimité (Admin)
                              </span>
                            ) : (
                              <div className="space-y-1">
                                {canAssignGroup ? (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                                    <input
                                      type="date"
                                      min={today}
                                      value={expVal}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedExpireAtForUser(prev => ({ ...prev, [u.id]: val }));
                                      }}
                                      className="bg-white border border-zinc-200 text-xs font-semibold px-2 py-1 rounded-lg focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 focus:outline-none w-[135px]"
                                      title="Définir ou prolonger la date d'expiration"
                                    />
                                  </div>
                                ) : null}
                                <div>
                                  {u.isExpired ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-extrabold bg-red-50 text-red-700 border border-red-200">
                                      <AlertCircle className="w-2.5 h-2.5 text-red-500" /> Expiré
                                    </span>
                                  ) : u.expireAt ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> Actif jusqu'au {new Date(u.expireAt).toLocaleDateString('fr-FR')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium text-zinc-400">
                                      <Clock className="w-2.5 h-2.5" /> Non définie
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Actions Granulaires */}
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1.5 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                              {/* Bouton : Activer / Suspendre le compte */}
                              {canActivateAccount && (
                                <button
                                  disabled={isUserAdmin || togglingStatusId === u.id}
                                  onClick={() => handleToggleUserStatus(u.id, u.isActive)}
                                  className={`p-2 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer flex items-center justify-center flex-shrink-0 border ${
                                    isUserAdmin ? 'opacity-30 cursor-not-allowed bg-zinc-100 border-zinc-200 text-zinc-400' :
                                    u.isActive !== false
                                      ? 'bg-white border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300'
                                      : 'bg-white border-emerald-300 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-400'
                                  }`}
                                  title={
                                    isUserAdmin
                                      ? "Action non applicable aux Administrateurs"
                                      : u.isActive !== false
                                        ? "Suspendre / Désactiver ce compte"
                                        : "Activer ce compte"
                                  }
                                >
                                  {togglingStatusId === u.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Power className="w-4 h-4" />
                                  )}
                                </button>
                              )}

                              {/* Bouton : Valider / Enregistrer Groupe & Expiration */}
                              {canAssignGroup && (
                                <button
                                  disabled={isAssigning || userGroups.length === 0}
                                  onClick={() => handleAssignGroup(u.id, u.accountType)}
                                  className="bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white p-2 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer flex items-center justify-center flex-shrink-0"
                                  title="Enregistrer le groupe et la date d'expiration"
                                >
                                  {isAssigning ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Check className="w-4 h-4" />
                                  )}
                                </button>
                              )}

                              {/* Bouton : Réinitialiser le mot de passe */}
                              {canResetPassword && (
                                <button
                                  onClick={() => setResetPasswordUser(u)}
                                  className="bg-white border border-amber-300 hover:bg-amber-50 text-amber-600 hover:text-amber-700 p-2 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer flex items-center justify-center flex-shrink-0"
                                  title="Réinitialiser le mot de passe et envoyer par e-mail"
                                >
                                  <KeyRound className="w-4 h-4" />
                                </button>
                              )}

                              {/* Bouton : Voir les consultations d'actes de l'abonné */}
                              <button
                                onClick={() => openUserConsultationsModal(u)}
                                className="bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-600 hover:text-indigo-800 p-2 rounded-xl shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer flex items-center justify-center flex-shrink-0"
                                title="Voir les actes juridiques consultés par cet abonné"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={usersPage} pageSize={usersPageSize} totalCount={usersTotalCount}
              onPageChange={setUsersPage} onPageSizeChange={setUsersPageSize}
            />
          </div>
        )}

        {/* 5. GROUPS & PERMISSIONS */}
        {activeSubTab === 'GROUPES' && canManageGroups && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 animate-fade-in relative">
            {/* Left sidebar: Groups list */}
            <div className="lg:col-span-3 bg-white border border-violet-100 rounded-2xl overflow-hidden shadow-lg h-fit sticky top-4">
              <div className="p-4 bg-gradient-to-br from-violet-700 to-purple-800 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-sm">Groupes disponibles</h3>
                  <button onClick={() => setShowCreateGroupModal(true)} className="bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[10px] text-violet-200 mt-1">Cliquez sur un groupe pour modifier ses permissions</p>
              </div>
              <div className="p-2 space-y-1">
                {Object.keys(groupLabels).map(gk => {
                  const isSelected = selectedGroup === gk;
                  const permCount = groupPerms[gk] ? Object.values(groupPerms[gk]).filter(Boolean).length : 0;
                  return (
                    <button
                      key={gk}
                      onClick={() => setSelectedGroup(gk)}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        isSelected ? 'bg-violet-700 text-white shadow-md' : 'hover:bg-violet-50 text-zinc-700'
                      }`}
                    >
                      <div className="font-bold text-xs">{groupLabels[gk]}</div>
                      <div className={`text-[10px] mt-0.5 ${isSelected ? 'text-violet-200' : 'text-zinc-400'}`}>{groupDescriptions[gk] || 'Description non disponible'}</div>
                      <div className={`text-[10px] mt-1 font-bold ${ isSelected ? 'text-violet-100' : 'text-violet-600'}`}>{permCount} permission(s) activée(s)</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right panel: Permissions for selected group */}
            <div className="lg:col-span-9 space-y-3">
              <div className="bg-white border border-violet-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                <div>
                  {editingGroupKey === selectedGroup ? (
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-base text-zinc-900">Permissions &gt;</h3>
                      <input 
                        type="text" 
                        value={tempGroupLabel}
                        onChange={(e) => setTempGroupLabel(e.target.value)}
                        className="font-bold text-base text-zinc-900 bg-violet-50 border border-violet-200 rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                        autoFocus
                      />
                      <button 
                        onClick={() => {
                          if (tempGroupLabel.trim()) {
                            setGroupLabels(prev => ({ ...prev, [selectedGroup]: tempGroupLabel.trim() }));
                          }
                          setEditingGroupKey(null);
                        }}
                        className="p-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-lg transition-colors"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <h3 className="font-bold text-base text-zinc-900 flex items-center gap-2">
                      Permissions &gt; {groupLabels[selectedGroup]}
                      <button 
                        onClick={() => {
                          setTempGroupLabel(groupLabels[selectedGroup]);
                          setEditingGroupKey(selectedGroup);
                        }}
                        className="text-zinc-400 hover:text-violet-600 transition-colors p-1"
                        title="Modifier le nom du groupe"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </h3>
                  )}
                  <p className="text-xs text-zinc-500 mt-1">Activez ou désactivez les droits pour ce groupe. Les modifications sont appliquées en temps réel.</p>
                </div>
                <button
                  onClick={() => { /* Optionnel: Recharger les perms depuis l'API */ }}
                  className="flex items-center gap-1.5 text-xs font-bold text-violet-700 border border-violet-200 bg-violet-50 hover:bg-violet-100 px-3 py-2 rounded-xl transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Recharger
                </button>
              </div>

              {/* Panneau de configuration des Quotas et de la Formule */}
              {selectedGroup && groupQuotas[selectedGroup] && (
                <div className="bg-white border border-indigo-100 rounded-2xl p-5 shadow-sm space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Quotas de Consultation, Téléchargement & Formule
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Définissez les plafonds de consommation (-1 = illimité) pour le groupe <strong>{groupLabels[selectedGroup]}</strong>.
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled={isSavingQuotas}
                      onClick={handleSaveGroupQuotas}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-sm transition-all flex items-center gap-2 cursor-pointer self-start sm:self-auto"
                    >
                      {isSavingQuotas ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span>Enregistrer les quotas</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                        Consultations Max <span className="text-slate-400 font-normal">(-1 = illimité)</span>
                      </label>
                      <input
                        type="number"
                        value={groupQuotas[selectedGroup].maxConsultations}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setGroupQuotas(prev => ({
                            ...prev,
                            [selectedGroup]: { ...prev[selectedGroup], maxConsultations: val }
                          }));
                        }}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                      <span className="text-[10px] text-slate-400">Actes distincts consultables</span>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                        Téléchargements / Jour <span className="text-slate-400 font-normal">(-1 = illimité)</span>
                      </label>
                      <input
                        type="number"
                        value={groupQuotas[selectedGroup].maxDailyDownloads}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setGroupQuotas(prev => ({
                            ...prev,
                            [selectedGroup]: { ...prev[selectedGroup], maxDailyDownloads: val }
                          }));
                        }}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                      <span className="text-[10px] text-slate-400">Plafond par 24 heures</span>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                        Téléchargements / Semaine <span className="text-slate-400 font-normal">(-1 = illimité)</span>
                      </label>
                      <input
                        type="number"
                        value={groupQuotas[selectedGroup].maxWeeklyDownloads}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setGroupQuotas(prev => ({
                            ...prev,
                            [selectedGroup]: { ...prev[selectedGroup], maxWeeklyDownloads: val }
                          }));
                        }}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                      <span className="text-[10px] text-slate-400">Plafond par semaine calendaire</span>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">
                        Téléchargements / Mois <span className="text-slate-400 font-normal">(-1 = illimité)</span>
                      </label>
                      <input
                        type="number"
                        value={groupQuotas[selectedGroup].maxMonthlyDownloads}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setGroupQuotas(prev => ({
                            ...prev,
                            [selectedGroup]: { ...prev[selectedGroup], maxMonthlyDownloads: val }
                          }));
                        }}
                        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                      <span className="text-[10px] text-slate-400">Plafond mensuel</span>
                    </div>
                  </div>

                  <div className="pt-2 flex items-center justify-between border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-700">Afficher dans le catalogue public des Formules</span>
                      <span className="text-[11px] text-slate-400">(visible dans le menu Formules des utilisateurs)</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setGroupQuotas(prev => ({
                          ...prev,
                          [selectedGroup]: { ...prev[selectedGroup], isFormulaVisible: !prev[selectedGroup].isFormulaVisible }
                        }));
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none cursor-pointer ${
                        groupQuotas[selectedGroup].isFormulaVisible ? 'bg-indigo-600' : 'bg-slate-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                        groupQuotas[selectedGroup].isFormulaVisible ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              )}

              {apiCategories.map(cat => (
                <div key={cat.key} className="bg-white border border-zinc-100 rounded-2xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setExpandedCategories(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                    className="w-full flex items-center justify-between p-4 hover:bg-violet-50/50 transition-colors"
                  >
                    <span className="font-bold text-xs uppercase tracking-widest text-violet-800">{cat.label}</span>
                    {expandedCategories[cat.key] ? <ChevronDown className="w-4 h-4 text-violet-400" /> : <ChevronRight className="w-4 h-4 text-violet-400" />}
                  </button>
                  {expandedCategories[cat.key] && (
                    <div className="divide-y divide-zinc-50">
                      {cat.perms.map((perm: any) => {
                        const isOn = groupPerms[selectedGroup]?.[perm.key] ?? false;
                        return (
                          <div key={perm.key} className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50/50 group">
                            <div className="flex-1 pr-4">
                              <div className="text-sm font-semibold text-zinc-800">{perm.label}</div>
                              <div className="text-[11px] text-zinc-400 mt-0.5">{perm.desc}</div>
                            </div>
                            <button
                              onClick={() => togglePerm(selectedGroup, perm.key)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                                isOn ? 'bg-violet-600' : 'bg-zinc-200'
                              }`}
                              title={isOn ? 'Désactiver' : 'Activer'}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                                isOn ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. SUIVI D'ACTIVITÉ : CONNEXIONS, CONSULTATIONS, TÉLÉCHARGEMENTS & RECHERCHES */}
        {activeSubTab === 'ACTIVITY' && canViewActivity && (
          <div className="space-y-4 animate-fade-in">
            {/* Sous-navigation Activité */}
            <div className="bg-white border border-zinc-200 rounded-2xl p-2 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex bg-zinc-100 p-1 rounded-xl gap-1 w-full sm:w-auto flex-wrap">
                {canViewLogins && (
                  <button
                    type="button"
                    onClick={() => setActivitySubTab('LOGINS')}
                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activitySubTab === 'LOGINS'
                        ? 'bg-white text-indigo-700 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                    <span>Historique des Connexions</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 font-mono">
                      {loginsTotalCount}
                    </span>
                  </button>
                )}

                {canViewConsultations && (
                  <button
                    type="button"
                    onClick={() => setActivitySubTab('CONSULTATIONS')}
                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activitySubTab === 'CONSULTATIONS'
                        ? 'bg-white text-violet-700 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <Eye className="w-4 h-4 text-violet-600" />
                    <span>Consultations d'Actes</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-violet-50 text-violet-700 font-mono">
                      {consultationsTotalCount}
                    </span>
                  </button>
                )}

                {canViewDownloads && (
                  <button
                    type="button"
                    onClick={() => setActivitySubTab('DOWNLOADS')}
                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activitySubTab === 'DOWNLOADS'
                        ? 'bg-white text-emerald-700 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <DownloadCloud className="w-4 h-4 text-emerald-600" />
                    <span>Téléchargements d'Actes</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 font-mono">
                      {downloadsTotalCount}
                    </span>
                  </button>
                )}

                {canViewSearches && (
                  <button
                    type="button"
                    onClick={() => setActivitySubTab('SEARCHES')}
                    className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activitySubTab === 'SEARCHES'
                        ? 'bg-white text-sky-700 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <Search className="w-4 h-4 text-sky-600" />
                    <span>Recherches Utilisateurs &amp; Invités</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-sky-50 text-sky-700 font-mono">
                      {searchesTotalCount}
                    </span>
                  </button>
                )}
              </div>

            </div>

            {/* VUE 1 : HISTORIQUE DES CONNEXIONS */}
            {activitySubTab === 'LOGINS' && (
              <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                {/* Search Bar */}
                <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setLoginsSearchTerm(loginsSearchInput);
                      setLoginsPage(1);
                    }}
                    className="flex items-center gap-2 w-full sm:max-w-md"
                  >
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Rechercher par nom, email, identifiant ou IP..."
                        value={loginsSearchInput}
                        onChange={(e) => setLoginsSearchInput(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-xs"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
                    >
                      Filtrer
                    </button>
                    {loginsSearchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setLoginsSearchInput('');
                          setLoginsSearchTerm('');
                          setLoginsPage(1);
                        }}
                        className="p-2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                        title="Effacer le filtre"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </form>

                  <button
                    type="button"
                    onClick={fetchLoginHistory}
                    disabled={loginsLoading}
                    className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs self-end sm:self-auto"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loginsLoading ? 'animate-spin text-indigo-600' : 'text-zinc-400'}`} />
                    <span>Actualiser</span>
                  </button>
                </div>

                {/* Table des connexions */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-200/80 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <th className="py-3 px-4">Utilisateur / Abonné</th>
                        <th className="py-3 px-4">Profil &amp; Groupe</th>
                        <th className="py-3 px-4">Date &amp; Heure de Connexion</th>
                        <th className="py-3 px-4">Adresse IP</th>
                        <th className="py-3 px-4">Navigateur &amp; Appareil</th>
                        <th className="py-3 px-4 text-center">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {loginsLoading ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-zinc-500">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
                            <span>Chargement de l'historique des connexions...</span>
                          </td>
                        </tr>
                      ) : loginHistory.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-zinc-500">
                            <Clock className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                            <p className="font-semibold text-zinc-700">Aucune session enregistrée</p>
                            <p className="text-[11px] text-zinc-400 mt-1">Aucune connexion ne correspond aux critères de recherche.</p>
                          </td>
                        </tr>
                      ) : (
                        loginHistory.map((session) => {
                          const humanDate = formatHumanDateTime(session.loginTime);

                          return (
                            <tr key={session.id} className="hover:bg-zinc-50/70 transition-colors">
                              <td className="py-3.5 px-4">
                                <div className="font-bold text-zinc-900">{session.fullName}</div>
                                <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                                  <span>{session.email}</span>
                                  {session.phoneNumber && (
                                    <>
                                      <span className="text-zinc-300">&bull;</span>
                                      <span className="font-mono text-zinc-600">{session.phoneNumber}</span>
                                    </>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4">
                                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                  session.accountType === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                                  session.accountType === 'BUSINESS' ? 'bg-blue-100 text-blue-800' :
                                  session.accountType === 'STAFF' ? 'bg-amber-100 text-amber-800' :
                                  'bg-emerald-100 text-emerald-800'
                                }`}>
                                  {session.accountType}
                                </span>
                                <div className="text-[11px] text-zinc-600 font-semibold mt-1">
                                  {session.groupName || 'Aucun groupe'}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap" title={humanDate.full}>
                                <div className="font-bold text-zinc-800 flex items-center gap-1.5">
                                  <span>{humanDate.label}</span>
                                  {humanDate.period === 'today' && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Connexion aujourd'hui"></span>
                                  )}
                                </div>
                                <div className="text-[11px] text-indigo-600 font-mono font-semibold flex items-center gap-1.5 mt-0.5">
                                  <span>{humanDate.timeWithSeconds}</span>
                                  {humanDate.relative && (
                                    <>
                                      <span className="text-zinc-300 font-normal">&bull;</span>
                                      <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                    </>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <span className="px-2 py-1 bg-zinc-100 border border-zinc-200 rounded-md font-mono text-[11px] text-zinc-700 font-bold">
                                  {session.ipAddress}
                                </span>
                              </td>

                              <td className="py-3.5 px-4">
                                <p className="text-[11px] text-zinc-600 max-w-xs truncate" title={session.userAgent}>
                                  {session.userAgent || 'Inconnu'}
                                </p>
                              </td>

                              <td className="py-3.5 px-4 text-center">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                  Connecté
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <PaginationBar
                  page={loginsPage}
                  pageSize={loginsPageSize}
                  totalCount={loginsTotalCount}
                  onPageChange={setLoginsPage}
                  onPageSizeChange={setLoginsPageSize}
                />
              </div>
            )}

            {/* VUE 2 : HISTORIQUE DES CONSULTATIONS D'ACTES */}
            {activitySubTab === 'CONSULTATIONS' && (
              <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                {/* Search Bar */}
                <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setConsultationsSearchTerm(consultationsSearchInput);
                      setConsultationsPage(1);
                    }}
                    className="flex items-center gap-2 w-full sm:max-w-md"
                  >
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Rechercher par titre, référence, abonné ou nature..."
                        value={consultationsSearchInput}
                        onChange={(e) => setConsultationsSearchInput(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 shadow-xs"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-3.5 py-2 bg-violet-700 hover:bg-violet-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
                    >
                      Filtrer
                    </button>
                    {consultationsSearchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setConsultationsSearchInput('');
                          setConsultationsSearchTerm('');
                          setConsultationsPage(1);
                        }}
                        className="p-2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                        title="Effacer le filtre"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </form>

                  <button
                    type="button"
                    onClick={fetchConsultationHistory}
                    disabled={consultationsLoading}
                    className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs self-end sm:self-auto"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${consultationsLoading ? 'animate-spin text-violet-600' : 'text-zinc-400'}`} />
                    <span>Actualiser</span>
                  </button>
                </div>

                {/* Table des consultations */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-200/80 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <th className="py-3 px-4">Abonné / Consultant</th>
                        <th className="py-3 px-4">Dernière Consultation</th>
                        <th className="py-3 px-4">Dernier Acte Juridique Consulté</th>
                        <th className="py-3 px-4 text-center">Actes Distincts</th>
                        <th className="py-3 px-4 text-center">Historique</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {consultationsLoading ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-zinc-500">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-violet-600" />
                            <span>Chargement des consultations d'actes...</span>
                          </td>
                        </tr>
                      ) : consultationHistory.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-zinc-500">
                            <Eye className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                            <p className="font-semibold text-zinc-700">Aucune consultation enregistrée</p>
                            <p className="text-[11px] text-zinc-400 mt-1">Les abonnés ayant consulté des actes s'afficheront ici par ordre de consultation récente.</p>
                          </td>
                        </tr>
                      ) : (
                        consultationHistory.map((item) => {
                          const humanDate = formatHumanDateTime(item.consultedAt);
                          const distinctCount = item.distinctDocumentsCount ?? 1;

                          return (
                            <tr key={item.accountId || item.id} className="hover:bg-zinc-50/70 transition-colors">
                              {/* Abonné */}
                              <td className="py-3.5 px-4">
                                <div className="font-bold text-zinc-900">{item.fullName}</div>
                                <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                                  <span>{item.email}</span>
                                  {item.phoneNumber && (
                                    <>
                                      <span className="text-zinc-300">&bull;</span>
                                      <span className="font-mono text-zinc-600">{item.phoneNumber}</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className={`inline-block px-2 py-0.2 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                    item.accountType === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                                    item.accountType === 'BUSINESS' ? 'bg-blue-100 text-blue-800' :
                                    item.accountType === 'STAFF' ? 'bg-amber-100 text-amber-800' :
                                    'bg-emerald-100 text-emerald-800'
                                  }`}>
                                    {item.accountType}
                                  </span>
                                  <span className="text-[10px] text-zinc-500 font-medium">
                                    {item.groupName || 'Aucun groupe'}
                                  </span>
                                </div>
                              </td>

                              {/* Date & Heure dernière consultation au format humain */}
                              <td className="py-3.5 px-4 whitespace-nowrap" title={humanDate.full}>
                                <div className="font-bold text-zinc-800 flex items-center gap-1.5">
                                  <span>{humanDate.label}</span>
                                  {humanDate.period === 'today' && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Consulté aujourd'hui"></span>
                                  )}
                                </div>
                                <div className="text-[11px] text-violet-600 font-mono font-semibold flex items-center gap-1.5 mt-0.5">
                                  <span>{humanDate.timeWithSeconds || humanDate.time}</span>
                                  {humanDate.relative && (
                                    <>
                                      <span className="text-zinc-300 font-normal">&bull;</span>
                                      <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                    </>
                                  )}
                                </div>
                              </td>

                              {/* Dernier acte juridique consulté */}
                              <td className="py-3.5 px-4 max-w-xs sm:max-w-sm">
                                <div className="font-bold text-zinc-900 line-clamp-2 leading-snug">
                                  {item.documentTitle}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {item.categoryName && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800">
                                      {item.categoryName}
                                    </span>
                                  )}
                                  {item.referenceNumber && (
                                    <span className="text-[10px] font-mono text-zinc-500 font-semibold">
                                      Réf : {item.referenceNumber}
                                    </span>
                                  )}
                                  {item.areaOfLaw && (
                                    <span className="text-[10px] text-zinc-400">
                                      &bull; {item.areaOfLaw}
                                    </span>
                                  )}
                                  {item.effectiveYear && (
                                    <span className="text-[10px] text-zinc-400">
                                      ({item.effectiveYear})
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Nombre total d'actes distincts déjà consultés */}
                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-violet-50 text-violet-700 border border-violet-200">
                                  <FileText className="w-3.5 h-3.5 text-violet-500" />
                                  <span>{distinctCount} acte{distinctCount > 1 ? 's' : ''}</span>
                                </span>
                                {item.maxConsultations !== undefined && (
                                  <div className="text-[10px] text-zinc-400 font-semibold mt-1">
                                    Quota : {distinctCount} / {item.maxConsultations > 0 ? `${item.maxConsultations}` : 'Illimité'}
                                  </div>
                                )}
                              </td>

                              {/* Bouton pour visualiser la liste complète dans le modal */}
                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => openUserConsultationsModal({
                                    id: item.accountId,
                                    fullName: item.fullName,
                                    email: item.email,
                                    groupLabel: item.groupName,
                                    distinctDocumentsCount: distinctCount,
                                    maxConsultations: item.maxConsultations
                                  })}
                                  className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 mx-auto cursor-pointer group"
                                  title="Visualiser la liste de tous les actes consultés par cet abonné"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>Voir les actes</span>
                                  <span className="px-1.5 py-0.2 rounded-full bg-violet-800 text-violet-100 text-[10px] font-mono font-bold group-hover:bg-violet-900">
                                    {distinctCount}
                                  </span>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <PaginationBar
                  page={consultationsPage}
                  pageSize={consultationsPageSize}
                  totalCount={consultationsTotalCount}
                  onPageChange={setConsultationsPage}
                  onPageSizeChange={setConsultationsPageSize}
                />
              </div>
            )}

            {/* VUE 3 : TÉLÉCHARGEMENTS D'ACTES */}
            {activitySubTab === 'DOWNLOADS' && (
              <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                {/* Search Bar */}
                <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setDownloadsSearchTerm(downloadsSearchInput);
                      setDownloadsPage(1);
                    }}
                    className="flex items-center gap-2 w-full sm:max-w-md"
                  >
                    <div className="relative flex-1">
                      <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Rechercher par titre, référence, abonné ou nature..."
                        value={downloadsSearchInput}
                        onChange={(e) => setDownloadsSearchInput(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 shadow-xs"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
                    >
                      Filtrer
                    </button>
                    {downloadsSearchTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setDownloadsSearchInput('');
                          setDownloadsSearchTerm('');
                          setDownloadsPage(1);
                        }}
                        className="p-2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                        title="Effacer le filtre"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </form>

                  <button
                    type="button"
                    onClick={fetchDownloadHistory}
                    disabled={downloadsLoading}
                    className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs self-end sm:self-auto"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${downloadsLoading ? 'animate-spin text-emerald-600' : 'text-zinc-400'}`} />
                    <span>Actualiser</span>
                  </button>
                </div>

                {/* Table des téléchargements */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-200/80 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <th className="py-3 px-4">Abonné / Téléchargeur</th>
                        <th className="py-3 px-4">Dernier Téléchargement</th>
                        <th className="py-3 px-4">Dernier Acte Juridique Téléchargé</th>
                        <th className="py-3 px-4 text-center">Actes Distincts</th>
                        <th className="py-3 px-4 text-center">Historique</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {downloadsLoading ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-zinc-500">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                            <span>Chargement des téléchargements d'actes...</span>
                          </td>
                        </tr>
                      ) : downloadHistory.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-zinc-500">
                            <DownloadCloud className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                            <p className="font-semibold text-zinc-700">Aucun téléchargement enregistré</p>
                            <p className="text-[11px] text-zinc-400 mt-1">Les abonnés ayant téléchargé des actes s'afficheront ici par ordre de téléchargement récent.</p>
                          </td>
                        </tr>
                      ) : (
                        downloadHistory.map((item) => {
                          const humanDate = formatHumanDateTime(item.downloadedAt);
                          const distinctCount = item.distinctDocumentsCount ?? 1;

                          return (
                            <tr key={item.accountId || item.id} className="hover:bg-zinc-50/70 transition-colors">
                              {/* Abonné */}
                              <td className="py-3.5 px-4">
                                <div className="font-bold text-zinc-900">{item.fullName}</div>
                                <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                                  <span>{item.email}</span>
                                  {item.phoneNumber && (
                                    <>
                                      <span className="text-zinc-300">&bull;</span>
                                      <span className="font-mono text-zinc-600">{item.phoneNumber}</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className={`inline-block px-2 py-0.2 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                    item.accountType === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                                    item.accountType === 'BUSINESS' ? 'bg-blue-100 text-blue-800' :
                                    item.accountType === 'STAFF' ? 'bg-amber-100 text-amber-800' :
                                    'bg-emerald-100 text-emerald-800'
                                  }`}>
                                    {item.accountType}
                                  </span>
                                  <span className="text-[10px] text-zinc-500 font-medium">
                                    {item.groupName || 'Aucun groupe'}
                                  </span>
                                </div>
                              </td>

                              {/* Date & Heure dernier téléchargement au format humain */}
                              <td className="py-3.5 px-4 whitespace-nowrap" title={humanDate.full}>
                                <div className="font-bold text-zinc-800 flex items-center gap-1.5">
                                  <span>{humanDate.label}</span>
                                  {humanDate.period === 'today' && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title="Téléchargé aujourd'hui"></span>
                                  )}
                                </div>
                                <div className="text-[11px] text-emerald-600 font-mono font-semibold flex items-center gap-1.5 mt-0.5">
                                  <span>{humanDate.timeWithSeconds || humanDate.time}</span>
                                  {humanDate.relative && (
                                    <>
                                      <span className="text-zinc-300 font-normal">&bull;</span>
                                      <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                    </>
                                  )}
                                </div>
                              </td>

                              {/* Dernier acte juridique téléchargé */}
                              <td className="py-3.5 px-4 max-w-xs sm:max-w-sm">
                                <div className="font-bold text-zinc-900 line-clamp-2 leading-snug">
                                  {item.documentTitle}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {item.categoryName && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                      {item.categoryName}
                                    </span>
                                  )}
                                  {item.referenceNumber && (
                                    <span className="text-[10px] font-mono text-zinc-500 font-semibold">
                                      Réf : {item.referenceNumber}
                                    </span>
                                  )}
                                  {item.areaOfLaw && (
                                    <span className="text-[10px] text-zinc-400">
                                      &bull; {item.areaOfLaw}
                                    </span>
                                  )}
                                  {item.effectiveYear && (
                                    <span className="text-[10px] text-zinc-400">
                                      ({item.effectiveYear})
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Nombre total d'actes distincts déjà téléchargés */}
                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <DownloadCloud className="w-3.5 h-3.5 text-emerald-500" />
                                  <span>{distinctCount} acte{distinctCount > 1 ? 's' : ''}</span>
                                </span>
                                {item.maxDownloads !== undefined && (
                                  <div className="text-[10px] text-zinc-400 font-semibold mt-1">
                                    Quota : {distinctCount} / {item.maxDownloads > 0 ? `${item.maxDownloads}` : 'Illimité'}
                                  </div>
                                )}
                              </td>

                              {/* Bouton pour visualiser la liste complète dans le modal */}
                              <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => openUserDownloadsModal({
                                    id: item.accountId,
                                    fullName: item.fullName,
                                    email: item.email,
                                    groupLabel: item.groupName,
                                    distinctDocumentsCount: distinctCount,
                                    maxDownloads: item.maxDownloads
                                  })}
                                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all shadow-xs flex items-center gap-1.5 mx-auto cursor-pointer group"
                                  title="Visualiser la liste de tous les actes téléchargés par cet abonné"
                                >
                                  <DownloadCloud className="w-3.5 h-3.5" />
                                  <span>Voir les actes</span>
                                  <span className="px-1.5 py-0.2 rounded-full bg-emerald-800 text-emerald-100 text-[10px] font-mono font-bold group-hover:bg-emerald-900">
                                    {distinctCount}
                                  </span>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <PaginationBar
                  page={downloadsPage}
                  pageSize={downloadsPageSize}
                  totalCount={downloadsTotalCount}
                  onPageChange={setDownloadsPage}
                  onPageSizeChange={setDownloadsPageSize}
                />
              </div>
            )}

            {/* VUE 4 : HISTORIQUE ET ANALYTIQUE DES RECHERCHES */}
            {activitySubTab === 'SEARCHES' && canViewSearches && (
              <div className="space-y-4">
                {/* 1. Cartes KPIs Analytiques */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 shrink-0">
                      <Search className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total Recherches</div>
                      <div className="text-xl font-black text-zinc-900 mt-0.5">
                        {searchAnalytics?.totalSearches ?? searchesTotalCount}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-medium truncate mt-0.5">
                        {searchAnalytics?.authenticatedSearchesCount ?? 0} connectés &bull; {searchAnalytics?.anonymousSearchesCount ?? 0} invités
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Taux de Satisfaction</div>
                      <div className="text-xl font-black text-emerald-600 mt-0.5">
                        {searchAnalytics?.successRate ?? 100}%
                      </div>
                      <div className="text-[10px] text-zinc-500 font-medium truncate mt-0.5">
                        {searchAnalytics?.successSearches ?? 0} avec au moins 1 résultat
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600 shrink-0">
                      <AlertCircle className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Recherches Infructueuses</div>
                      <div className="text-xl font-black text-rose-600 mt-0.5">
                        {searchAnalytics?.unfoundSearches ?? 0}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-medium truncate mt-0.5">
                        0 résultat trouvé (opportunités)
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Top Recherche Sans Résultat</div>
                      <div className="text-sm font-bold text-zinc-900 truncate mt-0.5" title={searchAnalytics?.topUnfoundQueries?.[0]?.query}>
                        {searchAnalytics?.topUnfoundQueries?.[0]?.query ? `"${searchAnalytics.topUnfoundQueries[0].query}"` : 'Aucune'}
                      </div>
                      <div className="text-[10px] text-purple-700 font-semibold mt-0.5">
                        {searchAnalytics?.topUnfoundQueries?.[0]?.count ? `Demandé ${searchAnalytics.topUnfoundQueries[0].count} fois` : 'Tous les termes trouvés'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Bandeau Top Requêtes Infructueuses (Opportunités documentaires) */}
                {searchAnalytics?.topUnfoundQueries && searchAnalytics.topUnfoundQueries.length > 0 && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 rounded-2xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                        <AlertCircle className="w-4 h-4" />
                      </span>
                      <div>
                        <div className="text-xs font-bold text-amber-950">Textes les plus recherchés mais non encore disponibles dans DOCARN :</div>
                        <div className="text-[11px] text-amber-800">Cliquez sur un mot-clé pour filtrer le journal.</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {searchAnalytics.topUnfoundQueries.map((t, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setSearchesSearchInput(t.query);
                            setSearchesSearchTerm(t.query);
                            setSearchesStatusFilter('NOT_FOUND_ONLY');
                            setSearchesPage(1);
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-semibold bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 transition-colors shadow-2xs cursor-pointer"
                        >
                          <span className="font-bold">"{t.query}"</span>
                          <span className="px-1.5 py-0.2 rounded-full bg-amber-200 text-amber-900 text-[10px] font-mono font-bold">
                            {t.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. Tableau & Filtres des recherches */}
                <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
                  {/* Search and Filters Bar */}
                  <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex flex-col gap-3">
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          setSearchesSearchTerm(searchesSearchInput);
                          setSearchesPage(1);
                        }}
                        className="flex items-center gap-2 flex-1 max-w-xl"
                      >
                        <div className="relative flex-1">
                          <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            placeholder="Rechercher par terme, utilisateur, token invité, IP..."
                            value={searchesSearchInput}
                            onChange={(e) => setSearchesSearchInput(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 shadow-xs"
                          />
                        </div>
                        <button
                          type="submit"
                          className="px-3.5 py-2 bg-sky-700 hover:bg-sky-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
                        >
                          Filtrer
                        </button>
                        {searchesSearchTerm && (
                          <button
                            type="button"
                            onClick={() => {
                              setSearchesSearchInput('');
                              setSearchesSearchTerm('');
                              setSearchesPage(1);
                            }}
                            className="p-2 text-zinc-400 hover:text-zinc-600 cursor-pointer"
                            title="Effacer le filtre texte"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </form>

                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Filtre Statut */}
                        <select
                          value={searchesStatusFilter}
                          onChange={(e) => {
                            setSearchesStatusFilter(e.target.value as any);
                            setSearchesPage(1);
                          }}
                          className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:border-sky-500 cursor-pointer shadow-xs"
                        >
                          <option value="ALL">Tous les statuts</option>
                          <option value="FOUND_ONLY">Trouvés (&gt; 0)</option>
                          <option value="NOT_FOUND_ONLY">Introuvables (0 résultat)</option>
                        </select>

                        {/* Filtre Utilisateur / Invité */}
                        <select
                          value={searchesUserFilter}
                          onChange={(e) => {
                            setSearchesUserFilter(e.target.value as any);
                            setSearchesPage(1);
                          }}
                          className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:border-sky-500 cursor-pointer shadow-xs"
                        >
                          <option value="ALL">Tous les visiteurs</option>
                          <option value="AUTHENTICATED_ONLY">Abonnés connectés</option>
                          <option value="ANONYMOUS_ONLY">Invités anonymes</option>
                        </select>

                        {/* Filtre Origine */}
                        <select
                          value={searchesOriginFilter}
                          onChange={(e) => {
                            setSearchesOriginFilter(e.target.value as any);
                            setSearchesPage(1);
                          }}
                          className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-semibold text-zinc-700 focus:outline-none focus:border-sky-500 cursor-pointer shadow-xs"
                        >
                          <option value="ALL">Toutes origines</option>
                          <option value="GLOBAL_SEARCH">Recherche Globale</option>
                          <option value="AUTOCOMPLETE_CLICK">Autocomplétion</option>
                        </select>

                        <button
                          type="button"
                          onClick={fetchSearchHistory}
                          disabled={searchesLoading}
                          className="px-3 py-2 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${searchesLoading ? 'animate-spin text-sky-600' : 'text-zinc-400'}`} />
                          <span>Actualiser</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Table des recherches */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200/80 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                          <th className="py-3 px-4">Visiteur / Utilisateur</th>
                          <th className="py-3 px-4">Terme Recherché &amp; Origine</th>
                          <th className="py-3 px-4">Filtres Appliqués</th>
                          <th className="py-3 px-4 text-center">Résultats &amp; Latence</th>
                          <th className="py-3 px-4">Date &amp; Heure</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {searchesLoading ? (
                          <tr>
                            <td colSpan={5} className="py-12 text-center text-zinc-500">
                              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-sky-600" />
                              <span>Chargement des recherches...</span>
                            </td>
                          </tr>
                        ) : searchHistory.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-12 text-center text-zinc-500">
                              <Search className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                              <p className="font-semibold text-zinc-700">Aucune recherche trouvée</p>
                              <p className="text-[11px] text-zinc-400 mt-1">Toutes les requêtes de recherche saisies par les utilisateurs ou invités apparaîtront ici.</p>
                            </td>
                          </tr>
                        ) : (
                          searchHistory.map((item) => {
                            const humanDate = formatHumanDateTime(item.executedAt);

                            let parsedFilters: Record<string, any> | null = null;
                            if (item.filtersApplied) {
                              try {
                                parsedFilters = JSON.parse(item.filtersApplied);
                              } catch {
                                parsedFilters = null;
                              }
                            }

                            return (
                              <tr key={item.id} className="hover:bg-zinc-50/70 transition-colors">
                                {/* Visiteur */}
                                <td className="py-3.5 px-4">
                                  {item.isAuthenticated ? (
                                    <>
                                      <div className="font-bold text-zinc-900">{item.userFullName || item.userEmail}</div>
                                      <div className="text-[11px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                                        <span>{item.userEmail}</span>
                                      </div>
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <span className={`inline-block px-2 py-0.2 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                          item.accountType === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                                          item.accountType === 'BUSINESS' ? 'bg-blue-100 text-blue-800' :
                                          item.accountType === 'STAFF' ? 'bg-amber-100 text-amber-800' :
                                          'bg-emerald-100 text-emerald-800'
                                        }`}>
                                          {item.accountType || 'ABONNÉ'}
                                        </span>
                                        <span className="text-[10px] text-zinc-500 font-medium">
                                          {item.groupName || 'Sans groupe'}
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="font-bold text-zinc-800 flex items-center gap-1.5">
                                        <span>Invité Anonyme</span>
                                        <span className="px-1.5 py-0.2 rounded-md bg-zinc-200 text-zinc-700 text-[9px] font-bold uppercase">
                                          Invité
                                        </span>
                                      </div>
                                      <div className="text-[10px] font-mono text-zinc-400 truncate max-w-[200px] mt-0.5" title={item.visitorToken || ''}>
                                        Token : {item.visitorToken || 'vis_anonyme'}
                                      </div>
                                    </>
                                  )}
                                  <div className="text-[10px] text-zinc-400 font-mono mt-1 flex items-center gap-1">
                                    <span>IP : {item.ipAddress || '127.0.0.1'}</span>
                                    <span>&bull;</span>
                                    <span>{item.deviceType || 'Ordinateur'}</span>
                                  </div>
                                </td>

                                {/* Terme recherché & Origine */}
                                <td className="py-3.5 px-4 max-w-xs">
                                  <div className="font-black text-sm text-zinc-900">
                                    "{item.query}"
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                      item.searchOrigin === 'AUTOCOMPLETE_CLICK'
                                        ? 'bg-violet-100 text-violet-800'
                                        : 'bg-sky-100 text-sky-800'
                                    }`}>
                                      {item.searchOrigin === 'AUTOCOMPLETE_CLICK' ? 'Clic Autocomplétion' : 'Recherche Globale'}
                                    </span>
                                    {item.page > 1 && (
                                      <span className="text-[10px] text-zinc-400 font-medium">
                                        (Page {item.page})
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* Filtres appliqués */}
                                <td className="py-3.5 px-4 max-w-xs">
                                  {parsedFilters && Object.keys(parsedFilters).length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries(parsedFilters).map(([k, v]) => (
                                        <span key={k} className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-zinc-100 text-zinc-700 border border-zinc-200/60">
                                          <strong className="font-bold text-zinc-900">{k}:</strong> {String(v)}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-[11px] text-zinc-400 italic">Aucun filtre</span>
                                  )}
                                </td>

                                {/* Résultats trouvés & Latence */}
                                <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                  {item.hasResults ? (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                      <span>{item.totalResults} résultat{item.totalResults > 1 ? 's' : ''}</span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                      <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
                                      <span>0 résultat (Introuvable)</span>
                                    </span>
                                  )}
                                  <div className="text-[10px] font-mono text-zinc-400 mt-1">
                                    {item.executionTimeMs} ms
                                  </div>
                                </td>

                                {/* Date & Heure au format humain */}
                                <td className="py-3.5 px-4 whitespace-nowrap" title={humanDate.full}>
                                  <div className="font-bold text-zinc-800 flex items-center gap-1.5">
                                    <span>{humanDate.label}</span>
                                    {humanDate.period === 'today' && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" title="Aujourd'hui"></span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-sky-600 font-mono font-semibold flex items-center gap-1.5 mt-0.5">
                                    <span>{humanDate.timeWithSeconds || humanDate.time}</span>
                                    {humanDate.relative && (
                                      <>
                                        <span className="text-zinc-300 font-normal">&bull;</span>
                                        <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <PaginationBar
                    page={searchesPage}
                    pageSize={searchesPageSize}
                    totalCount={searchesTotalCount}
                    onPageChange={setSearchesPage}
                    onPageSizeChange={setSearchesPageSize}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* 6. NUMÉRISATION & STRUCTURATION OCR HAUTE PRÉCISION */}
        {activeSubTab === 'OCR' && canManageOcr && (
          <AdminOcrTab
            openPdfPreview={(doc) => openDocumentPreview(doc, false)}
            categoryOptions={categoryOptions}
          />
        )}
      </div>

      {/* MODAL : FICHE CONSULTATIONS D'UN ABONNÉ SPÉCIFIQUE */}
      {selectedUserConsultation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedUserConsultation(null)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden animate-fade-in flex flex-col border border-zinc-100">
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-slate-900 via-indigo-950 to-violet-950 text-white flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 text-violet-300 text-[10px] font-bold uppercase tracking-wider mb-2">
                  <Eye className="w-3 h-3 text-violet-400" /> Historique de Consultation de l'Abonné
                </div>
                <h3 className="text-xl font-bold text-white">{selectedUserConsultation.fullName}</h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  {selectedUserConsultation.email} &bull; Formule / Groupe : <span className="font-bold text-amber-300">{selectedUserConsultation.groupName}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedUserConsultation(null)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quota Summary Card */}
            <div className="p-5 bg-zinc-50 border-b border-zinc-200/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="w-full sm:w-auto">
                <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Quota Consommé</div>
                <div className="text-2xl font-black text-zinc-900 mt-0.5">
                  {selectedUserConsultation.totalConsulted} / {selectedUserConsultation.maxConsultations < 0 ? 'Illimité' : `${selectedUserConsultation.maxConsultations} actes`}
                </div>
              </div>

              {selectedUserConsultation.maxConsultations > 0 && (
                <div className="w-full sm:w-64">
                  <div className="flex justify-between text-[11px] font-bold text-zinc-600 mb-1">
                    <span>Progression du quota</span>
                    <span>{Math.min(100, Math.round((selectedUserConsultation.totalConsulted / selectedUserConsultation.maxConsultations) * 100))}%</span>
                  </div>
                  <div className="w-full bg-zinc-200 h-2.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        (selectedUserConsultation.totalConsulted / selectedUserConsultation.maxConsultations) >= 1
                          ? 'bg-red-500'
                          : (selectedUserConsultation.totalConsulted / selectedUserConsultation.maxConsultations) >= 0.7
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                      }`}
                      style={{
                        width: `${Math.min(100, Math.round((selectedUserConsultation.totalConsulted / selectedUserConsultation.maxConsultations) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Consulted Acts Table */}
            <div className="overflow-y-auto flex-1 p-5">
              {userConsultationLoading ? (
                <div className="py-12 text-center text-zinc-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
                  <span>Chargement des actes consultés...</span>
                </div>
              ) : selectedUserConsultation.consultations.length === 0 ? (
                <div className="py-12 text-center text-zinc-500">
                  <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="font-semibold text-zinc-700">Aucun acte consulté</p>
                  <p className="text-xs text-zinc-400 mt-1">Cet abonné n'a pas encore ouvert d'acte juridique sur la plateforme.</p>
                </div>
              ) : (() => {
                const totalItems = selectedUserConsultation.totalConsulted || selectedUserConsultation.consultations.length || 0;
                const totalPages = Math.ceil(totalItems / modalConsultationsPageSize) || 1;
                const currentPage = modalConsultationsPage;
                const startIndex = (currentPage - 1) * modalConsultationsPageSize;

                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
                        Actes juridiques distincts consultés ({totalItems})
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span>Afficher</span>
                        <select
                          value={modalConsultationsPageSize}
                          disabled={userConsultationLoading}
                          onChange={(e) => {
                            const newSize = Number(e.target.value);
                            setModalConsultationsPageSize(newSize);
                            setModalConsultationsPage(1);
                            fetchUserConsultationsPage(selectedUserConsultation.accountId, 1, newSize);
                          }}
                          className="px-2 py-0.5 bg-white border border-zinc-200 rounded-md text-xs font-bold text-zinc-700 focus:outline-none focus:border-violet-500 cursor-pointer disabled:opacity-50"
                        >
                          <option value={5}>5 / page</option>
                          <option value={10}>10 / page</option>
                          <option value={20}>20 / page</option>
                        </select>
                      </div>
                    </div>

                    {userConsultationLoading ? (
                      <div className="py-12 text-center text-zinc-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-violet-600" />
                        <span className="text-xs font-semibold">Chargement des actes (page {currentPage})...</span>
                      </div>
                    ) : selectedUserConsultation.consultations.length === 0 ? (
                      <div className="py-12 text-center text-zinc-500">
                        <FileText className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                        <p className="font-semibold text-zinc-700">Aucun acte pour cette page</p>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {selectedUserConsultation.consultations.map((item, idx) => {
                          const humanDate = formatHumanDateTime(item.consultedAt);

                          return (
                            <div
                              key={item.id || idx}
                              className="bg-white border border-zinc-200 rounded-xl p-3.5 hover:border-violet-300 hover:shadow-xs transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {item.categoryName && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800">
                                      {item.categoryName}
                                    </span>
                                  )}
                                  {item.referenceNumber && (
                                    <span className="text-[10px] font-mono text-zinc-500 font-semibold">
                                      Réf : {item.referenceNumber}
                                    </span>
                                  )}
                                </div>
                                <h4 className="text-xs font-bold text-zinc-900 leading-snug">
                                  {item.documentTitle}
                                </h4>
                                <div className="text-[10px] text-zinc-400">
                                  {item.areaOfLaw || 'Droit Général'} {item.effectiveYear ? `• Année ${item.effectiveYear}` : ''}
                                </div>
                              </div>

                              <div className="text-right shrink-0" title={humanDate.full}>
                                <div className="text-[10px] font-bold text-zinc-400 uppercase">Consulté</div>
                                <div className="text-xs font-bold text-zinc-800">{humanDate.label}</div>
                                <div className="text-[10px] font-mono font-semibold text-violet-600 flex items-center justify-end gap-1 mt-0.5">
                                  <span>{humanDate.time}</span>
                                  {humanDate.relative && (
                                    <>
                                      <span className="text-zinc-300 font-normal">&bull;</span>
                                      <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination Bar interne au modal - Connectée au Backend */}
                    {totalPages > 1 && (
                      <div className="pt-3 mt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500 flex-wrap gap-2">
                        <span className="text-[11px] font-medium text-zinc-500">
                          Affichage de {startIndex + 1} à {Math.min(startIndex + modalConsultationsPageSize, totalItems)} sur {totalItems} acte{totalItems > 1 ? 's' : ''}
                        </span>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={currentPage <= 1 || userConsultationLoading}
                            onClick={() => {
                              const newPage = Math.max(1, currentPage - 1);
                              setModalConsultationsPage(newPage);
                              fetchUserConsultationsPage(selectedUserConsultation.accountId, newPage, modalConsultationsPageSize);
                            }}
                            className="px-2.5 py-1 bg-white border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-xs font-bold text-zinc-700 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            <span>Précédent</span>
                          </button>

                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                              if (totalPages > 7 && Math.abs(pg - currentPage) > 2 && pg !== 1 && pg !== totalPages) {
                                if (pg === 2 || pg === totalPages - 1) {
                                  return <span key={pg} className="px-1 text-zinc-400">...</span>;
                                }
                                return null;
                              }
                              return (
                                <button
                                  key={pg}
                                  type="button"
                                  disabled={userConsultationLoading}
                                  onClick={() => {
                                    setModalConsultationsPage(pg);
                                    fetchUserConsultationsPage(selectedUserConsultation.accountId, pg, modalConsultationsPageSize);
                                  }}
                                  className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                    pg === currentPage
                                      ? 'bg-violet-600 text-white shadow-xs'
                                      : 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                                  }`}
                                >
                                  {pg}
                                </button>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            disabled={currentPage >= totalPages || userConsultationLoading}
                            onClick={() => {
                              const newPage = Math.min(totalPages, currentPage + 1);
                              setModalConsultationsPage(newPage);
                              fetchUserConsultationsPage(selectedUserConsultation.accountId, newPage, modalConsultationsPageSize);
                            }}
                            className="px-2.5 py-1 bg-white border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-xs font-bold text-zinc-700 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <span>Suivant</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedUserConsultation(null)}
                className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-900 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL : FICHE TÉLÉCHARGEMENTS D'UN ABONNÉ SPÉCIFIQUE */}
      {selectedUserDownload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedUserDownload(null)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden animate-fade-in flex flex-col border border-zinc-100">
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-slate-900 via-teal-950 to-emerald-950 text-white flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 text-emerald-300 text-[10px] font-bold uppercase tracking-wider mb-2">
                  <DownloadCloud className="w-3 h-3 text-emerald-400" /> Historique de Téléchargement de l'Abonné
                </div>
                <h3 className="text-xl font-bold text-white">{selectedUserDownload.fullName}</h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  {selectedUserDownload.email} &bull; Formule / Groupe : <span className="font-bold text-amber-300">{selectedUserDownload.groupName}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedUserDownload(null)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quota Summary Card */}
            <div className="p-5 bg-zinc-50 border-b border-zinc-200/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="w-full sm:w-auto">
                <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Quota Consommé</div>
                <div className="text-2xl font-black text-zinc-900 mt-0.5">
                  {selectedUserDownload.totalDownloaded} / {selectedUserDownload.maxDownloads < 0 ? 'Illimité' : `${selectedUserDownload.maxDownloads} actes`}
                </div>
              </div>

              {selectedUserDownload.maxDownloads > 0 && (
                <div className="w-full sm:w-64">
                  <div className="flex justify-between text-[11px] font-bold text-zinc-600 mb-1">
                    <span>Progression du quota</span>
                    <span>{Math.min(100, Math.round((selectedUserDownload.totalDownloaded / selectedUserDownload.maxDownloads) * 100))}%</span>
                  </div>
                  <div className="w-full bg-zinc-200 h-2.5 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        (selectedUserDownload.totalDownloaded / selectedUserDownload.maxDownloads) >= 1
                          ? 'bg-red-500'
                          : (selectedUserDownload.totalDownloaded / selectedUserDownload.maxDownloads) >= 0.7
                            ? 'bg-amber-500'
                            : 'bg-emerald-500'
                      }`}
                      style={{
                        width: `${Math.min(100, Math.round((selectedUserDownload.totalDownloaded / selectedUserDownload.maxDownloads) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Downloaded Acts Table */}
            <div className="overflow-y-auto flex-1 p-5">
              {userDownloadLoading ? (
                <div className="py-12 text-center text-zinc-500">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                  <span>Chargement des actes téléchargés...</span>
                </div>
              ) : selectedUserDownload.downloads.length === 0 ? (
                <div className="py-12 text-center text-zinc-500">
                  <DownloadCloud className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="font-semibold text-zinc-700">Aucun acte téléchargé</p>
                  <p className="text-xs text-zinc-400 mt-1">Cet abonné n'a pas encore téléchargé d'acte juridique sur la plateforme.</p>
                </div>
              ) : (() => {
                const totalItems = selectedUserDownload.totalDownloaded || selectedUserDownload.downloads.length || 0;
                const totalPages = Math.ceil(totalItems / modalDownloadsPageSize) || 1;
                const currentPage = modalDownloadsPage;
                const startIndex = (currentPage - 1) * modalDownloadsPageSize;

                return (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
                        Actes juridiques distincts téléchargés ({totalItems})
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span>Afficher</span>
                        <select
                          value={modalDownloadsPageSize}
                          disabled={userDownloadLoading}
                          onChange={(e) => {
                            const newSize = Number(e.target.value);
                            setModalDownloadsPageSize(newSize);
                            setModalDownloadsPage(1);
                            fetchUserDownloadsPage(selectedUserDownload.accountId, 1, newSize);
                          }}
                          className="px-2 py-0.5 bg-white border border-zinc-200 rounded-md text-xs font-bold text-zinc-700 focus:outline-none focus:border-emerald-500 cursor-pointer disabled:opacity-50"
                        >
                          <option value={5}>5 / page</option>
                          <option value={10}>10 / page</option>
                          <option value={20}>20 / page</option>
                        </select>
                      </div>
                    </div>

                    {userDownloadLoading ? (
                      <div className="py-12 text-center text-zinc-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-emerald-600" />
                        <span className="text-xs font-semibold">Chargement des actes (page {currentPage})...</span>
                      </div>
                    ) : selectedUserDownload.downloads.length === 0 ? (
                      <div className="py-12 text-center text-zinc-500">
                        <DownloadCloud className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                        <p className="font-semibold text-zinc-700">Aucun acte pour cette page</p>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        {selectedUserDownload.downloads.map((item, idx) => {
                          const humanDate = formatHumanDateTime(item.downloadedAt);

                          return (
                            <div
                              key={item.id || idx}
                              className="bg-white border border-zinc-200 rounded-xl p-3.5 hover:border-emerald-300 hover:shadow-xs transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {item.categoryName && (
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                      {item.categoryName}
                                    </span>
                                  )}
                                  {item.referenceNumber && (
                                    <span className="text-[10px] font-mono text-zinc-500 font-semibold">
                                      Réf : {item.referenceNumber}
                                    </span>
                                  )}
                                </div>
                                <h4 className="text-xs font-bold text-zinc-900 leading-snug">
                                  {item.documentTitle}
                                </h4>
                                <div className="text-[10px] text-zinc-400">
                                  {item.areaOfLaw || 'Droit Général'} {item.effectiveYear ? `• Année ${item.effectiveYear}` : ''}
                                </div>
                              </div>

                              <div className="text-right shrink-0" title={humanDate.full}>
                                <div className="text-[10px] font-bold text-zinc-400 uppercase">Téléchargé</div>
                                <div className="text-xs font-bold text-zinc-800">{humanDate.label}</div>
                                <div className="text-[10px] font-mono font-semibold text-emerald-600 flex items-center justify-end gap-1 mt-0.5">
                                  <span>{humanDate.time}</span>
                                  {humanDate.relative && (
                                    <>
                                      <span className="text-zinc-300 font-normal">&bull;</span>
                                      <span className="text-zinc-500 font-sans font-medium">{humanDate.relative}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination Bar interne au modal - Connectée au Backend */}
                    {totalPages > 1 && (
                      <div className="pt-3 mt-4 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500 flex-wrap gap-2">
                        <span className="text-[11px] font-medium text-zinc-500">
                          Affichage de {startIndex + 1} à {Math.min(startIndex + modalDownloadsPageSize, totalItems)} sur {totalItems} acte{totalItems > 1 ? 's' : ''}
                        </span>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={currentPage <= 1 || userDownloadLoading}
                            onClick={() => {
                              const newPage = Math.max(1, currentPage - 1);
                              setModalDownloadsPage(newPage);
                              fetchUserDownloadsPage(selectedUserDownload.accountId, newPage, modalDownloadsPageSize);
                            }}
                            className="px-2.5 py-1 bg-white border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-xs font-bold text-zinc-700 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            <span>Précédent</span>
                          </button>

                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                              if (totalPages > 7 && Math.abs(pg - currentPage) > 2 && pg !== 1 && pg !== totalPages) {
                                if (pg === 2 || pg === totalPages - 1) {
                                  return <span key={pg} className="px-1 text-zinc-400">...</span>;
                                }
                                return null;
                              }
                              return (
                                <button
                                  key={pg}
                                  type="button"
                                  disabled={userDownloadLoading}
                                  onClick={() => {
                                    setModalDownloadsPage(pg);
                                    fetchUserDownloadsPage(selectedUserDownload.accountId, pg, modalDownloadsPageSize);
                                  }}
                                  className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                    pg === currentPage
                                      ? 'bg-emerald-600 text-white shadow-xs'
                                      : 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                                  }`}
                                >
                                  {pg}
                                </button>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            disabled={currentPage >= totalPages || userDownloadLoading}
                            onClick={() => {
                              const newPage = Math.min(totalPages, currentPage + 1);
                              setModalDownloadsPage(newPage);
                              fetchUserDownloadsPage(selectedUserDownload.accountId, newPage, modalDownloadsPageSize);
                            }}
                            className="px-2.5 py-1 bg-white border border-zinc-200 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-xs font-bold text-zinc-700 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <span>Suivant</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-200 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedUserDownload(null)}
                className="px-5 py-2.5 bg-zinc-800 hover:bg-zinc-900 text-white font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. FULL EDIT MODAL (Harmonisé avec le modèle Brouillon) */}
      {editingFullDocId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeEditModal}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fade-in flex flex-col">
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-zinc-100 p-6 flex items-center justify-between z-10">
              <div>
                <h3 className="font-display text-xl font-bold text-zinc-900 flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-emerald-600" /> Modifier l'Acte Officiel
                </h3>
                <p className="text-xs text-zinc-500 mt-1">Mettez à jour les références juridiques et raccordez si besoin le document PDF officiel.</p>
              </div>
              <button onClick={closeEditModal} className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-500 rounded-full transition-colors cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 md:p-8 space-y-5 flex-1 overflow-y-auto">
              {msg && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 text-emerald-800 border border-emerald-200/60 rounded-2xl font-semibold shadow-sm">
                  <div className="bg-emerald-100 p-1.5 rounded-full"><Check className="w-4 h-4 text-emerald-600" /></div>
                  {msg}
                </div>
              )}

              <form id="editDocForm" onSubmit={handleEditSubmit} className="space-y-5">
                {/* 1. SÉLECTION DU TYPE DE MODIFICATION TOUT EN HAUT */}
                <div className="p-4 bg-zinc-50 border border-zinc-200/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                  <div>
                    <span className="text-xs font-bold text-zinc-900">Type de modification</span>
                    <p className="text-[11px] text-zinc-500 mt-0.5">Indiquez si vous modifiez uniquement les références ou également le fichier PDF.</p>
                  </div>
                  <div className="flex items-center gap-4 bg-white p-1.5 px-3 rounded-xl border border-zinc-200 shrink-0">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-700 hover:text-zinc-900">
                      <input
                        type="radio"
                        name="fullDocEditScope"
                        checked={!editDocWithFile}
                        onChange={() => { setEditDocWithFile(false); setPdfFile(null); }}
                        className="text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>Références seulement</span>
                    </label>
                    <div className="w-px h-4 bg-zinc-200"></div>
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-700 hover:text-zinc-900">
                      <input
                        type="radio"
                        name="fullDocEditScope"
                        checked={editDocWithFile}
                        onChange={() => setEditDocWithFile(true)}
                        className="text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <span>Avec le document (PDF)</span>
                    </label>
                  </div>
                </div>

                {/* 2. ZONE DE SÉLECTION DU DOCUMENT PDF (BIEN VISIBLE TOUT EN HAUT SI ACTIVÉ) */}
                {editDocWithFile && (
                  <div className={`p-4 rounded-2xl border transition-all animate-fade-in ${
                    editingDoc?.isDeployedToMinio 
                      ? 'bg-emerald-50/60 border-emerald-200' 
                      : 'bg-amber-50/70 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                        editingDoc?.isDeployedToMinio ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {editingDoc?.isDeployedToMinio ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <h4 className="text-xs font-bold text-zinc-900">
                            {editingDoc?.isDeployedToMinio ? 'Document PDF actuellement associé dans la bibliothèque' : 'Aucun fichier PDF rattaché'}
                          </h4>
                          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                            editingDoc?.isDeployedToMinio ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {editingDoc?.isDeployedToMinio ? 'Document disponible' : 'Fichier manquant'}
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 leading-relaxed">
                          {editingDoc?.isDeployedToMinio 
                            ? 'Un document PDF officiel est déjà hébergé. Sélectionnez ci-dessous le nouveau fichier pour le remplacer.'
                            : 'Cet acte n\'a pas encore de document PDF rattaché. Veuillez sélectionner le fichier PDF officiel ci-dessous.'}
                        </p>

                        <div className="mt-3 pt-3 border-t border-zinc-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-zinc-200 hover:border-emerald-500 rounded-xl text-xs font-bold text-zinc-700 hover:text-emerald-700 shadow-sm cursor-pointer transition-colors">
                            <Upload className="w-3.5 h-3.5" />
                            <span>{pdfFile ? 'Changer de fichier...' : (editingDoc?.isDeployedToMinio ? 'Remplacer le document PDF' : 'Sélectionner le document PDF')}</span>
                            <input
                              type="file"
                              accept="application/pdf,.pdf"
                              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                              className="hidden"
                            />
                          </label>

                          {pdfFile ? (
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-emerald-200 text-xs font-semibold text-emerald-900 shadow-sm">
                              <FileText className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              <span className="truncate max-w-[200px]">{pdfFile.name}</span>
                              <span className="text-[10px] text-zinc-400">({(pdfFile.size / 1024).toFixed(0)} Ko)</span>
                              <button
                                type="button"
                                onClick={() => setPdfFile(null)}
                                className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-red-500 transition-colors ml-1 cursor-pointer"
                                title="Retirer ce fichier"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[11px] font-semibold text-amber-700 bg-amber-100/70 px-2.5 py-1 rounded-lg">
                              ⚠️ Fichier requis : veuillez sélectionner un PDF
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. INTITULÉ OFFICIEL (TITRE) DANS TEXTAREA */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Intitulé Officiel (Titre) *</label>
                  <textarea
                    required
                    rows={3}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-xs font-semibold leading-relaxed"
                    placeholder="Intitulé officiel complet du texte juridique..."
                  />
                </div>

                {/* 4. RÉFÉRENCE, CATÉGORIE / NATURE (SELECT BD) & ANNÉE */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Référence Légale *</label>
                    <input
                      required
                      type="text"
                      value={refNum}
                      onChange={(e) => setRefNum(e.target.value)}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-xs font-semibold font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Catégorie / Nature <span className="text-red-500">*</span></label>
                      <span className="text-[9px] text-zinc-400 font-medium">Sélection ou nouvelle nature</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        list="editDocCategoryList"
                        value={nature}
                        onChange={(e) => setNature(e.target.value)}
                        placeholder="Sélectionner ou saisir une nature..."
                        className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold bg-white"
                      />
                      <datalist id="editDocCategoryList">
                        {categoryOptions.map(c => (
                          <option key={c.value} value={c.label || c.value} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Année d'application *</label>
                    <input
                      type="number"
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                    />
                  </div>
                </div>

                {/* 5. DATES & STATUT JURIDIQUE */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Date d'Adoption</label>
                    <input
                      type="date"
                      value={adoptDate}
                      onChange={(e) => setAdoptDate(e.target.value)}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Date d'effet / Promulgation</label>
                    <input
                      type="date"
                      value={effectiveDate}
                      onChange={(e) => setEffectiveDate(e.target.value)}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Statut Juridique *</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as any)}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold bg-white cursor-pointer"
                    >
                      {primaryLegalStatuses.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 6. DOMAINE DE DROIT (DOUBLE FONCTION) & ORIGINE */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Domaine de Droit</label>
                      <span className="text-[9px] text-zinc-400 font-medium">Sélection ou nouveau domaine</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        list="editDocAreaList"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="Sélectionner ou saisir un domaine..."
                        className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold bg-white"
                      />
                      <datalist id="editDocAreaList">
                        {areaOfLawOptions.map(a => (
                          <option key={a.value} value={a.label || a.value} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Origine Légale</label>
                    <select
                      value={origin}
                      onChange={(e) => setOrigin(e.target.value as any)}
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold bg-white cursor-pointer"
                    >
                      {originsList.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>

                {/* 7. AUTORITÉ ÉMETTRICE */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Autorité Émettrice</label>
                  <input
                    type="text"
                    placeholder="Ex: Présidence de la République, Ministère des Finances..."
                    value={authority}
                    onChange={(e) => setAuthority(e.target.value)}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                  />
                </div>

                {/* 8. MOTS-CLÉS */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Mots-clés (séparés par des virgules)</label>
                  <input
                    type="text"
                    placeholder="Ex: Impôt, Déclaration, TVA, Régulation"
                    value={keywordsText}
                    onChange={(e) => setKeywordsText(e.target.value)}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                  />
                </div>

                {/* 9. TEXTES LIÉS */}
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Textes Liés / Références Complémentaires</label>
                  <input
                    type="text"
                    placeholder="Ex: Modifie la Loi N°96/06..."
                    value={relatedTexts}
                    onChange={(e) => setRelatedTexts(e.target.value)}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 text-xs font-semibold"
                  />
                </div>
              </form>
            </div>
            
            {/* FOOTER AVEC BOUTON GRISÉ + SPINNER ET PROTECTION SI FICHIER MANQUANT */}
            <div className="sticky bottom-0 bg-zinc-50 border-t border-zinc-200 p-4 flex justify-between items-center gap-3 z-10">
              <div className="text-xs text-zinc-500">
                {editDocWithFile && !pdfFile && (
                  <span className="text-amber-600 font-semibold flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Veuillez joindre un document PDF
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button 
                  type="button" 
                  onClick={closeEditModal} 
                  disabled={savingDoc} 
                  className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs uppercase tracking-wider hover:bg-zinc-100 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  form="editDocForm" 
                  disabled={savingDoc || (editDocWithFile && !pdfFile)}
                  className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-600/30 transition-all cursor-pointer"
                >
                  {savingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingDoc ? "Enregistrement en cours..." : "Enregistrer les modifications"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 7. CREATE USER MODAL */}
      {showCreateUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateUserModal(false)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl animate-fade-in">
            <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-2xl flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-violet-700" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">Créer un utilisateur</h3>
                  <p className="text-xs text-zinc-500">Le compte sera immédiatement actif après création.</p>
                </div>
              </div>
              <button onClick={() => setShowCreateUserModal(false)} className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Nom Complet *</label>
                  <input required type="text" placeholder="Ex: Cédric NGUEMO" value={newUserForm.fullName} onChange={e => setNewUserForm(p => ({...p, fullName: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 text-sm font-semibold" />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Adresse E-mail *</label>
                  <input required type="email" placeholder="email@exemple.com" value={newUserForm.email} onChange={e => setNewUserForm(p => ({...p, email: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 text-sm font-semibold" />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Téléphone</label>
                  <input type="tel" placeholder="(XXX) XXX XXX" value={newUserForm.phone} onChange={e => setNewUserForm(p => ({...p, phone: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Adresse Postale</label>
                  <input type="text" placeholder="Ville, Pays" value={newUserForm.address} onChange={e => setNewUserForm(p => ({...p, address: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Cabinet / Structure</label>
                  <input type="text" placeholder="Cabinet Dupont &amp; Associés" value={newUserForm.cabinetName} onChange={e => setNewUserForm(p => ({...p, cabinetName: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Groupe (Rôle) *</label>
                  <select required value={newUserForm.group} onChange={e => setNewUserForm(p => ({...p, group: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold bg-white">
                    <option value="" disabled>Sélectionner d'abord un profil...</option>
                    <option value="ADMINISTRATEUR">Administrateur</option>
                    <option value="MANAGER">Manager</option>
                    <option value="PARTICULIER">Particulier</option>
                    <option value="ENTREPRISE_B">Entreprise Catégorie B</option>
                    <option value="ENTREPRISE_C">Entreprise Catégorie C</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Date d'Expiration du Compte *</label>
                  <input required type="date" value={newUserForm.expiryDate} onChange={e => setNewUserForm(p => ({...p, expiryDate: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
                  <p className="text-[10px] text-zinc-400">Le compte sera désactivé automatiquement après cette date.</p>
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Mot de Passe par Défaut *</label>
                  <input required type="text" placeholder="Saisir le mot de passe temporaire" value={newUserForm.password} onChange={e => setNewUserForm(p => ({...p, password: e.target.value}))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
                  <p className="text-[10px] text-zinc-400">L'utilisateur pourra modifier après sa connexion.</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
                <button type="button" onClick={() => setShowCreateUserModal(false)} className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs hover:bg-zinc-50 transition-colors">
                  Annuler
                </button>
                <button type="submit" className="px-6 py-2.5 rounded-xl bg-violet-700 hover:bg-violet-800 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-violet-700/20 transition-all hover:-translate-y-0.5">
                  <UserPlus className="w-4 h-4" /> Créer le compte
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. RESET PASSWORD CONFIRMATION MODAL */}
      {resetPasswordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !resetPasswordLoading && setResetPasswordUser(null)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in overflow-hidden">
            <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">Réinitialiser le mot de passe</h3>
                  <p className="text-xs text-zinc-500">Envoi immédiat des identifiants par e-mail</p>
                </div>
              </div>
              <button 
                disabled={resetPasswordLoading}
                onClick={() => setResetPasswordUser(null)} 
                className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-full transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* User details card */}
              <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
                <div className="text-xs font-bold text-zinc-900 flex items-center gap-1.5 flex-wrap">
                  <span>{resetPasswordUser.fullName}</span>
                  {resetPasswordUser.userName && (
                    <span className="inline-flex items-center gap-0.5 font-mono text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-md">
                      <AtSign className="w-2.5 h-2.5 text-violet-500" />
                      {resetPasswordUser.userName}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-600 font-mono flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-zinc-400" />
                  <span>{resetPasswordUser.email}</span>
                </div>
              </div>

              {/* Information alert */}
              <div className="p-3.5 bg-amber-50 border border-amber-200/80 rounded-2xl text-xs text-amber-900 leading-relaxed space-y-1.5">
                <div className="font-bold flex items-center gap-1.5 text-amber-800">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  Confirmation de réinitialisation
                </div>
                <p>
                  Un nouveau mot de passe temporaire et sécurisé sera automatiquement généré par le système et envoyé immédiatement par e-mail à l'adresse <strong>{resetPasswordUser.email}</strong>.
                </p>
                <p className="text-[11px] text-amber-700 font-medium">
                  L'ancien mot de passe de l'utilisateur sera instantanément invalidé.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button"
                  disabled={resetPasswordLoading}
                  onClick={() => setResetPasswordUser(null)} 
                  className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs hover:bg-zinc-50 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  Annuler
                </button>
                <button 
                  type="button"
                  disabled={resetPasswordLoading}
                  onClick={handleConfirmResetPassword} 
                  className="px-6 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-amber-600/20 transition-all hover:-translate-y-0.5 cursor-pointer"
                >
                  {resetPasswordLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <KeyRound className="w-4 h-4" />
                  )}
                  {resetPasswordLoading ? 'Envoi en cours...' : 'Confirmer et envoyer le mot de passe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 9. CREATE GROUP MODAL */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateGroupModal(false)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-2xl flex items-center justify-center">
                  <Plus className="w-5 h-5 text-violet-700" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">Nouveau Profil (Groupe)</h3>
                  <p className="text-xs text-zinc-500">Ajoutez un nouveau rôle utilisateur</p>
                </div>
              </div>
              <button onClick={() => setShowCreateGroupModal(false)} className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>
            <form onSubmit={handleCreateGroup} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Nom du profil *</label>
                <input required type="text" placeholder="Ex: Abonné Premium" value={newGroupForm.label} onChange={e => setNewGroupForm(p => ({...p, label: e.target.value}))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 text-sm font-semibold" />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Description</label>
                <input type="text" placeholder="Ex: Accès complet aux documents" value={newGroupForm.description} onChange={e => setNewGroupForm(p => ({...p, description: e.target.value}))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold" />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Catégorie *</label>
                <select required value={newGroupForm.category} onChange={e => setNewGroupForm(p => ({...p, category: e.target.value}))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 text-sm font-semibold bg-white">
                  <option value="CUSTOMER">Client (Customer)</option>
                  <option value="STAFF">Personnel (Staff)</option>
                </select>
                <p className="text-[10px] text-zinc-400">Le groupe "Administrateur" est réservé au système.</p>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
                <button type="button" onClick={() => setShowCreateGroupModal(false)} className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs hover:bg-zinc-50 transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={isCreatingGroup} className="px-6 py-2.5 rounded-xl bg-violet-700 hover:bg-violet-800 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-violet-700/20 transition-all hover:-translate-y-0.5">
                  <Check className="w-4 h-4" /> {isCreatingGroup ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 10. UNIFIED DOCUMENT & DRAFT PDF PREVIEW MODAL (PAGE-BY-PAGE STREAMING) */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="absolute inset-0" onClick={closeDocumentPreview}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 z-10">
            {/* Modal Header */}
            <div className={`p-4 sm:p-5 text-white flex items-center justify-between gap-4 ${
              previewDoc.isDraft 
                ? 'bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900' 
                : 'bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-900'
            }`}>
              <div className="space-y-1 flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-md uppercase ${
                    previewDoc.isDraft ? 'bg-indigo-500/30 text-indigo-200' : 'bg-emerald-500/30 text-emerald-200'
                  }`}>
                    {previewDoc.categoryName || previewDoc.nature || (previewDoc.isDraft ? 'Brouillon' : 'Acte Officiel')}
                  </span>
                  {previewDoc.referenceNumber && (
                    <span className="text-xs font-mono font-bold text-white/90">
                      {previewDoc.referenceNumber}
                    </span>
                  )}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                    previewDoc.isDraft 
                      ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-400/30' 
                      : 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                  }`}>
                    {previewDoc.isDraft ? 'Brouillon' : 'Catalogue Officiel'}
                  </span>
                  {previewDoc.isDeployedToMinio !== undefined && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                      previewDoc.isDeployedToMinio 
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30' 
                        : 'bg-amber-500/20 text-amber-300 border border-amber-400/30'
                    }`}>
                      {previewDoc.isDeployedToMinio ? <Check className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
                      {previewDoc.isDeployedToMinio ? 'Document disponible' : 'Fichier manquant'}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-sm sm:text-base font-bold text-white line-clamp-1">
                  {previewDoc.title}
                </h3>
              </div>

              {/* Controls Toolbar in Header */}
              <div className="flex items-center gap-2 shrink-0">
                {/* Page Navigation Controls */}
                <div className="flex items-center gap-1 bg-black/40 backdrop-blur-md rounded-xl p-1 border border-white/10">
                  <button
                    type="button"
                    onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                    disabled={previewPage <= 1 || previewPdfLoading}
                    className="p-1 rounded-lg hover:bg-white/20 transition-colors text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    title="Page précédente"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1 px-1">
                    <input
                      type="number"
                      min={1}
                      max={previewTotalPages || 1}
                      value={previewPageInput}
                      onChange={(e) => setPreviewPageInput(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handlePageInputSubmit();
                      }}
                      onBlur={handlePageInputSubmit}
                      className="w-11 text-center bg-white/10 text-white text-xs font-mono font-bold rounded py-0.5 border border-white/20 focus:border-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none transition-colors"
                      title="Entrez un numéro de page puis appuyez sur Entrée"
                    />
                    <span className="text-[11px] font-mono text-white/70 select-none">
                      / {previewTotalPages || '...'}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => setPreviewPage(p => previewTotalPages ? Math.min(previewTotalPages, p + 1) : p + 1)}
                    disabled={(previewTotalPages ? previewPage >= previewTotalPages : false) || previewPdfLoading}
                    className="p-1 rounded-lg hover:bg-white/20 transition-colors text-white disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    title="Page suivante"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={closeDocumentPreview}
                  className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors cursor-pointer border border-white/10"
                  title="Fermer la prévisualisation"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body: Single Page Viewer */}
            <div className="flex-1 bg-zinc-900 p-2 min-h-[65vh] flex flex-col items-center justify-center relative">
              {previewPdfLoading ? (
                <div className="flex flex-col items-center justify-center p-12 text-center space-y-3">
                  <Loader2 className={`w-10 h-10 animate-spin ${previewDoc.isDraft ? 'text-indigo-400' : 'text-emerald-400'}`} />
                  <p className="text-sm font-bold text-white">Chargement ultra-rapide de la page {previewPage}...</p>
                  <p className="text-xs text-zinc-400">Diffusion progressive sans latence</p>
                </div>
              ) : previewPdfError ? (
                <div className="max-w-md bg-white rounded-2xl p-6 text-center space-y-3 shadow-xl border border-red-100">
                  <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-zinc-900 text-sm">Page introuvable ou indisponible</h4>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    {previewPdfError}
                  </p>
                  <div className="text-[11px] bg-zinc-50 p-2.5 rounded-xl text-zinc-600 font-mono text-left break-all">
                    Référence : {previewDoc.referenceNumber || 'Non spécifiée'} (Page {previewPage})
                  </div>
                </div>
              ) : previewPdfData ? (
                <div 
                  className="w-full h-full min-h-[65vh] flex justify-center items-center overflow-auto p-4 select-none"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <Document
                    file={{ data: previewPdfData }}
                    loading={
                      <div className="flex flex-col items-center justify-center p-8 text-zinc-400 select-none">
                        <Loader2 className="w-8 h-8 animate-spin mb-2 text-primary-400" />
                        <span className="text-xs">Rendu de la page...</span>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={1}
                      scale={1.2}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="shadow-2xl select-none pointer-events-none rounded-sm"
                    />
                  </Document>
                </div>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-zinc-400" />
                <span>Visualisation progressive sécurisée : seule la page {previewPage} est chargée.</span>
              </div>
              <div className="flex items-center gap-2.5">
                {previewDoc.isDraft ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const d = previewDoc.draftObj || drafts.find(x => x.id === previewDoc.id);
                        closeDocumentPreview();
                        if (d) openEditDraftModal(d);
                      }}
                      className="px-4 py-2 rounded-xl border border-zinc-200 text-zinc-700 font-bold text-xs hover:bg-white transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Modifier les métadonnées
                    </button>
                    <button
                      type="button"
                      disabled={draftActionLoading[previewDoc.id]}
                      onClick={() => handlePublishDraft(previewDoc.id)}
                      className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
                    >
                      {draftActionLoading[previewDoc.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      <span>Valider &amp; Publier l'acte</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const doc = previewDoc.docObj || documents.find(x => x.id === previewDoc.id);
                        closeDocumentPreview();
                        if (doc) openEditModal(doc);
                      }}
                      className="px-4 py-2 rounded-xl border border-zinc-200 text-zinc-700 font-bold text-xs hover:bg-white transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
                    >
                      <Edit3 className="w-3.5 h-3.5" /> Modifier cet acte
                    </button>
                    <button
                      type="button"
                      onClick={closeDocumentPreview}
                      className="px-5 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs transition-all cursor-pointer"
                    >
                      Fermer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 11. DRAFT EDIT METADATA & DOCUMENT MODAL */}
      {editingDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !savingDraft && setEditingDraft(null)}></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in z-10">
            {/* Header */}
            <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-2xl flex items-center justify-center">
                  <Edit3 className="w-5 h-5 text-indigo-700" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-zinc-900">Modifier le brouillon</h3>
                  <p className="text-xs text-zinc-500">Mettez à jour les informations juridiques ou rattachez un document PDF</p>
                </div>
              </div>
              <button
                disabled={savingDraft}
                onClick={() => {
                  setEditingDraft(null);
                  setDraftFile(null);
                }}
                className="p-2 bg-zinc-100 hover:bg-zinc-200 rounded-full transition-colors disabled:opacity-50 cursor-pointer"
              >
                <X className="w-5 h-5 text-zinc-500" />
              </button>
            </div>

            {/* Form Body */}
            <form id="editDraftForm" onSubmit={(e) => handleSaveEditDraft(e)} className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Type de modification */}
              <div className="p-4 bg-zinc-50 border border-zinc-200/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                <div>
                  <span className="text-xs font-bold text-zinc-900">Type de modification</span>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Indiquez si vous modifiez uniquement les références ou également le fichier PDF.</p>
                </div>
                <div className="flex items-center gap-4 bg-white p-1.5 px-3 rounded-xl border border-zinc-200 shrink-0">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-700 hover:text-zinc-900">
                    <input
                      type="radio"
                      name="draftEditScope"
                      checked={!editDraftWithFile}
                      onChange={() => { setEditDraftWithFile(false); setDraftFile(null); }}
                      className="text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span>Références seulement</span>
                  </label>
                  <div className="w-px h-4 bg-zinc-200"></div>
                  <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-700 hover:text-zinc-900">
                    <input
                      type="radio"
                      name="draftEditScope"
                      checked={editDraftWithFile}
                      onChange={() => setEditDraftWithFile(true)}
                      className="text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span>Avec le document (PDF)</span>
                  </label>
                </div>
              </div>

              {/* Document PDF Attachment / Replacement Section - visible uniquement si editDraftWithFile est activé */}
              {editDraftWithFile && (
                <div className={`p-4 rounded-2xl border transition-all animate-fade-in ${
                  editingDraft.isDeployedToMinio 
                    ? 'bg-emerald-50/60 border-emerald-200' 
                    : 'bg-amber-50/70 border-amber-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                      editingDraft.isDeployedToMinio ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {editingDraft.isDeployedToMinio ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold text-zinc-900">
                          {editingDraft.isDeployedToMinio ? 'Document PDF actuellement rattaché' : 'Fichier PDF manquant (Document introuvable)'}
                        </h4>
                        <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${
                          editingDraft.isDeployedToMinio ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {editingDraft.isDeployedToMinio ? 'Document disponible' : 'Fichier manquant'}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        {editingDraft.isDeployedToMinio 
                          ? 'Un fichier PDF est actuellement associé à ce brouillon. Si vous sélectionnez un nouveau PDF, l\'ancien fichier sera automatiquement remplacé.'
                          : 'Ce brouillon n\'a aucun fichier PDF associé. Vous pouvez joindre le document PDF officiel ci-dessous.'}
                      </p>

                      <div className="mt-3 pt-3 border-t border-zinc-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-zinc-200 hover:border-indigo-500 rounded-xl text-xs font-bold text-zinc-700 hover:text-indigo-700 shadow-sm cursor-pointer transition-colors">
                          <Upload className="w-3.5 h-3.5" />
                          <span>{draftFile ? 'Changer de fichier...' : (editingDraft.isDeployedToMinio ? 'Remplacer le document PDF' : 'Sélectionner le document PDF')}</span>
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) => setDraftFile(e.target.files?.[0] || null)}
                            className="hidden"
                          />
                        </label>

                        {draftFile && (
                          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-indigo-200 text-xs font-semibold text-indigo-900 shadow-sm">
                            <FileText className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                            <span className="truncate max-w-[200px]">{draftFile.name}</span>
                            <span className="text-[10px] text-zinc-400">({(draftFile.size / 1024).toFixed(0)} Ko)</span>
                            <button
                              type="button"
                              onClick={() => setDraftFile(null)}
                              className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-red-500 transition-colors ml-1 cursor-pointer"
                              title="Retirer ce fichier"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Intitulé Officiel (Titre) *</label>
                <textarea
                  required
                  rows={3}
                  value={editDraftForm.officialTitle || ''}
                  onChange={e => setEditDraftForm((p: any) => ({ ...p, officialTitle: e.target.value }))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold leading-relaxed"
                  placeholder="Intitulé officiel complet du texte juridique..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Référence Légale *</label>
                  <input
                    required
                    type="text"
                    value={editDraftForm.referenceNumber || ''}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, referenceNumber: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Catégorie / Nature <span className="text-red-500">*</span></label>
                    <span className="text-[9px] text-zinc-400 font-medium">Sélection ou nouvelle nature</span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      list="editDraftCategoryList"
                      value={editDraftForm.categoryName || ''}
                      onChange={e => setEditDraftForm((p: any) => ({ ...p, categoryName: e.target.value }))}
                      placeholder="Sélectionner ou saisir une nature..."
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold bg-white"
                    />
                    <datalist id="editDraftCategoryList">
                      {categoryOptions.map(c => (
                        <option key={c.value} value={c.label || c.value} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Année</label>
                  <input
                    type="number"
                    value={editDraftForm.effectiveYear || ''}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, effectiveYear: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Date d'Adoption</label>
                  <input
                    type="date"
                    value={editDraftForm.dateOfAdoption || ''}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, dateOfAdoption: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Date d'Effet</label>
                  <input
                    type="date"
                    value={editDraftForm.effectiveDate || ''}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, effectiveDate: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Statut Juridique *</label>
                  <select
                    value={editDraftForm.legalStatus || 'EnVigeur'}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, legalStatus: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold bg-white cursor-pointer"
                  >
                    {primaryLegalStatuses.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Domaine de Droit</label>
                    <span className="text-[9px] text-zinc-400 font-medium">Sélection ou nouveau domaine</span>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      list="editDraftAreaList"
                      value={editDraftForm.areaOfLaw || ''}
                      onChange={e => setEditDraftForm((p: any) => ({ ...p, areaOfLaw: e.target.value }))}
                      placeholder="Sélectionner ou saisir un domaine..."
                      className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold bg-white"
                    />
                    <datalist id="editDraftAreaList">
                      {areaOfLawOptions.map(a => (
                        <option key={a.value} value={a.label || a.value} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Origine Légale</label>
                  <select
                    value={editDraftForm.legalOrigin || 'NATIONAL'}
                    onChange={e => setEditDraftForm((p: any) => ({ ...p, legalOrigin: e.target.value }))}
                    className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold bg-white"
                  >
                    {originsList.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Autorité Émettrice</label>
                <input
                  type="text"
                  placeholder="Ex: Présidence de la République, Ministère des Finances..."
                  value={editDraftForm.issuingAuthority || ''}
                  onChange={e => setEditDraftForm((p: any) => ({ ...p, issuingAuthority: e.target.value }))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Mots-clés (séparés par des virgules)</label>
                <input
                  type="text"
                  placeholder="Ex: Impôt, Déclaration, TVA, Régulation"
                  value={editDraftForm.legalKeywords || ''}
                  onChange={e => setEditDraftForm((p: any) => ({ ...p, legalKeywords: e.target.value }))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500">Textes Liés / Références Complémentaires</label>
                <input
                  type="text"
                  placeholder="Ex: Modifie la Loi N°96/06..."
                  value={editDraftForm.relatedTexts || ''}
                  onChange={e => setEditDraftForm((p: any) => ({ ...p, relatedTexts: e.target.value }))}
                  className="w-full p-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-xs font-semibold"
                />
              </div>
            </form>

            {/* Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between gap-3">
              <div className="text-xs text-zinc-500">
                {editDraftWithFile && !draftFile && (
                  <span className="text-amber-600 font-semibold flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Veuillez joindre un document PDF
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={savingDraft}
                  onClick={() => {
                    setEditingDraft(null);
                    setDraftFile(null);
                  }}
                  className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs hover:bg-zinc-100 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  form="editDraftForm"
                  disabled={savingDraft || (editDraftWithFile && !draftFile)}
                  className="px-6 py-2.5 rounded-xl bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-indigo-700/20 transition-all cursor-pointer"
                >
                  {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingDraft ? "Sauvegarde en cours..." : "Sauvegarder les modifications"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EXCEL IMPORT & SYNCHRONIZATION MODAL ── */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] shadow-2xl flex flex-col overflow-hidden border border-zinc-100 animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[10px] font-bold uppercase tracking-wider mb-2">
                  <FileSpreadsheet className="w-3.5 h-3.5 text-cyan-400" /> Ingestion &amp; Upsert Excel
                </div>
                <h3 className="font-display text-lg font-bold">Import &amp; Synchronisation Continue</h3>
                <p className="text-xs text-white/70 mt-0.5">Mise à jour sans perte des documents et archivage sécurisé des brouillons.</p>
              </div>
              <button
                disabled={isImporting}
                onClick={() => setShowImportModal(false)}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors disabled:opacity-50 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
              {/* Informative Note */}
              <div className="bg-indigo-50/70 border border-indigo-100 rounded-2xl p-4 space-y-2">
                <div className="font-bold text-indigo-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600" />
                  Règle de synchronisation continue (OldPath) :
                </div>
                <ul className="text-indigo-800 space-y-1 list-disc list-inside leading-relaxed text-[11px]">
                  <li><strong className="font-bold">Actes existants :</strong> Si le document existe déjà en brouillon (identifié via son ancien chemin <span className="font-mono bg-indigo-100 px-1 py-0.5 rounded text-indigo-950">OldPath</span>), ses métadonnées corrigées sont mises à jour <span className="underline decoration-indigo-400 font-bold">sans altérer le document déjà archivé</span>.</li>
                  <li><strong className="font-bold">Nouveaux actes :</strong> Si le texte est nouveau, un nouveau brouillon est créé et son document PDF est conservé de manière sécurisée.</li>
                </ul>
              </div>

              {/* File input area */}
              {!importJobStatus && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-indigo-200 hover:border-indigo-400 bg-indigo-50/20 rounded-2xl p-6 text-center transition-colors">
                    <input
                      type="file"
                      id="excel-file-upload"
                      accept=".xlsx"
                      disabled={isImporting}
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setImportFile(file);
                        setImportError(null);
                      }}
                      className="hidden"
                    />
                    <label htmlFor="excel-file-upload" className="cursor-pointer flex flex-col items-center gap-3">
                      <div className="w-14 h-14 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center shadow-sm">
                        <FileSpreadsheet className="w-7 h-7" />
                      </div>
                      <div>
                        <div className="font-bold text-sm text-zinc-900">
                          {importFile ? importFile.name : "Sélectionner un fichier Excel (.xlsx)"}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1">
                          {importFile ? `${(importFile.size / 1024).toFixed(1)} KB` : "Cliquez ou glissez-déposez le fichier Excel corrigé"}
                        </p>
                      </div>
                    </label>
                  </div>

                  {importError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2 font-medium">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{importError}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Progress & Results */}
              {importJobStatus && (
                <div className="space-y-4">
                  {/* Status Banner */}
                  <div className={`p-4 rounded-2xl border flex items-center gap-3 ${
                    importJobStatus.status === 'Completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                    importJobStatus.status === 'Failed' ? 'bg-red-50 border-red-200 text-red-900' :
                    'bg-indigo-50 border-indigo-200 text-indigo-900'
                  }`}>
                    {isImporting ? (
                      <Loader2 className="w-5 h-5 text-indigo-600 animate-spin shrink-0" />
                    ) : importJobStatus.status === 'Completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                    )}
                    <div>
                      <div className="font-extrabold text-xs uppercase tracking-wider">
                        {importJobStatus.status === 'Completed' ? "Importation & Synchronisation Terminées" :
                         importJobStatus.status === 'Failed' ? "Échec du Traitement" :
                         "Traitement du Fichier en cours..."}
                      </div>
                      <p className="text-[11px] opacity-80 mt-0.5">{importJobStatus.message || "Analyse des lignes et synchronisation en cours..."}</p>
                    </div>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="p-3.5 bg-emerald-50/60 border border-emerald-100 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Nouveaux Créés</div>
                      <div className="text-xl font-extrabold text-emerald-900 mt-1">{importJobStatus.successCount || 0}</div>
                      <div className="text-[9px] text-emerald-600 mt-0.5">Document rattaché &amp; Brouillon</div>
                    </div>

                    <div className="p-3.5 bg-sky-50/60 border border-sky-100 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Synchronisés</div>
                      <div className="text-xl font-extrabold text-sky-900 mt-1">{importJobStatus.updatedCount || 0}</div>
                      <div className="text-[9px] text-sky-600 mt-0.5">Métadonnées à jour</div>
                    </div>

                    <div className="p-3.5 bg-amber-50/60 border border-amber-100 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Fichiers Introuvables</div>
                      <div className="text-xl font-extrabold text-amber-900 mt-1">{importJobStatus.fileNotFoundCount || 0}</div>
                      <div className="text-[9px] text-amber-600 mt-0.5">Hors répertoire documents</div>
                    </div>

                    <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Ignorés / Doublons</div>
                      <div className="text-xl font-extrabold text-zinc-800 mt-1">{importJobStatus.skippedCount || 0}</div>
                      <div className="text-[9px] text-zinc-400 mt-0.5">Champs requis manquants</div>
                    </div>

                    <div className="p-3.5 bg-rose-50/60 border border-rose-100 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Erreurs Techniques</div>
                      <div className="text-xl font-extrabold text-rose-900 mt-1">{importJobStatus.errorCount || 0}</div>
                      <div className="text-[9px] text-rose-600 mt-0.5">Format ou exception</div>
                    </div>

                    <div className="p-3.5 bg-indigo-50/60 border border-indigo-100 rounded-2xl text-center">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Total Traité</div>
                      <div className="text-xl font-extrabold text-indigo-900 mt-1">
                        {(importJobStatus.successCount || 0) + (importJobStatus.updatedCount || 0) + (importJobStatus.fileNotFoundCount || 0) + (importJobStatus.skippedCount || 0) + (importJobStatus.errorCount || 0)}
                      </div>
                      <div className="text-[9px] text-indigo-600 mt-0.5">Lignes du fichier</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={isImporting}
                onClick={() => {
                  setShowImportModal(false);
                  if (importPollingRef.current) clearInterval(importPollingRef.current);
                }}
                className="px-5 py-2.5 rounded-xl border border-zinc-200 text-zinc-600 font-bold text-xs hover:bg-zinc-100 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {importJobStatus?.status === 'Completed' ? 'Fermer' : 'Annuler'}
              </button>

              {!importJobStatus ? (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={isImporting || !importFile}
                      onClick={startImportExcel}
                      className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-indigo-600/20 transition-all hover:-translate-y-0.5 cursor-pointer"
                    >
                      {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Lancer (Normal)
                    </button>
                    <button
                      type="button"
                      disabled={isImporting || !importFile}
                      onClick={startUpdateExcel}
                      className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold text-xs flex items-center gap-2 shadow-md shadow-orange-500/20 transition-all hover:-translate-y-0.5 cursor-pointer"
                      title="Utiliser ceci si vous souhaitez actualiser et écraser les fichiers existants."
                    >
                      {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Mise à jour Fichiers
                    </button>
                  </div>
                ) : (
                <button
                  type="button"
                  onClick={() => {
                    setImportFile(null);
                    setImportJobStatus(null);
                    setImportError(null);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-900 text-white font-bold text-xs flex items-center gap-2 shadow-sm transition-all cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4" />
                  Importer un autre fichier
                </button>
              )}
            </div>
          </div>
        </div>
      )}

          <CreateStaffModal isOpen={isCreateStaffModalOpen} onClose={() => setIsCreateStaffModalOpen(false)} onSuccess={() => fetchUsers()} />
      <CreateUserModal isOpen={isCreateUserModalOpen} onClose={() => setIsCreateUserModalOpen(false)} onSuccess={() => fetchUsers()} availableGroups={availableGroups} />
    </div>
  );
};
